import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedClient } from '../../store/slices/clientsSlice'
import { fetchInboxHealth, clearInboxHealth } from '../../store/slices/inboxHealthSlice'
import { clearDailyPerformance } from '../../store/slices/dailyPerformanceSlice'
import { fetchDailyPostStats, clearDailyPostStats } from '../../store/slices/dailyPostStatsSlice'
import {
  fetchClientMessagesReport,
  clearClientMessagesReport,
} from '../../store/slices/clientMessagesReportSlice'
import {
  fetchClientSentimentsReport,
  clearClientSentimentsReport,
} from '../../store/slices/clientSentimentsReportSlice'
import {
  fetchClientAlertsReport,
  clearClientAlertsReport,
} from '../../store/slices/clientAlertsReportSlice'
import {
  fetchClientMessagesTimeOfDay,
  clearClientMessagesTimeOfDay,
} from '../../store/slices/clientMessagesTimeOfDaySlice'
import {
  fetchClientAlertsTimeOfDay,
  clearClientAlertsTimeOfDay,
} from '../../store/slices/clientAlertsTimeOfDaySlice'
import {
  fetchClientMessagesHeatmap,
  clearClientMessagesHeatmap,
} from '../../store/slices/clientMessagesHeatmapSlice'
import {
  fetchClientDemographicsFollowers,
  clearClientDemographicsFollowers,
} from '../../store/slices/clientDemographicsFollowersSlice'
import {
  fetchClientPostHeatmap,
  clearClientPostHeatmap,
} from '../../store/slices/clientPostHeatmapSlice'
import {
  fetchClientDetailsByClientId,
  clearClientDetails,
} from '../../store/slices/clientDetailsSlice'
import {
  fetchClientTopPosts,
  clearClientTopPosts,
} from '../../store/slices/clientTopPostsSlice'
import {
  fetchClientSocialPerformance,
  fetchClientPulseSocialPerformance,
  clearClientSocialPerformance,
} from '../../store/slices/clientSocialPerformanceSlice'
import {
  fetchClientCompetitivePerformance,
  clearClientCompetitivePerformance,
} from '../../store/slices/clientCompetitivePerformanceSlice'
import {
  fetchClientCampaignPerformance,
  clearClientCampaignPerformance,
} from '../../store/slices/clientCampaignPerformanceSlice'
import {
  fetchClientStoryPerformance,
  clearClientStoryPerformance,
} from '../../store/slices/clientStoryPerformanceSlice'
import {
  fetchClientPaidPerformance,
  clearClientPaidPerformance,
} from '../../store/slices/clientPaidPerformanceSlice'
import {
  fetchClientPaidMediaPerformance,
  clearClientPaidMediaPerformance,
} from '../../store/slices/clientPaidMediaPerformanceSlice'
import {
  buildTopPostsFetchKey,
  filterTopPostsByPropertyDateRange,
  formatTopPostDisplayDate,
  formatTopPostEngagementRate,
  formatTopPostsReportDate,
  filterTopPostsPerformanceRows,
  getTopPostSortValue,
  resolveTopPosts,
  splitPostOverlayText,
  unwrapTopPostsPayload,
  topPostDashboardPlatformKey,
  topPostMatchesPlatformFilter,
} from '../../lib/utils/topPostsReport'
import {
  buildPlatformPerformanceComparisonData,
  buildSocialPerformanceFetchKey,
  buildPulseSocialPerformanceFetchKey,
  buildEngagementCardFromSocialPerformance,
  buildGrowthCardFromSocialPerformance,
  buildMonthlySocialTable,
  buildMonthlySocialTablesBySource,
  formatSocialPerformanceDate,
  normalizeSocialPerformancePayload,
  resolvePulseSocialPerformancePlatforms,
  resolveSocialPerformancePlatforms,
  tableSourceToAggregationMode,
} from '../../lib/utils/socialPerformanceReport'
import {
  buildCompetitivePerformanceFetchKey,
  buildSocialPerformanceRowsFromCompetitiveReports,
} from '../../lib/utils/competitivePerformanceReport'
import {
  buildCampaignPerformanceChartData,
  buildCampaignPerformanceFetchKey,
  campaignChartRowsHaveMetrics,
  formatCampaignPerformanceDate,
  sumCampaignChartPostCount,
} from '../../lib/utils/campaignPerformanceReport'
import {
  buildStoryPerformanceFetchKey,
  formatStoryPerformanceDate,
  resolveStoryPerformance,
} from '../../lib/utils/storyPerformanceReport'
import {
  buildPaidPerformanceFetchKey,
  formatPaidPerformanceDate,
  mapPaidPerformanceAds,
  mapPaidPerformanceCampaigns,
  PAID_REPORT_ALL_DATA_PAGE_SIZE,
  PAID_REPORT_LEVELS,
  paidUiSortToApiSort,
  paidViewToLevel,
} from '../../lib/utils/paidPerformanceReport'
import {
  buildPaidMediaChartView,
  buildPaidMediaPerformanceFetchKey,
  formatPaidMediaPerformanceDate,
  getPaidMediaSeriesForPlatform,
  getPaidMediaTotalsForPlatform,
} from '../../lib/utils/paidMediaPerformanceReport'
import {
  AUDIENCE_INSIGHTS_INFO_TOOLTIP,
  buildAgeGenderInsight,
  buildAgeGenderSourceLabel,
  buildAudienceInsightsFromDemographicsRows,
  buildDemographicsFollowersFetchKey,
  buildGeographicInsight,
  buildTopCitiesInsight,
  buildTopCountriesInsight,
  filterTopCountriesRowsByPlatformKeys,
  formatDemographicsReportDate,
} from '../../lib/utils/demographicsFollowers'
import { fetchSocialNetworkTypes } from '../../store/slices/socialNetworkTypesSlice'
import { fetchMetricGoalTypes } from '../../store/slices/metricGoalTypesSlice'
import { fetchMetricGoals } from '../../store/slices/metricGoalsSlice'
import {
  fetchClientMetricGoals,
  clearClientMetricGoals,
  saveClientMetricGoals,
} from '../../store/slices/clientMetricGoalsSlice'
import {
  fetchClientYtdPagePerformance,
  clearClientYtdPagePerformance,
  selectPaidOrganicPerformanceIndex,
} from '../../store/slices/clientYtdPagePerformanceSlice'
import {
  periodValueForApi,
  applyClientRowToMetricDraft,
  buildGoalTypeDraftFromClientApi,
  buildGoalsUiStateFromClientRows,
  resolveClientGoalMetricId,
} from '../../lib/utils/clientMetricGoals'
import { resolveGoalPresets } from '../../lib/utils/metricGoalTypesReport'
import { buildGoalMetricGroupsFromApi } from '../../lib/utils/metricGoals'
import {
  goalProgressStatusFromRatio,
  lookupGoalPerformanceFromYtdIndex,
  buildPaidOrganicPerformanceIndex,
  buildYtdPagePerformanceFetchKey,
  formatYtdPerformanceDate,
  parseGoalMetricNumber,
} from '../../lib/utils/ytdPagePerformanceReport'
import SohoLoader from '../common/SohoLoader/SohoLoader'
import LazyMount from '../common/LazyMount'
import { chartHasReportData, buildChartInsightText } from './utils/dashboardExportChartData'
import {
  lockDashboardReportViewport,
  unlockDashboardReportViewport,
} from './utils/dashboardReportScrollLock'
import { chartReportSkipsDomCapture } from './utils/dashboardReportHtml'
import { createDashboardChartDataReadiness } from './utils/dashboardReportApiReadiness'
import { buildInboxHealthCard, getPulseSnapshotDateRange, pulseInboxStatsForClient, socialNetworkTypeIdToLabel } from '../../lib/utils/inboxHealthReport'
import { buildCampaignCard } from '../../lib/utils/dailyPostStatsReport'
import { pulseApiRowsForClient } from '../../lib/utils/pulseApiRows'
import { pulseCardTooltipText } from '../../lib/utils/pulseCardReport'
import {
  ensureConnectedPlatformRows,
} from '../../lib/utils/pulseCardReport'
import {
  buildMessagesReportFetchKey,
  buildMonitoringChartsFromMessagesRows,
  connectedChartKeysFromSocialAccounts,
  formatMessagesReportBoundaryDate,
  formatPostHeatmapReportDate,
  orderedConnectedChartKeys,
} from '../../lib/utils/messagesReport'
import { fetchSocialNetworkAccounts, clearAccounts } from '../../store/slices/socialNetworkAccountsSlice'
import { buildSentimentChartsFromRows } from '../../lib/utils/sentimentsReport'
import { buildAlertsChartsFromRows } from '../../lib/utils/alertsReport'
import {
  buildTimeOfDayFromRows,
  TIME_OF_DAY_CHART_SERIES,
} from '../../lib/utils/timeOfDayReport'
import { buildMessageHeatmapFromRows } from '../../lib/utils/heatmapReport'
import { buildPostHeatmapFromReportRows } from '../../lib/utils/postHeatmapReport'
import { resolvePropertyChartTimezone } from './utils/chartHeaderTimezone'
import { getClientIanaTimeZone, resolveSchedulingClient } from '../../lib/utils/timezone'
import './Dashboard.css'
import './DashboardResponsive.css'
import { EMPTY_DASHBOARD_DATA } from '../../data/dashboardEmptyDefaults'
import {
  buildDailyCalendarHourlyMatrix,
  buildMonthlyCalendarHourlyMatrix,
  getMessagesByCalendarDayFromSeries,
} from '../../lib/utils/dashboardMessageVolumeUtils'
import BaseBarChart from '../charts/BaseBarChart'
import ScrollableStackedBarChart from '../charts/ScrollableStackedBarChart'
import CampaignPerformanceBarChart from '../charts/CampaignPerformanceBarChart'
import BaseLineChart from '../charts/BaseLineChart'
import PaidMediaPerformanceChart from '../charts/PaidMediaPerformanceChart'
import MonthlySocialPerformancePanel, {
  MonthlySocialPerformanceFilterBar,
} from './components/MonthlySocialPerformancePanel'
import HorizontalScrollCards from './components/HorizontalScrollCards'
import TopStories, { TOP_STORY_PLATFORMS } from './components/TopStories'
import PostTopicCard from './components/PostTopicCard'
import TopPostThumbnailMedia from './components/TopPostThumbnailMedia'
import {
  getRankedTopicPerformance,
  POST_TOPIC_PERFORMANCE_GROUPS,
  POST_TOPIC_RANK_METRICS,
} from './components/postPerformanceUtils'
import BasePieChart from '../charts/BasePieChart'
import SegmentedRingDonut from '../charts/SegmentedRingDonut'
import { formatCompactNumber, formatNumber, formatPercent } from '../charts/formatters'
import ChartPanel from '../charts/ChartPanel'
import TopPerformingAdsWidget from './TopPerformingAdsWidget'
import { PAID_MEDIA_AD_PLATFORM_OPTIONS } from './topPerformingAds/topPerformingAdsData'
import KpiHoverLayer from './components/KpiHoverLayer'
import KpiInfoTooltip from './components/KpiInfoTooltip'
import AudienceGeographicDistribution from './components/AudienceGeographicDistribution'
import PlatformPerformanceComparison from './PlatformPerformanceComparison'
import SocialPerformanceBenchmark from './SocialPerformanceBenchmark'
import SentimentThemeDrivers from './components/SentimentThemeDrivers'
import SentimentThemeDrawer from './components/SentimentThemeDrawer'
import OptimalPostingTimesUnifiedWidget from './components/OptimalPostingTimesUnifiedWidget'
import { adaptPlatformSeriesToOptimalPostingCalendarHourly } from './utils/optimalPostingTimesAdapter'
import MessageHeatmapUnifiedWidget from './components/MessageHeatmapUnifiedWidget'
import ExportHoverTooltip from './export/ExportHoverTooltip'
import DashboardExportProgressBanner from './export/DashboardExportProgressBanner'
import ExportReportSettingsForm from './export/ExportReportSettingsForm'
import ExportWidgetDataScopeSwitch from './export/ExportWidgetDataScopeSwitch'
import { EXPORT_REPORT_SETTINGS_TOOLTIPS } from './export/exportReportSettingsTooltips'
import {
  REPORT_DATA_SCOPE,
  createDefaultWidgetDataScopes,
  formatDataScopeLabel,
  expandPaidMediaChartIdsForAllData,
  PAID_MEDIA_EXPORT_CHART_IDS,
  PAID_MEDIA_WIDGET_VIEW_BY_CHART_ID,
  chartExportUsesAllData,
  shouldUseAllDashboardFiltersForExport,
  dedupeExportChartIds,
} from './export/exportDataScope'
import './export/ExportDataScope.css'
import ComingSoonPill from '../common/coming-soon/ComingSoonPill'
import HoverTooltip from '../common/tooltip/HoverTooltip'
import GoalCard from './components/GoalCard'
import GoalPerformanceTable from './components/GoalPerformanceTable'
import GoalsZeroState from './components/GoalsZeroState'
import GoalsActiveState from './components/GoalsActiveState'
import GoalsSummaryHeader from './components/GoalsSummaryHeader'
import GoalsConfigPanel from './components/GoalsConfigPanel'
import GoalsPreActivationDrawer from './components/GoalsPreActivationDrawer'
import GoalsMetricsConfigurator from './components/GoalsMetricsConfigurator'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import { GOALS_UI_COMPACT } from '../../config/featureFlags'
import {
  getPreviousPeriod,
  formatPreviousPeriodTooltip,
  formatLocalDateKey,
  parseLocalDateInput,
  endOfLocalDay,
  localDateTodayKey,
} from '../../utils/dateRangeUtils'
import {
  getGoalMetricPeriodEndDate,
  getGoalMetricPeriodExtendLabel,
  isGoalMetricPeriodExpired,
  formatGoalPeriodAnchorLabel,
} from '../../utils/goalMetricPeriodUtils'
import { reportTemplates } from '../../config/reportTemplates'
import { resolvePublicAssetUrl, socialIcon } from '../../lib/assets'

function toGoalPeriodStartKey(date = new Date()) {
  return date.toISOString().slice(0, 10)
}

function isPulseClientSlicePending(slice, clientId) {
  if (clientId == null || clientId === '') return false
  if (slice?.status === 'failed') return false
  if (slice?.status === 'succeeded' && String(slice.fetchedClientId ?? '') === String(clientId)) {
    return false
  }
  return true
}

const POST_PERFORMANCE_RANK_COLUMNS = [
  'engagement',
  'likes',
  'comments',
  'shares',
  'saves',
  'impressions',
  'postClicks',
]

const POST_PERFORMANCE_GROUPS = [
  {
    value: 'top',
    label: 'Top 15',
    description: 'Highest-performing posts',
  },
  {
    value: 'bottom',
    label: 'Bottom 15',
    description: 'Lowest-performing posts',
  },
]

const postPerformanceRankColumnLabel = (column) => {
  if (column === 'engagements') return 'Engagements'
  if (column === 'engagement') return 'Engagement rate'
  if (column === 'postClicks') return 'Post clicks'
  return column.charAt(0).toUpperCase() + column.slice(1)
}

const formatPostPerformanceMetricValue = (metricKey, value) => {
  if (metricKey === 'engagement') {
    return formatTopPostEngagementRate(value)
  }
  if (value == null || Number.isNaN(Number(value))) return '—'
  return formatNumber(Number(value))
}

/** Last 30 / custom use start_date + end_date so prior-month days are included. */
function dashboardReportRangeMode(preset) {
  return preset === 'month_to_date' ? 'month' : 'custom'
}

function getLastMonthRange() {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const end = new Date(now.getFullYear(), now.getMonth(), 0)
  return { start, end }
}

const chartVisibilitySections = [
  {
    id: 'social',
    label: 'Social Performance',
    charts: [
      { id: 'goalPerformance', label: 'Goal Performance' },
      { id: 'platformPerformanceComparison', label: 'Platform Performance Comparison' },
      { id: 'campaignPerformance', label: 'Event Performance' },
      { id: 'monthlySocialPerformance', label: 'Social Performance' },
      { id: 'socialPerformanceBenchmark', label: 'Social Performance: You vs Competitors' },
      { id: 'topPosts', label: 'Post Performance' },
      { id: 'topStories', label: 'Top Story' },
    ],
  },
  {
    id: 'content',
    label: 'Content Analytics',
    charts: [{ id: 'optimal_posting_times', label: 'Posting Time Performance' }],
  },
  {
    id: 'paid',
    label: 'Paid Media',
    charts: [
      { id: 'paidMediaPerformance', label: 'Paid Media Performance' },
      { id: 'topPerformingAds', label: 'Top Ads' },
      { id: 'topObjectives', label: 'Top Objectives' },
      { id: 'topCampaigns', label: 'Top Campaigns' },
      { id: 'advertisingPerformance', label: 'Advertising Performance' },
    ],
  },
  {
    id: 'audience',
    label: 'Audience Insights',
    charts: [{ id: 'audienceInsights', label: 'Audience Demographics' }],
  },
  {
    id: 'monitoring',
    label: 'Monitoring & Engagement',
    charts: [
      { id: 'messages', label: 'Messages' },
      { id: 'messageSentiment', label: 'Message Sentiment' },
      { id: 'alerts', label: 'Alerts' },
      { id: 'timeOfDay', label: 'Time of Day' },
      { id: 'messageHeatmap', label: 'Message Heatmap' },
    ],
  },
  {
    id: 'goals',
    label: 'Goals',
    charts: [{ id: 'socialGoalsOverview', label: 'Social Media Goals Overview' }],
  },
]

const DEFAULT_WIDGET_ORDER = chartVisibilitySections.flatMap((s) => s.charts.map((c) => c.id))
const CHART_LOOKUP = chartVisibilitySections.reduce((acc, section) => {
  section.charts.forEach((chart) => {
    acc[chart.id] = { ...chart, sectionId: section.id, sectionLabel: section.label }
  })
  return acc
}, {})

/** Sections omitted from report export customize list (none currently). */
const REPORT_EXPORT_EXCLUDED_SECTION_IDS = new Set()
/** Duplicate paid-media views of Top Performing Ads — keep dashboard tabs, one report table. */
const REPORT_EXPORT_EXCLUDED_CHART_IDS = new Set([
  "advertisingPerformance",
  "topObjectives",
  "topCampaigns",
])

/** Tints for Messages platform summary cards (cycles with platform index). */
const MESSAGES_SUMMARY_PLATFORM_TONE_CLASSES = [
  'sentimentSummaryCard--messagesP0',
  'sentimentSummaryCard--messagesP1',
  'sentimentSummaryCard--messagesP2',
  'sentimentSummaryCard--messagesP3',
  'sentimentSummaryCard--messagesP4',
]

const STORAGE_KEY = 'dashboard_analytics_prefs'

const loadDashboardPrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && Array.isArray(parsed.widgetOrder) && Array.isArray(parsed.visibleWidgets)) {
      return {
        ...parsed,
        showSohoPulse: parsed.showSohoPulse !== false,
      }
    }
  } catch (_e) {
    /* ignore */
  }
  return null
}

const VALID_ANALYTICS_TABS = ['all', 'social', 'content', 'paid', 'audience', 'monitoring']
const ANALYTICS_FETCH_SECTION_IDS = ['social', 'content', 'paid', 'audience', 'monitoring']
const PAID_MEDIA_WIDGET_IDS = [
  'paidMediaPerformance',
  'topPerformingAds',
  'topObjectives',
  'topCampaigns',
  'advertisingPerformance',
]

function ensurePaidMediaWidgetsVisible(chartIds, allIds) {
  const next = new Set(chartIds)
  PAID_MEDIA_WIDGET_IDS.forEach((id) => {
    if (allIds.includes(id)) next.add(id)
  })
  return next
}

const ALWAYS_VISIBLE_SOCIAL_WIDGET_IDS = ['socialPerformanceBenchmark']

function ensureAlwaysVisibleSocialWidgets(chartIds, allIds) {
  const next = new Set(chartIds)
  ALWAYS_VISIBLE_SOCIAL_WIDGET_IDS.forEach((id) => {
    if (allIds.includes(id)) next.add(id)
  })
  return next
}

function migrateVisibleWidgetsFromPrefs(migrated, allIds) {
  const saved = new Set(migrated)
  const next = new Set()
  for (const id of migrated) {
    if (allIds.includes(id)) next.add(id)
  }
  DEFAULT_WIDGET_ORDER.forEach((id) => {
    if (allIds.includes(id) && !saved.has(id)) {
      next.add(id)
    }
  })
  return ensureAlwaysVisibleSocialWidgets(
    ensurePaidMediaWidgetsVisible(next, allIds),
    allIds,
  )
}
const COMING_SOON_ANALYTICS_TABS = new Set()
const isComingSoonAnalyticsTab = (tabId) => COMING_SOON_ANALYTICS_TABS.has(tabId)
/** Coming-soon sections stay off the aggregated All view until their API ships. */
const isAnalyticsSectionInAllView = (sectionId) => !COMING_SOON_ANALYTICS_TABS.has(sectionId)

/** Positive / Friction theme driver cards under Message Sentiment — hidden for now. */
const SHOW_SENTIMENT_THEME_DRIVERS = false

const saveDashboardPrefs = (widgetOrder, visibleWidgets, analyticsSectionTab, widgetVisibility = {}) => {
  try {
    const payload = { widgetOrder, visibleWidgets: Array.from(visibleWidgets) }
    if (analyticsSectionTab) payload.analyticsSectionTab = analyticsSectionTab
    if (widgetVisibility.showSohoPulse !== undefined) payload.showSohoPulse = widgetVisibility.showSohoPulse
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (_e) {
    /* ignore */
  }
}

const mergeWithDefaultOrder = (savedOrder) => {
  const mapped = (savedOrder || []).map((id) => {
    if (id === 'optimalPostingTimesRange') return 'optimal_posting_times'
    if (id === 'topPaidAds') return 'topCampaigns'
    return id
  })
  const deduped = []
  const seen = new Set()
  for (const id of mapped) {
    if (seen.has(id)) continue
    seen.add(id)
    deduped.push(id)
  }
  const known = new Set(DEFAULT_WIDGET_ORDER)
  const ordered = deduped.filter((id) => known.has(id))
  DEFAULT_WIDGET_ORDER.forEach((id) => {
    if (ordered.includes(id)) return
    const defaultIdx = DEFAULT_WIDGET_ORDER.indexOf(id)
    let insertAt = ordered.length
    for (let i = defaultIdx + 1; i < DEFAULT_WIDGET_ORDER.length; i += 1) {
      const neighborIdx = ordered.indexOf(DEFAULT_WIDGET_ORDER[i])
      if (neighborIdx !== -1) {
        insertAt = neighborIdx
        break
      }
    }
    ordered.splice(insertAt, 0, id)
  })
  return ordered
}

const REPORT_SCHEDULE_STORAGE_KEY = 'dashboard_report_schedule'

const loadReportSchedule = () => {
  try {
    const raw = localStorage.getItem(REPORT_SCHEDULE_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') return parsed
  } catch (_e) {
    /* ignore */
  }
  return null
}

const saveReportSchedule = (schedule) => {
  try {
    if (schedule && typeof schedule === 'object') {
      localStorage.setItem(REPORT_SCHEDULE_STORAGE_KEY, JSON.stringify(schedule))
    }
  } catch (_e) {
    /* ignore */
  }
}

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim())

const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const formatScheduleTime = (time) => {
  if (!time) return ''
  const [hours, minutes] = time.split(':').map(Number)
  if (Number.isNaN(hours)) return time
  const date = new Date()
  date.setHours(hours, minutes || 0, 0, 0)
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

const formatReportScheduleStatus = (schedule) => {
  if (!schedule?.enabled) {
    return { label: 'Not scheduled', isScheduled: false }
  }

  const frequencyLabel =
    schedule.frequency === 'monthly'
      ? `Monthly · day ${schedule.dayOfMonth ?? 1}`
      : `Weekly · ${WEEKDAY_LABELS[schedule.dayOfWeek ?? 1] || 'Monday'}`
  const timeLabel = formatScheduleTime(schedule.time)
  const recipientCount = Array.isArray(schedule.recipients) ? schedule.recipients.length : 0
  const recipientLabel = recipientCount === 1 ? '1 recipient' : `${recipientCount} recipients`

  return {
    label: [frequencyLabel, timeLabel, recipientLabel].filter(Boolean).join(' · '),
    isScheduled: true,
  }
}

const buildExportSettingsSummary = ({
  reportName,
  exportFormat,
  reportDataScope,
}) => ({
  reportName,
  details: [
    (exportFormat || 'pdf').toUpperCase(),
    formatDataScopeLabel(reportDataScope),
  ].join(' • '),
})

const IndeterminateCheckbox = ({ checked, indeterminate, className, ...props }) => {
  const checkboxRef = useRef(null)

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  return (
    <input
      ref={checkboxRef}
      type="checkbox"
      checked={checked}
      className={className}
      {...props}
    />
  )
}

const DragHandleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
)

const SortableSectionHeader = ({
  section,
  isExpanded,
  onToggleExpand,
  selectedCount,
  totalCount,
  onToggleSection,
  isDraggable,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id, disabled: !isDraggable })

  const style = isDraggable ? { transform: CSS.Transform.toString(transform), transition } : undefined
  const isSectionChecked = selectedCount === totalCount
  const isSectionIndeterminate = selectedCount > 0 && selectedCount < totalCount

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`dashboardConfigureSectionHeader ${isDragging ? 'dashboardConfigureSectionHeader--dragging' : ''} ${isExpanded ? 'dashboardConfigureSectionHeader--expanded' : ''}`}
    >
      {isDraggable && (
        <div
          ref={setActivatorNodeRef}
          className="dashboardConfigureSectionDragHandle"
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder ${section.label} section`}
        >
          <DragHandleIcon />
        </div>
      )}
      <label className="dashboardConfigureSectionHeaderLabel" onClick={(e) => e.stopPropagation()}>
        <IndeterminateCheckbox
          className="dashboardConfigureCheckbox"
          checked={isSectionChecked}
          indeterminate={isSectionIndeterminate}
          onChange={() => onToggleSection(section)}
        />
        <span>{section.label}</span>
        <span className="dashboardConfigureSectionBadge">{selectedCount}/{totalCount}</span>
      </label>
      <button
        type="button"
        className="dashboardConfigureSectionExpandBtn"
        onClick={() => onToggleExpand(section.id)}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? `Collapse ${section.label}` : `Expand ${section.label}`}
      >
        {isExpanded ? '−' : '+'}
      </button>
    </div>
  )
}

const SortableChartItem = ({ chart, isVisible, onToggle, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: chart.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const dragging = isSortableDragging || isDragging

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`dashboardConfigureSortableItem ${dragging ? 'dashboardConfigureSortableItem--dragging' : ''}`}
    >
      <div
        ref={setActivatorNodeRef}
        className="dashboardConfigureSortableHandle"
        {...attributes}
        {...listeners}
        aria-label={`Drag to reorder ${chart.label}`}
      >
        <DragHandleIcon />
      </div>
      <label
        className="dashboardConfigureSortableLabel"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          className="dashboardConfigureCheckbox"
          checked={isVisible}
          onChange={() => onToggle(chart.id)}
        />
        <span>{chart.label}</span>
      </label>
    </div>
  )
}

const insightsSections = [
  { id: 'insights', label: 'Insights' },
  { id: 'wins', label: 'Wins' },
  { id: 'challenges', label: 'Challenges' },
  { id: 'recommendations', label: 'Recommendations' },
]

const DEFAULT_GOAL_METRIC_GROUPS = [
  {
    id: 'visibility',
    label: 'Visibility',
    metrics: [
      {
        id: 'instagramImpressions',
        name: 'Instagram Impressions',
        platform: 'Instagram',
        source: 'Organic',
        format: 'number',
        defaultTarget: 2400000,
        defaultPeriod: 'monthly',
        current: 1750000,
        helper: 'Suggested: 2.4M based on last 90 days',
      },
      {
        id: 'facebookReach',
        name: 'Facebook Reach',
        platform: 'Facebook',
        source: 'Organic',
        format: 'number',
        defaultTarget: 1250000,
        defaultPeriod: 'monthly',
        current: 840000,
        helper: 'Suggested: 1.25M based on last 90 days',
      },
      {
        id: 'linkedinImpressions',
        name: 'LinkedIn Impressions',
        platform: 'LinkedIn',
        source: 'Organic',
        format: 'number',
        defaultTarget: 380000,
        defaultPeriod: 'monthly',
        current: 245000,
        helper: 'Suggested: 380k based on last 90 days',
      },
      {
        id: 'twitterImpressions',
        name: 'X (Twitter) Impressions',
        platform: 'X',
        source: 'Organic',
        format: 'number',
        defaultTarget: 520000,
        defaultPeriod: 'monthly',
        current: 318000,
        helper: 'Suggested: 520k based on last 90 days',
      },
      {
        id: 'tiktokViews',
        name: 'TikTok Video Views',
        platform: 'TikTok',
        source: 'Organic',
        format: 'number',
        defaultTarget: 890000,
        defaultPeriod: 'monthly',
        current: 612000,
        helper: 'Suggested: 890k based on last 90 days',
      },
      {
        id: 'crossPlatformImpressions',
        name: 'Total Cross-Platform Impressions',
        platform: 'All Platforms',
        source: 'Mixed',
        format: 'number',
        defaultTarget: 3800000,
        defaultPeriod: 'monthly',
        current: 2960000,
        helper: 'Suggested: 3.8M based on last 90 days',
      },
    ],
  },
  {
    id: 'engagement',
    label: 'Engagement',
    metrics: [
      {
        id: 'instagramEngagementRate',
        name: 'Instagram Engagement Rate',
        platform: 'Instagram',
        source: 'Organic',
        format: 'percent',
        defaultTarget: 4.2,
        defaultPeriod: 'monthly',
        current: 3.6,
        helper: 'Suggested: 4.2% based on last 90 days',
      },
      {
        id: 'instagramSaves',
        name: 'Instagram Saves',
        platform: 'Instagram',
        source: 'Organic',
        format: 'number',
        defaultTarget: 12500,
        defaultPeriod: 'monthly',
        current: 9200,
        helper: 'Suggested: 12.5k based on last 90 days',
      },
      {
        id: 'instagramShares',
        name: 'Instagram Shares',
        platform: 'Instagram',
        source: 'Organic',
        format: 'number',
        defaultTarget: 8300,
        defaultPeriod: 'monthly',
        current: 6100,
        helper: 'Suggested: 8.3k based on last 90 days',
      },
      {
        id: 'facebookEngagement',
        name: 'Facebook Engagement',
        platform: 'Facebook',
        source: 'Organic',
        format: 'number',
        defaultTarget: 45000,
        defaultPeriod: 'monthly',
        current: 31400,
        helper: 'Suggested: 45k based on last 90 days',
      },
      {
        id: 'linkedinEngagement',
        name: 'LinkedIn Engagement',
        platform: 'LinkedIn',
        source: 'Organic',
        format: 'number',
        defaultTarget: 18500,
        defaultPeriod: 'monthly',
        current: 12300,
        helper: 'Suggested: 18.5k based on last 90 days',
      },
      {
        id: 'twitterEngagement',
        name: 'X (Twitter) Engagement',
        platform: 'X',
        source: 'Organic',
        format: 'number',
        defaultTarget: 22000,
        defaultPeriod: 'monthly',
        current: 14800,
        helper: 'Suggested: 22k based on last 90 days',
      },
      {
        id: 'tiktokEngagement',
        name: 'TikTok Engagement',
        platform: 'TikTok',
        source: 'Organic',
        format: 'number',
        defaultTarget: 42000,
        defaultPeriod: 'monthly',
        current: 28600,
        helper: 'Suggested: 42k based on last 90 days',
      },
    ],
  },
  {
    id: 'growth',
    label: 'Growth',
    metrics: [
      {
        id: 'instagramFollowerGrowth',
        name: 'Instagram Follower Growth',
        platform: 'Instagram',
        source: 'Organic',
        format: 'number',
        defaultTarget: 15000,
        defaultPeriod: 'quarterly',
        current: 9200,
        helper: 'Suggested: 15k based on current trends',
      },
      {
        id: 'facebookPageLikes',
        name: 'Facebook Page Likes',
        platform: 'Facebook',
        source: 'Organic',
        format: 'number',
        defaultTarget: 8000,
        defaultPeriod: 'monthly',
        current: 5100,
        helper: 'Suggested: 8k based on last 90 days',
      },
      {
        id: 'instagramProfileVisits',
        name: 'Instagram Profile Visits',
        platform: 'Instagram',
        source: 'Organic',
        format: 'number',
        defaultTarget: 125000,
        defaultPeriod: 'monthly',
        current: 94000,
        helper: 'Suggested: 125k based on last 90 days',
      },
      {
        id: 'linkedinFollowerGrowth',
        name: 'LinkedIn Follower Growth',
        platform: 'LinkedIn',
        source: 'Organic',
        format: 'number',
        defaultTarget: 4200,
        defaultPeriod: 'quarterly',
        current: 2680,
        helper: 'Suggested: 4.2k based on current trends',
      },
      {
        id: 'twitterFollowerGrowth',
        name: 'X (Twitter) Follower Growth',
        platform: 'X',
        source: 'Organic',
        format: 'number',
        defaultTarget: 5800,
        defaultPeriod: 'quarterly',
        current: 3650,
        helper: 'Suggested: 5.8k based on current trends',
      },
      {
        id: 'tiktokFollowerGrowth',
        name: 'TikTok Follower Growth',
        platform: 'TikTok',
        source: 'Organic',
        format: 'number',
        defaultTarget: 12500,
        defaultPeriod: 'quarterly',
        current: 8200,
        helper: 'Suggested: 12.5k based on current trends',
      },
    ],
  },
  {
    id: 'inbox',
    label: 'Inbox Experience',
    metrics: [
      {
        id: 'averageResponseTime',
        name: 'Average Response Time',
        platform: 'All Platforms',
        source: 'Mixed',
        format: 'time',
        defaultTarget: 30,
        defaultPeriod: 'monthly',
        current: 42,
        helper: 'Suggested: 30 min based on current performance',
      },
      {
        id: 'responseRate',
        name: 'Response Rate',
        platform: 'All Platforms',
        source: 'Mixed',
        format: 'percent',
        defaultTarget: 95,
        defaultPeriod: 'monthly',
        current: 91,
        helper: 'Suggested: 95% based on current performance',
      },
      {
        id: 'aiRepliesSent',
        name: 'AI Replies Sent',
        platform: 'All Platforms',
        source: 'Mixed',
        format: 'number',
        defaultTarget: 6000,
        defaultPeriod: 'monthly',
        current: 4200,
        helper: 'Suggested: 6k based on last 90 days',
      },
      {
        id: 'customerSatisfactionScore',
        name: 'Customer Satisfaction Score',
        platform: 'All Platforms',
        source: 'Mixed',
        format: 'percent',
        defaultTarget: 90,
        defaultPeriod: 'monthly',
        current: 86,
        helper: 'Suggested: 90% based on current performance',
      },
    ],
  },
  {
    id: 'cross-platform-growth',
    label: 'Cross-Platform Growth',
    metrics: [
      {
        id: 'linkedinFollowerGrowthMonthly',
        name: 'LinkedIn Follower Growth',
        platform: 'LinkedIn',
        source: 'Organic',
        format: 'number',
        defaultTarget: 500,
        defaultPeriod: 'monthly',
        current: 340,
        helper: 'Target: +500 followers/month',
      },
      {
        id: 'tiktokEngagementRate',
        name: 'TikTok Engagement Rate',
        platform: 'TikTok',
        source: 'Organic',
        format: 'percent',
        defaultTarget: 8,
        defaultPeriod: 'monthly',
        current: 6.2,
        helper: 'Target: 8%',
      },
    ],
  },
]

const Dashboard = forwardRef(function Dashboard(
  { embedded = false, propertyId = null, propertyName = null, onHasConfigChanges } = {},
  ref,
) {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { selectedClient, clients } = useSelector((state) => state.clients || {})

  useEffect(() => {
    if (!propertyId || !Array.isArray(clients) || clients.length === 0) return
    const match = clients.find(
      (client) => String(client?.id ?? client?.clientId) === String(propertyId)
    )
    if (!match) return
    const currentId = selectedClient?.id ?? selectedClient?.clientId
    if (String(currentId) === String(propertyId)) return
    dispatch(setSelectedClient(match))
  }, [clients, dispatch, propertyId, selectedClient])
  const inboxHealthState = useSelector((state) => state.inboxHealth)
  const dailyPostStatsState = useSelector((state) => state.dailyPostStats)
  const clientMessagesReportState = useSelector((state) => state.clientMessagesReport)
  const clientSentimentsReportState = useSelector((state) => state.clientSentimentsReport)
  const clientAlertsReportState = useSelector((state) => state.clientAlertsReport)
  const clientMessagesTimeOfDayState = useSelector((state) => state.clientMessagesTimeOfDay)
  const clientAlertsTimeOfDayState = useSelector((state) => state.clientAlertsTimeOfDay)
  const clientMessagesHeatmapState = useSelector((state) => state.clientMessagesHeatmap)
  const clientDemographicsFollowersState = useSelector((state) => state.clientDemographicsFollowers)
  const clientPostHeatmapState = useSelector((state) => state.clientPostHeatmap)
  const clientTopPostsState = useSelector((state) => state.clientTopPosts)
  const clientSocialPerformanceState = useSelector((state) => state.clientSocialPerformance)
  const clientCompetitivePerformanceState = useSelector((state) => state.clientCompetitivePerformance)
  const clientCampaignPerformanceState = useSelector((state) => state.clientCampaignPerformance)
  const clientStoryPerformanceState = useSelector((state) => state.clientStoryPerformance)
  const clientPaidPerformanceState = useSelector((state) => state.clientPaidPerformance)
  const clientPaidMediaPerformanceState = useSelector((state) => state.clientPaidMediaPerformance)
  const metricGoalTypesState = useSelector((state) => state.metricGoalTypes)
  const metricGoalsState = useSelector((state) => state.metricGoals)
  const clientMetricGoalsState = useSelector((state) => state.clientMetricGoals)
  const clientYtdPagePerformanceState = useSelector((state) => state.clientYtdPagePerformance)
  const socialNetworkAccountsState = useSelector((state) => state.socialNetworkAccounts)
  const clientDetailsState = useSelector((state) => state.clientDetails)
  const socialNetworkTypes = useSelector((state) => state.socialNetworkTypes?.types ?? [])
  const ytdPaidOrganicPerformanceIndex = useMemo(() => {
    const pages = clientYtdPagePerformanceState.pages
    if (Array.isArray(pages) && pages.length > 0) {
      return buildPaidOrganicPerformanceIndex(pages, socialNetworkTypes)
    }
    return selectPaidOrganicPerformanceIndex({ clientYtdPagePerformance: clientYtdPagePerformanceState })
  }, [clientYtdPagePerformanceState, socialNetworkTypes])
  const goalMetricGroups = useMemo(() => {
    if (metricGoalsState.goals?.length > 0) {
      return buildGoalMetricGroupsFromApi(metricGoalsState.goals)
    }
    return []
  }, [metricGoalsState.goals])
  const socialPerformancePlatforms = useMemo(
    () => resolveSocialPerformancePlatforms(clientSocialPerformanceState, socialNetworkTypes),
    [clientSocialPerformanceState, socialNetworkTypes],
  )
  const pulseSocialPerformancePlatforms = useMemo(
    () => resolvePulseSocialPerformancePlatforms(clientSocialPerformanceState, socialNetworkTypes),
    [clientSocialPerformanceState, socialNetworkTypes],
  )
  const selectedClientId = selectedClient?.id ?? selectedClient?.clientId ?? null
  const connectedPropertyPlatformKeysList = useMemo(() => {
    if (!selectedClientId) return null
    const clientKey = String(selectedClientId)
    if (socialNetworkAccountsState.status !== 'succeeded') return null
    if (socialNetworkAccountsState.fetchedForClientId !== clientKey) return null
    return orderedConnectedChartKeys(
      connectedChartKeysFromSocialAccounts(
        socialNetworkAccountsState.accounts,
        socialNetworkTypes,
      ),
    )
  }, [
    selectedClientId,
    socialNetworkAccountsState.status,
    socialNetworkAccountsState.fetchedForClientId,
    socialNetworkAccountsState.accounts,
    socialNetworkTypes,
  ])
  const selectedClientName =
    selectedClient?.name ??
    (selectedClient?.id ? `Client ${selectedClient.id}` : null) ??
    null

  const propertyChartTimezone = useMemo(() => {
    const client = resolveSchedulingClient(selectedClient, clients)
    const iana =
      getClientIanaTimeZone(client) ||
      getClientIanaTimeZone(selectedClient) ||
      getClientIanaTimeZone(clientDetailsState.detail)
    return resolvePropertyChartTimezone(iana)
  }, [selectedClient, clients, clientDetailsState.detail])

  const [activeTab, setActiveTab] = useState('analytics')
  useEffect(() => {
    document.querySelectorAll('.dashboard-report-capture-raster').forEach((node) => node.remove())
    document.body.classList.remove('dashboard-report-capture-mode')
    unlockDashboardReportViewport()
  }, [])

  useEffect(() => {
    setAnalyticsSectionTab('all')
  }, [])
  const [dateRange, setDateRange] = useState(() => getLastMonthRange())
  const [dateRangePreset, setDateRangePreset] = useState('previous_month')
  const [dateRangeOpen, setDateRangeOpen] = useState(false)
  const [platformFilterOpen, setPlatformFilterOpen] = useState(false)
  const [showCustomPicker, setShowCustomPicker] = useState(false)
  const [customRange, setCustomRange] = useState({ start: '', end: '' })
  const [goalsTimeRange, setGoalsTimeRange] = useState('month')
  const [selectedGoalsPreset, setSelectedGoalsPreset] = useState(null)
  const [goalsTypeOpen, setGoalsTypeOpen] = useState(false)
  const [goalsTimeOpen, setGoalsTimeOpen] = useState(false)
  const [goalsPlatformFilterOpen, setGoalsPlatformFilterOpen] = useState(false)
  const [goalsPlatformFilter, setGoalsPlatformFilter] = useState('all')
  const [customGoals, setCustomGoals] = useState([])
  const [customGoalModalOpen, setCustomGoalModalOpen] = useState(false)
  const [customGoalNameDraft, setCustomGoalNameDraft] = useState('')
  const [customGoalIntentDraft, setCustomGoalIntentDraft] = useState('')
  const [customGoalMetricsDraft, setCustomGoalMetricsDraft] = useState(new Set())
  const [customGoalTimeRangeDraft, setCustomGoalTimeRangeDraft] = useState('month')
  const [customGoalProposal, setCustomGoalProposal] = useState(null)
  const [customGoalManualMode, setCustomGoalManualMode] = useState(false)
  const [customGoalExpandedGroups, setCustomGoalExpandedGroups] = useState(
    () => new Set(DEFAULT_GOAL_METRIC_GROUPS.map((group) => group.id))
  )
  const [selectedGoalMetrics, setSelectedGoalMetrics] = useState(() => new Set())
  const [committedGoalMetrics, setCommittedGoalMetrics] = useState(() => new Set())
  const [goalsPersistPending, setGoalsPersistPending] = useState(false)
  const [goalMetricTargets, setGoalMetricTargets] = useState({})
  const [goalMetricPeriods, setGoalMetricPeriods] = useState({})
  const [goalMetricReach, setGoalMetricReach] = useState({})
  const [goalMetricPeriodStarts, setGoalMetricPeriodStarts] = useState({})
  const [goalMetricPeriodValues, setGoalMetricPeriodValues] = useState({})
  const [goalPeriodEndedAcknowledged, setGoalPeriodEndedAcknowledged] = useState(() => new Set())
  const [goalPeriodEndedNoticeMetricId, setGoalPeriodEndedNoticeMetricId] = useState(null)
  const [goalExtendConfirmMetricId, setGoalExtendConfirmMetricId] = useState(null)
  const [goalsActivated, setGoalsActivated] = useState(false)
  const [showGoalsCoachmarks, setShowGoalsCoachmarks] = useState(true)
  const [showGoalsProgress, setShowGoalsProgress] = useState(false)
  const [goalsEditMode, setGoalsEditMode] = useState(true)
  const [goalsConfigExpanded, setGoalsConfigExpanded] = useState(null)
  const [expandedGoalGroups, setExpandedGoalGroups] = useState(() => new Set())
  const [editingMetricIds, setEditingMetricIds] = useState(() => new Set())
  const [goalsShowSetup, setGoalsShowSetup] = useState(false)
  const [goalsPreActivationDrawerOpen, setGoalsPreActivationDrawerOpen] = useState(false)
  const [showDeleteGoalConfirm, setShowDeleteGoalConfirm] = useState(false)
  const [topPostsPlatform, setTopPostsPlatform] = useState('instagram')
  const [topPostsView, setTopPostsView] = useState('posts')
  const [topPostsViewOpen, setTopPostsViewOpen] = useState(false)
  const [topPostsRankModeOpen, setTopPostsRankModeOpen] = useState(false)
  const [topPostsPlatformFilterOpen, setTopPostsPlatformFilterOpen] = useState(false)
  const [topPostsRankBy, setTopPostsRankBy] = useState('engagement')
  const [topPostsRankMode, setTopPostsRankMode] = useState('top')
  const [topPostsVisibleCount, setTopPostsVisibleCount] = useState(15)
  const [topStoriesVisibleForExport, setTopStoriesVisibleForExport] = useState([])
  const [paidMediaPlatformFilterOpen, setPaidMediaPlatformFilterOpen] = useState(false)
  const [paidMediaPlatform, setPaidMediaPlatform] = useState('all')
  const [topAdsPlatforms, setTopAdsPlatforms] = useState(() => new Set(['all']))
  const [paidWidgetView, setPaidWidgetView] = useState('ads')
  const [paidWidgetSortBy, setPaidWidgetSortBy] = useState('roas')
  const [configurePlatformFilterOpen, setConfigurePlatformFilterOpen] = useState(false)
  const [showChartTrendCampaign, setShowChartTrendCampaign] = useState(false)
  const [showChartTrendAudience, setShowChartTrendAudience] = useState(false)
  const [showChartTrendAlerts, setShowChartTrendAlerts] = useState(false)
  const [platformPulsePlatforms, setPlatformPulsePlatforms] = useState(() => new Set())
  const [monthlySocialComparePeriod, setMonthlySocialComparePeriod] = useState('mom')
  const [monthlySocialSource, setMonthlySocialSource] = useState('all')
  const campaignChannel = useMemo(() => {
    if (platformPulsePlatforms.size === 0) return 'all'
    if (platformPulsePlatforms.size === 1) return [...platformPulsePlatforms][0]
    return 'all'
  }, [platformPulsePlatforms])
  const [isInsightsOpen, setIsInsightsOpen] = useState(false)
  const [insightsTab, setInsightsTab] = useState('insights')
  const [insightsSeverityFilter, setInsightsSeverityFilter] = useState('all')
  const [insightReviewedIds, setInsightReviewedIds] = useState(() => new Set())
  const [socialPerformanceTab, setSocialPerformanceTab] = useState('engagement')
  const [competitorMetric, setCompetitorMetric] = useState('response')
  const [competitorPlatform, setCompetitorPlatform] = useState('all')
  const [messageChannelFilter, setMessageChannelFilter] = useState('all')
  const [sentimentFilter, setSentimentFilter] = useState('all')
  const [hoveredKpiId, setHoveredKpiId] = useState(null)
  const [hoveredKpiRect, setHoveredKpiRect] = useState(null)
  const [openKpiCardState, setOpenKpiCardState] = useState({ id: null, rect: null })
  const openKpiCardId = openKpiCardState.id
  const [isDocked, setIsDocked] = useState(false)
  const [isConfigureOpen, setIsConfigureOpen] = useState(false)
  const [isExportOpen, setIsExportOpen] = useState(false)
  const [exportFormat, setExportFormat] = useState('pdf')
  const [exportBusy, setExportBusy] = useState(false)
  const [exportProgress, setExportProgress] = useState(null)
  const [exportName, setExportName] = useState('')
  const [exportCustomizeMode, setExportCustomizeMode] = useState(false)
  const [exportSectionCollapsed, setExportSectionCollapsed] = useState(true)
  const [exportPaidMediaCaptureView, setExportPaidMediaCaptureView] = useState(null)
  const [exportPaidMediaAllViews, setExportPaidMediaAllViews] = useState(false)
  const exportContextRunRef = useRef({
    reportDataScope: REPORT_DATA_SCOPE.CURRENT_VIEW,
    widgetDataScopes: {},
    allDataChartIds: [],
  })
  const isChartDataReadyRef = useRef(() => true)
  const buildExportDataContextRef = useRef(() => ({}))
  const paidExportSortRef = useRef({
    view: 'ads',
    sortBy: 'roas',
    sortDir: 'desc',
    tableSortBy: 'impressions',
    tableSortDir: 'desc',
  })
  const [audienceDemographicPlatform, setAudienceDemographicPlatform] = useState('all')
  const dashboardView = EMPTY_DASHBOARD_DATA

  const messageVolumeDaily = useMemo(
    () => getMessagesByCalendarDayFromSeries(dashboardView.combinedSeries, dateRange.start, dateRange.end),
    [dashboardView.combinedSeries, dateRange.start, dateRange.end],
  )
  const {
    competitors,
    platforms,
    platformSeries,
    ranges,
    monthlySocialColumns,
    monthlySocialRows,
    audienceAgeGender,
    audienceTopCities,
    audienceTopCountries,
    audienceDemographicsByPlatform,
    topPostsData,
    monitoringSeries,
    monitoringShare,
    sentimentSeries,
    sentimentShare,
    sentimentKeywordsPositive = [],
    sentimentKeywordsNegative = [],
    alertsSeries,
    timeOfDayMessages,
    timeOfDayAlerts,
    messageHeatmapHours,
    messageHeatmapDays,
    messageHeatmapValues,
    socialPerformanceRows,
    competitorDaily,
    liveNowCards,
    paidMedia,
    totals,
    monitoringSharePrevious,
  } = dashboardView

  const topStoriesAvailablePlatforms = useMemo(() => {
    const connected = (platforms || []).filter((platformId) => TOP_STORY_PLATFORMS.includes(platformId))
    if (platformPulsePlatforms.size === 0) return connected
    return connected.filter((platformId) => platformPulsePlatforms.has(platformId))
  }, [platforms, platformPulsePlatforms])

  const platformSeriesForComparison = useMemo(() => {
    if (platformPulsePlatforms.size === 0) return platformSeries
    const next = {}
    platformPulsePlatforms.forEach((p) => {
      if (platformSeries[p]) next[p] = platformSeries[p]
    })
    return Object.keys(next).length > 0 ? next : platformSeries
  }, [platformSeries, platformPulsePlatforms])

  const messageVolumeDailyHourly = useMemo(
    () => buildDailyCalendarHourlyMatrix(messageVolumeDaily.dates, messageVolumeDaily.values),
    [messageVolumeDaily.dates, messageVolumeDaily.values],
  )

  const messageHeatmapMonthlyHourly = useMemo(
    () => buildMonthlyCalendarHourlyMatrix(messageVolumeDaily.dates, messageVolumeDaily.values),
    [messageVolumeDaily.dates, messageVolumeDaily.values],
  )

  const allChartIds = useMemo(
    () => chartVisibilitySections.flatMap((section) => section.charts.map((chart) => chart.id)),
    []
  )

  const exportableChartIds = useMemo(
    () =>
      chartVisibilitySections
        .filter((section) => !REPORT_EXPORT_EXCLUDED_SECTION_IDS.has(section.id))
        .flatMap((section) => section.charts.map((chart) => chart.id))
        .filter((id) => !REPORT_EXPORT_EXCLUDED_CHART_IDS.has(id)),
    []
  )

  const exportChartVisibilitySections = useMemo(
    () =>
      chartVisibilitySections
        .filter((section) => !REPORT_EXPORT_EXCLUDED_SECTION_IDS.has(section.id))
        .map((section) => ({
          ...section,
          charts: section.charts.filter((chart) => !REPORT_EXPORT_EXCLUDED_CHART_IDS.has(chart.id)),
        }))
        .filter((section) => section.charts.length > 0),
    []
  )

  const [widgetOrder, setWidgetOrder] = useState(() => {
    const prefs = loadDashboardPrefs()
    return prefs ? mergeWithDefaultOrder(prefs.widgetOrder) : [...DEFAULT_WIDGET_ORDER]
  })
  const [selectedCharts, setSelectedCharts] = useState(() => {
    const prefs = loadDashboardPrefs()
    if (prefs && Array.isArray(prefs.visibleWidgets)) {
      const migrated = prefs.visibleWidgets.map((id) => {
        if (id === 'optimalPostingTimesRange') return 'optimal_posting_times'
        if (id === 'topPaidAds') return 'topCampaigns'
        return id
      })
      const valid = new Set(migrated.filter((id) => allChartIds.includes(id)))
      if (valid.size > 0) {
        return migrateVisibleWidgetsFromPrefs(migrated, allChartIds)
      }
    }
    return new Set(allChartIds)
  })
  const [draftSelectedCharts, setDraftSelectedCharts] = useState(() => new Set(allChartIds))
  const [draftWidgetOrder, setDraftWidgetOrder] = useState(() => [...DEFAULT_WIDGET_ORDER])
  const [draftCampaignChannel, setDraftCampaignChannel] = useState('all')
  const [draftExportCharts, setDraftExportCharts] = useState(() =>
    new Set(
      chartVisibilitySections
        .filter((section) => !REPORT_EXPORT_EXCLUDED_SECTION_IDS.has(section.id))
        .flatMap((section) => section.charts.map((chart) => chart.id))
        .filter((id) => !REPORT_EXPORT_EXCLUDED_CHART_IDS.has(id)),
    ),
  )
  const [selectedInsights, setSelectedInsights] = useState(() => new Set(insightsSections.map((section) => section.id)))
  const [draftExportInsights, setDraftExportInsights] = useState(() => new Set())
  const [reportDataScope, setReportDataScope] = useState(REPORT_DATA_SCOPE.CURRENT_VIEW)
  const [widgetDataScopes, setWidgetDataScopes] = useState(() =>
    createDefaultWidgetDataScopes(allChartIds, REPORT_DATA_SCOPE.CURRENT_VIEW),
  )
  const [exportSourceTemplateId, setExportSourceTemplateId] = useState(null)
  const [exportSchedule, setExportSchedule] = useState(() => loadReportSchedule())
  const exportScheduleStatus = useMemo(
    () => formatReportScheduleStatus(exportSchedule),
    [exportSchedule],
  )
  const [exportContext, setExportContext] = useState({ mode: 'single', primary: {} })
  const [isScheduleOpen, setIsScheduleOpen] = useState(false)
  const [scheduleDraft, setScheduleDraft] = useState(() => ({
    enabled: false,
    frequency: 'weekly',
    dayOfWeek: 1,
    dayOfMonth: 1,
    time: '09:00',
    timezone: typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
    recipients: [],
    recipientInput: '',
    format: 'pdf',
  }))
  const [exportTemplateOpen, setExportTemplateOpen] = useState(false)
  const [exportCompareOpen, setExportCompareOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [configureSectionExpanded, setConfigureSectionExpanded] = useState(() =>
    chartVisibilitySections.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  )
  const [analyticsSectionTab, setAnalyticsSectionTab] = useState('all')
  const [readyAnalyticsSections, setReadyAnalyticsSections] = useState(() => new Set(['social']))
  const [showSohoPulse, setShowSohoPulse] = useState(() => {
    const prefs = loadDashboardPrefs()
    return prefs?.showSohoPulse !== false
  })
  const [draftShowSohoPulse, setDraftShowSohoPulse] = useState(true)
  const dashboardContentRef = useRef(null)
  const liveNowCardsRef = useRef(null)
  const liveNowCardsScrollRef = useRef(null)
  const topBarSentinelRef = useRef(null)
  const topBarRef = useRef(null)
  const dateRangeRef = useRef(null)
  const platformFilterRef = useRef(null)
  const topPostsViewRef = useRef(null)
  const topPostsRankModeRef = useRef(null)
  const topPostsPlatformFilterRef = useRef(null)
  const paidMediaPlatformFilterRef = useRef(null)
  const configurePlatformFilterRef = useRef(null)
  const exportTemplateRef = useRef(null)
  const exportCompareRef = useRef(null)
  const goalsTypeRef = useRef(null)
  const goalsTimeRef = useRef(null)
  const goalsPlatformFilterRef = useRef(null)
  const goalsBuilderRef = useRef(null)
  const goalsMetricListRef = useRef(null)
  const goalMetricEditSnapshotRef = useRef({})
  const prevHasActiveGoalsRef = useRef(false)
  const hydratedClientMetricGoalsClientIdRef = useRef(null)
  const hydratedClientMetricGoalsRowsKeyRef = useRef(null)
  const goalsSyncedClientIdRef = useRef(null)
  const kpiHoverTimeoutRef = useRef(null)
  const kpiCardRefs = useRef({})
  const kpiPopoverRef = useRef(null)
  const platformIconBySection = {
    Facebook: socialIcon('facebook.svg'),
    facebook: socialIcon('facebook.svg'),
    Instagram: socialIcon('instagram.svg'),
    instagram: socialIcon('instagram.svg'),
    instagram_graph: socialIcon('instagram.svg'),
    LinkedIn: socialIcon('linkedin.svg'),
    linkedin: socialIcon('linkedin.svg'),
    X: socialIcon('x.png'),
    x: socialIcon('x.png'),
    twitter: socialIcon('x.png'),
    TikTok: socialIcon('tiktok.svg'),
    tiktok: socialIcon('tiktok.svg'),
  }

  const chartIdsBySection = useMemo(() => {
    const map = {}
    chartVisibilitySections.forEach((section) => {
      map[section.id] = section.charts.map((chart) => chart.id)
    })
    return map
  }, [])

  const isChartVisible = (chartId) => selectedCharts.has(chartId)
  const orderedVisibleWidgets = useMemo(
    () => widgetOrder.filter((id) => selectedCharts.has(id)),
    [widgetOrder, selectedCharts]
  )
  const getOrderedChartsForSection = useCallback(
    (sectionId) => {
      const section = chartVisibilitySections.find((s) => s.id === sectionId)
      if (!section) return []
      return section.charts
        .filter((c) => widgetOrder.includes(c.id))
        .sort((a, b) => {
          const idxA = widgetOrder.indexOf(a.id)
          const idxB = widgetOrder.indexOf(b.id)
          return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB)
        })
    },
    [widgetOrder]
  )
  const showSocialSection = chartIdsBySection.social.some((chartId) => isChartVisible(chartId))
  useEffect(() => {
    setSelectedCharts((prev) => ensureAlwaysVisibleSocialWidgets(prev, allChartIds))
  }, [allChartIds])
  const orderedSocialRenderItems = useMemo(() => {
    const ordered = getOrderedChartsForSection('social').filter(
      (c) => selectedCharts.has(c.id) || c.id === 'socialPerformanceBenchmark',
    )
    const items = []
    let gridAdded = false
    for (const chart of ordered) {
      if (chart.id === 'goalPerformance' && !(goalsActivated && committedGoalMetrics.size > 0)) {
        continue
      }
      if (chart.id === 'socialPerformanceBenchmark') continue
      if (chart.id === 'campaignPerformance' || chart.id === 'platformPerformanceComparison') {
        if (!gridAdded) {
          items.push('grid')
          gridAdded = true
        }
      } else {
        items.push(chart.id)
      }
    }
    const monthlyIdx = items.indexOf('monthlySocialPerformance')
    items.splice(monthlyIdx === -1 ? items.length : monthlyIdx + 1, 0, 'socialPerformanceBenchmark')
    return items
  }, [getOrderedChartsForSection, selectedCharts, goalsActivated, committedGoalMetrics])
  const showContentSection = chartIdsBySection.content?.some((chartId) => isChartVisible(chartId))
  const showPaidSection = chartIdsBySection.paid.some((chartId) => isChartVisible(chartId))
  const showAudienceSection = chartIdsBySection.audience.some((chartId) => isChartVisible(chartId))
  const showMonitoringSection = chartIdsBySection.monitoring.some((chartId) => isChartVisible(chartId))
  const showGoalsSection = chartIdsBySection.goals.some((chartId) => isChartVisible(chartId))
  const hasAnyVisibleSection = showSocialSection || showContentSection || showPaidSection ||
    showAudienceSection || showMonitoringSection || true

  const visibleAnalyticsTabs = useMemo(() => {
    const sectionVisibility = {
      social: showSocialSection,
      content: showContentSection,
      paid: showPaidSection,
      audience: showAudienceSection,
      monitoring: showMonitoringSection,
    }
    const sectionTabs = chartVisibilitySections
      .filter((s) => s.id !== 'goals' && sectionVisibility[s.id])
      .map((s) => ({ id: s.id, label: s.label }))
    return [{ id: 'all', label: 'All' }, ...sectionTabs]
  }, [
    showSocialSection,
    showContentSection,
    showPaidSection,
    showAudienceSection,
    showMonitoringSection,
  ])

  const markAnalyticsSectionReady = useCallback((sectionId) => {
    if (!sectionId || sectionId === 'all') return
    setReadyAnalyticsSections((prev) => {
      if (prev.has(sectionId)) return prev
      const next = new Set(prev)
      next.add(sectionId)
      return next
    })
  }, [])

  useEffect(() => {
    if (analyticsSectionTab && analyticsSectionTab !== 'all') {
      markAnalyticsSectionReady(analyticsSectionTab)
    }
  }, [analyticsSectionTab, markAnalyticsSectionReady])

  useEffect(() => {
    if (!exportBusy) return
    setReadyAnalyticsSections(new Set(ANALYTICS_FETCH_SECTION_IDS))
  }, [exportBusy])

  const clientReportFetchPlan = useMemo(() => {
    const tab = analyticsSectionTab
    const all = tab === 'all'
    const sectionOn = (id) => {
      if (!(all || tab === id)) return false
      if (all && !exportBusy && !readyAnalyticsSections.has(id)) return false
      return true
    }
    return {
      social: sectionOn('social') && showSocialSection,
      content: sectionOn('content') && showContentSection,
      paid: sectionOn('paid') && showPaidSection,
      audience: sectionOn('audience') && showAudienceSection,
      monitoring: sectionOn('monitoring') && showMonitoringSection,
    }
  }, [
    analyticsSectionTab,
    exportBusy,
    readyAnalyticsSections,
    showSocialSection,
    showContentSection,
    showPaidSection,
    showAudienceSection,
    showMonitoringSection,
  ])

  const navigateToReportChartFromHash = useCallback(() => {
    const raw = window.location.hash?.replace(/^#/, '')
    if (!raw?.startsWith('widget-')) return
    const chartId = raw.slice('widget-'.length)
    const meta = CHART_LOOKUP[chartId]
    if (!meta) return
    flushSync(() => {
      if (meta.sectionId === 'goals') {
        setActiveTab('goals')
      } else {
        setActiveTab('analytics')
        const tab = meta.sectionId === 'social' ? 'all' : meta.sectionId
        if (VALID_ANALYTICS_TABS.includes(tab)) {
          setAnalyticsSectionTab(tab)
        }
      }
    })
    window.setTimeout(() => {
      const el =
        document.querySelector(`[data-chart-id="${chartId}"]`) ||
        document.getElementById(raw)
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    }, 450)
  }, [])

  useEffect(() => {
    navigateToReportChartFromHash()
    window.addEventListener('hashchange', navigateToReportChartFromHash)
    return () => window.removeEventListener('hashchange', navigateToReportChartFromHash)
  }, [navigateToReportChartFromHash])

  useEffect(() => {
    if (activeTab !== 'analytics') return
    const isCurrentTabVisible = visibleAnalyticsTabs.some((t) => t.id === analyticsSectionTab)
    if (!isCurrentTabVisible && visibleAnalyticsTabs.length > 0) {
      setAnalyticsSectionTab(visibleAnalyticsTabs[0].id)
    }
  }, [activeTab, analyticsSectionTab, visibleAnalyticsTabs])

  // Paid media widgets ship enabled; patch older saved dashboard prefs that omitted them.
  useEffect(() => {
    setSelectedCharts((prev) => {
      const next = ensurePaidMediaWidgetsVisible(prev, allChartIds)
      const unchanged = next.size === prev.size && [...next].every((id) => prev.has(id))
      if (unchanged) return prev
      saveDashboardPrefs(widgetOrder, next, analyticsSectionTab, { showSohoPulse })
      return next
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- one-time prefs migration

  const allGoalMetrics = useMemo(
    () => goalMetricGroups.flatMap((group) => group.metrics),
    [goalMetricGroups],
  )

  const goalMetricLookup = useMemo(() => {
    return allGoalMetrics.reduce((acc, metric) => {
      acc[metric.id] = metric
      return acc
    }, {})
  }, [allGoalMetrics])

  const clientGoalRowByMetricId = useMemo(() => {
    const map = {}
    clientMetricGoalsState.rows.forEach((row) => {
      const metricId = resolveClientGoalMetricId(row, goalMetricLookup)
      if (metricId) map[metricId] = row
    })
    return map
  }, [clientMetricGoalsState.rows, goalMetricLookup])

  const goalsPlatformOptions = useMemo(() => {
    const platforms = new Set()
    goalMetricGroups.forEach((group) => {
      group.metrics.forEach((m) => platforms.add(m.platform))
    })
    return [
      { value: 'all', label: 'All platforms' },
      ...Array.from(platforms)
        .filter((p) => p !== 'All Platforms')
        .sort((a, b) => a.localeCompare(b))
        .map((p) => ({ value: p, label: p })),
      { value: 'All Platforms', label: 'Cross-platform' },
    ]
  }, [goalMetricGroups])

  const filteredGoalMetricGroups = useMemo(() => {
    if (goalsPlatformFilter === 'all') return goalMetricGroups
    return goalMetricGroups
      .map((group) => ({
        ...group,
        metrics: group.metrics.filter((m) => m.platform === goalsPlatformFilter),
      }))
      .filter((group) => group.metrics.length > 0)
  }, [goalsPlatformFilter, goalMetricGroups])

  const maxCustomGoalMetrics = 5

  const customGoalsById = useMemo(() => {
    return customGoals.reduce((acc, goal) => {
      acc[goal.id] = goal
      return acc
    }, {})
  }, [customGoals])

  const resolvedGoalPresets = useMemo(() => {
    return resolveGoalPresets(metricGoalTypesState.types, metricGoalsState.goals)
  }, [metricGoalTypesState.types, metricGoalsState.goals])

  const selectedCustomGoal = selectedGoalsPreset ? customGoalsById[selectedGoalsPreset] : null

  const goalsConfiguratorMetricGroups = filteredGoalMetricGroups

  const handleMainTabClick = useCallback(
    (tabId) => {
      setActiveTab(tabId)
      if (tabId === 'goals') {
        dispatch(fetchMetricGoalTypes())
        dispatch(fetchMetricGoals())
        if (selectedClientId) {
          dispatch(fetchClientMetricGoals({ clientId: selectedClientId }))
        }
      }
    },
    [dispatch, selectedClientId],
  )

  const selectedGoalsPresetLabel = useMemo(() => {
    if (!selectedGoalsPreset) return 'Select a goal type'
    if (selectedCustomGoal) return `Custom: ${selectedCustomGoal.name}`
    return resolvedGoalPresets.find((preset) => preset.id === selectedGoalsPreset)?.name ?? 'Select a goal type'
  }, [selectedGoalsPreset, selectedCustomGoal, resolvedGoalPresets])

  const goalsPlatformFilterLabel = useMemo(() => {
    return goalsPlatformOptions.find((o) => o.value === goalsPlatformFilter)?.label ?? 'All platforms'
  }, [goalsPlatformFilter, goalsPlatformOptions])

  const goalsTimeRangeLabel = useMemo(() => {
    switch (goalsTimeRange) {
      case 'month':
        return 'Monthly'
      case 'quarter':
        return 'Quarterly'
      case 'year':
        return 'Annual'
      default:
        return 'Monthly'
    }
  }, [goalsTimeRange])

  const formatGoalValue = (value, metric) => {
    if (value == null) return '—'
    switch (metric.format) {
      case 'currency':
        return `$${formatCompactNumber(value)}`
      case 'percent':
        return `${value}%`
      case 'ratio':
        return `${Number(value).toFixed(1)}x`
      case 'time':
        return `${value} min`
      case 'timeSeconds':
        return `${value} sec`
      case 'stars':
        return `${Number(value).toFixed(1)} stars`
      default:
        return formatCompactNumber(value)
    }
  }

  const resetGoalsSetup = useCallback(() => {
    setSelectedGoalsPreset(null)
    setSelectedGoalMetrics(new Set())
    setCommittedGoalMetrics(new Set())
    setGoalsPersistPending(false)
    setGoalMetricTargets({})
    setGoalMetricPeriods({})
    setGoalMetricReach({})
    setGoalMetricPeriodStarts({})
    setGoalMetricPeriodValues({})
    setGoalPeriodEndedAcknowledged(new Set())
    setGoalPeriodEndedNoticeMetricId(null)
    setGoalExtendConfirmMetricId(null)
    setGoalsActivated(false)
    setShowGoalsCoachmarks(true)
    setShowGoalsProgress(false)
    setGoalsEditMode(true)
    setGoalsPreActivationDrawerOpen(false)
    setGoalsConfigExpanded(true)
    setGoalsShowSetup(false)
    setExpandedGoalGroups(new Set())
    setEditingMetricIds(new Set())
    setGoalsTypeOpen(false)
    setGoalsTimeOpen(false)
    setGoalsPlatformFilterOpen(false)
    setShowDeleteGoalConfirm(false)
    dispatch(clearClientYtdPagePerformance())
  }, [dispatch])

  const handleDeleteGoal = useCallback(() => {
    setShowDeleteGoalConfirm(true)
  }, [])

  const confirmDeleteGoal = useCallback(() => {
    if (selectedCustomGoal) {
      setCustomGoals((prev) => prev.filter((goal) => goal.id !== selectedCustomGoal.id))
    }
    resetGoalsSetup()
  }, [resetGoalsSetup, selectedCustomGoal])

  /** Abandon unsaved setup (preset or new custom). Removes draft custom goal from list if applicable. */
  const handleGoalsSetupCancel = useCallback(() => {
    if (selectedCustomGoal) {
      setCustomGoals((prev) => prev.filter((goal) => goal.id !== selectedCustomGoal.id))
    }
    resetGoalsSetup()
  }, [resetGoalsSetup, selectedCustomGoal])

  useEffect(() => {
    hydratedClientMetricGoalsClientIdRef.current = null
    hydratedClientMetricGoalsRowsKeyRef.current = null
    if (!selectedClientId) {
      dispatch(clearClientMetricGoals())
      goalsSyncedClientIdRef.current = null
      return
    }
    const clientKey = String(selectedClientId)
    if (goalsSyncedClientIdRef.current !== clientKey) {
      goalsSyncedClientIdRef.current = clientKey
      resetGoalsSetup()
    }
    dispatch(fetchMetricGoalTypes())
    dispatch(fetchMetricGoals())
    dispatch(fetchClientMetricGoals({ clientId: selectedClientId }))
  }, [selectedClientId, dispatch, resetGoalsSetup])

  const applyGoalsPreset = (presetId) => {
    const customGoal = customGoalsById[presetId]
    if (customGoal) {
      const draft = {
        metrics: new Set(),
        targets: {},
        periods: {},
        reach: {},
        periodStarts: {},
        periodValues: {},
      }
      const addMetricDraft = (metricId) => {
        const metric = goalMetricLookup[metricId]
        if (!metric) return
        const clientRow = clientGoalRowByMetricId[metricId]
        if (clientRow) {
          applyClientRowToMetricDraft(metricId, clientRow, metric, draft)
          if (clientRow.enabled !== false) draft.metrics.add(metricId)
          return
        }
        draft.metrics.add(metricId)
        draft.targets[metricId] = metric.defaultTarget
        draft.periods[metricId] = metric.defaultPeriod || 'monthly'
        draft.reach[metricId] = metric.source || 'Organic'
      }
      customGoal.metricIds.forEach((metricId) => addMetricDraft(metricId))
      setSelectedGoalsPreset(customGoal.id)
      setSelectedGoalMetrics(draft.metrics)
      setGoalMetricTargets(draft.targets)
      setGoalMetricPeriods(draft.periods)
      setGoalMetricReach(draft.reach)
      setGoalMetricPeriodStarts(draft.periodStarts)
      setGoalMetricPeriodValues(draft.periodValues)
      setGoalsTimeRange(customGoal.timeRange || goalsTimeRange)
      setGoalsShowSetup(true)
      setGoalsEditMode(false)
      setGoalsConfigExpanded(true)
      setGoalsActivated(false)
      setEditingMetricIds(new Set())
      return
    }
    const preset = resolvedGoalPresets.find((item) => item.id === presetId)
    if (!preset) return
    const typeId = Number(presetId)
    const draft = buildGoalTypeDraftFromClientApi(
      clientMetricGoalsState.rows,
      typeId,
      goalMetricLookup,
    )
    setSelectedGoalsPreset(presetId)
    setSelectedGoalMetrics(draft.metrics)
    setGoalMetricTargets(draft.targets)
    setGoalMetricPeriods(draft.periods)
    setGoalMetricReach(draft.reach)
    setGoalMetricPeriodStarts(draft.periodStarts)
    setGoalMetricPeriodValues(draft.periodValues)
    setGoalsShowSetup(true)
    setGoalsEditMode(false)
    setGoalsConfigExpanded(true)
    setGoalsActivated(false)
    setEditingMetricIds(new Set())
    setExpandedGoalGroups(new Set(goalMetricGroups.map((group) => group.id)))
    setTimeout(() => {
      goalsMetricListRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  useEffect(() => {
    if (clientMetricGoalsState.saveStatus === 'loading' || goalsPersistPending) return
    if (!selectedClientId) return
    if (metricGoalsState.status !== 'succeeded') return
    if (clientMetricGoalsState.status !== 'succeeded') return
    if (String(clientMetricGoalsState.fetchedClientId) !== String(selectedClientId)) return

    const rows = clientMetricGoalsState.rows || []
    const rowsKey = rows
      .map((row) => `${row.id}:${row.enabled}:${row.goal}:${row.metricId}:${row.metricGoalId}`)
      .join('|')
    const clientKey = String(selectedClientId)
    if (
      hydratedClientMetricGoalsClientIdRef.current === clientKey &&
      hydratedClientMetricGoalsRowsKeyRef.current === rowsKey
    ) {
      return
    }

    const ui = buildGoalsUiStateFromClientRows(rows, goalMetricLookup)
    hydratedClientMetricGoalsClientIdRef.current = clientKey
    hydratedClientMetricGoalsRowsKeyRef.current = rowsKey

    if (!ui || ui.metrics.size === 0) return

    setSelectedGoalMetrics(ui.metrics)
    setCommittedGoalMetrics(ui.metrics)
    setGoalMetricTargets(ui.targets)
    setGoalMetricPeriods(ui.periods)
    setGoalMetricReach(ui.reach)
    setGoalMetricPeriodStarts(ui.periodStarts)
    setGoalMetricPeriodValues(ui.periodValues)
    if (ui.goalTypeId != null) setSelectedGoalsPreset(ui.goalTypeId)
    setGoalsActivated(true)
    setGoalsEditMode(false)
    setShowGoalsCoachmarks(false)
    setShowGoalsProgress(true)
    setGoalsShowSetup(false)
    setGoalsConfigExpanded(false)
    setEditingMetricIds(new Set())
  }, [
    selectedClientId,
    metricGoalsState.status,
    clientMetricGoalsState.status,
    clientMetricGoalsState.saveStatus,
    clientMetricGoalsState.fetchedClientId,
    clientMetricGoalsState.rows,
    goalMetricLookup,
    goalsPersistPending,
  ])

  useEffect(() => {
    if (clientMetricGoalsState.saveStatus === 'loading' || goalsPersistPending) return
    if (goalsActivated || !selectedGoalsPreset || selectedCustomGoal) return
    if (clientMetricGoalsState.status !== 'succeeded') return
    const clientId = selectedClientId != null ? String(selectedClientId) : null
    if (!clientId || clientMetricGoalsState.fetchedClientId !== clientId) return

    const typeId = Number(selectedGoalsPreset)
    if (!Number.isFinite(typeId)) return

    const draft = buildGoalTypeDraftFromClientApi(
      clientMetricGoalsState.rows,
      typeId,
      goalMetricLookup,
    )
    setSelectedGoalMetrics(draft.metrics)
    setGoalMetricTargets((prev) => ({ ...prev, ...draft.targets }))
    setGoalMetricPeriods((prev) => ({ ...prev, ...draft.periods }))
    setGoalMetricReach((prev) => ({ ...prev, ...draft.reach }))
    setGoalMetricPeriodStarts((prev) => ({ ...prev, ...draft.periodStarts }))
    setGoalMetricPeriodValues((prev) => ({ ...prev, ...draft.periodValues }))
  }, [
    goalsActivated,
    selectedGoalsPreset,
    selectedCustomGoal,
    clientMetricGoalsState.status,
    clientMetricGoalsState.saveStatus,
    clientMetricGoalsState.fetchedClientId,
    clientMetricGoalsState.rows,
    selectedClientId,
    goalMetricLookup,
    goalsPersistPending,
  ])

  const openCustomGoalModal = () => {
    setCustomGoalNameDraft('')
    setCustomGoalIntentDraft('')
    setCustomGoalMetricsDraft(new Set())
    setCustomGoalTimeRangeDraft(goalsTimeRange)
    setCustomGoalExpandedGroups(new Set(goalMetricGroups.map((group) => group.id)))
    setCustomGoalProposal(null)
    setCustomGoalManualMode(false)
    setCustomGoalModalOpen(true)
  }

  const toggleCustomGoalMetric = (metricId) => {
    setCustomGoalMetricsDraft((prev) => {
      const next = new Set(prev)
      if (next.has(metricId)) {
        next.delete(metricId)
        return next
      }
      if (next.size >= maxCustomGoalMetrics) return next
      next.add(metricId)
      return next
    })
  }

  const toggleCustomGoalMetricGroup = (metricIds) => {
    setCustomGoalMetricsDraft((prev) => {
      const next = new Set(prev)
      const isGroupSelected = metricIds.every((metricId) => next.has(metricId))
      if (isGroupSelected) {
        metricIds.forEach((metricId) => next.delete(metricId))
        return next
      }
      metricIds.forEach((metricId) => {
        if (next.size >= maxCustomGoalMetrics) return
        next.add(metricId)
      })
      return next
    })
  }

  const handleCreateCustomGoal = (options = {}) => {
    const trimmedName = customGoalNameDraft.trim()
    const metricIds = options.metricIds ?? Array.from(customGoalMetricsDraft)
    const timeRange = options.timeRange ?? customGoalTimeRangeDraft
    const description = options.description ?? customGoalIntentDraft.trim()
    if (!trimmedName || metricIds.length === 0) return
    const newGoal = {
      id: `custom-${Date.now()}`,
      name: trimmedName,
      description,
      metricIds,
      timeRange,
    }
    const nextTargets = {}
    const nextPeriods = {}
    const nextReach = {}
    metricIds.forEach((metricId) => {
      const metric = goalMetricLookup[metricId]
      if (!metric) return
      nextTargets[metricId] = metric.defaultTarget
      nextPeriods[metricId] = metric.defaultPeriod || 'monthly'
      nextReach[metricId] = metric.source || 'Organic'
    })
    setCustomGoals((prev) => [...prev, newGoal])
    setSelectedGoalsPreset(newGoal.id)
    setSelectedGoalMetrics(new Set(metricIds))
    setGoalMetricTargets(nextTargets)
    setGoalMetricPeriods(nextPeriods)
    setGoalMetricReach(nextReach)
    setGoalsTimeRange(customGoalTimeRangeDraft)
    setCustomGoalModalOpen(false)
  }

  const toggleCustomGoalGroupExpanded = (groupId) => {
    setCustomGoalExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const buildCustomGoalProposal = (intent) => {
    const normalizedIntent = intent.toLowerCase()
    let presetId = 3
    let timeRange = goalsTimeRange

    if (normalizedIntent.includes('growth') || normalizedIntent.includes('follower')) {
      presetId = 1
    } else if (
      normalizedIntent.includes('engagement') ||
      normalizedIntent.includes('community') ||
      normalizedIntent.includes('comments')
    ) {
      presetId = 2
    }

    if (normalizedIntent.includes('monthly')) timeRange = 'month'
    if (normalizedIntent.includes('quarter')) timeRange = 'quarter'
    if (normalizedIntent.includes('annual') || normalizedIntent.includes('year')) timeRange = 'year'

    const preset = resolvedGoalPresets.find((item) => item.id === presetId)
    const metricIds = preset?.metricIds ?? []
    return {
      presetId,
      presetName: preset?.name ?? 'Balanced Approach',
      metricIds,
      timeRange,
    }
  }

  const handleGenerateCustomGoal = () => {
    const proposal = buildCustomGoalProposal(customGoalIntentDraft)
    setCustomGoalProposal(proposal)
  }

  const handleUseCustomGoalProposal = () => {
    if (!customGoalProposal) return
    handleCreateCustomGoal({
      metricIds: customGoalProposal.metricIds,
      timeRange: customGoalProposal.timeRange,
      description: customGoalIntentDraft.trim(),
    })
  }

  const handleEditCustomGoalManual = () => {
    if (!customGoalProposal) return
    const limitedMetricIds = customGoalProposal.metricIds.slice(0, maxCustomGoalMetrics)
    setCustomGoalMetricsDraft(new Set(limitedMetricIds))
    setCustomGoalTimeRangeDraft(customGoalProposal.timeRange)
    setCustomGoalManualMode(true)
  }

  const toggleGoalMetric = (metricId) => {
    setSelectedGoalMetrics((prev) => {
      const next = new Set(prev)
      const willSelect = !next.has(metricId)
      if (willSelect) {
        next.add(metricId)
      } else {
        next.delete(metricId)
        setEditingMetricIds((ids) => {
          const editingNext = new Set(ids)
          editingNext.delete(metricId)
          return editingNext
        })
      }
      setGoalMetricTargets((targetsPrev) => {
        const targetsNext = { ...targetsPrev }
        if (willSelect) {
          const metric = goalMetricLookup[metricId]
          const clientRow = clientGoalRowByMetricId[metricId]
          if (clientRow && metric) {
            const draft = {
              targets: targetsNext,
              periods: {},
              reach: {},
              periodStarts: {},
              periodValues: {},
            }
            applyClientRowToMetricDraft(metricId, clientRow, metric, draft)
            setGoalMetricPeriods((p) => ({ ...p, ...draft.periods }))
            setGoalMetricReach((r) => ({ ...r, ...draft.reach }))
            setGoalMetricPeriodStarts((s) => ({ ...s, ...draft.periodStarts }))
            setGoalMetricPeriodValues((v) => ({ ...v, ...draft.periodValues }))
          } else if (metric && targetsNext[metricId] == null) {
            targetsNext[metricId] = metric.defaultTarget
          }
        } else {
          delete targetsNext[metricId]
        }
        return targetsNext
      })
      setGoalMetricPeriods((periodsPrev) => {
        const periodsNext = { ...periodsPrev }
        if (willSelect) {
          const metric = goalMetricLookup[metricId]
          const clientRow = clientGoalRowByMetricId[metricId]
          if (!clientRow && metric && periodsNext[metricId] == null) {
            periodsNext[metricId] = metric.defaultPeriod || 'monthly'
          }
        } else {
          delete periodsNext[metricId]
        }
        return periodsNext
      })
      setGoalMetricReach((reachPrev) => {
        const reachNext = { ...reachPrev }
        if (willSelect) {
          const metric = goalMetricLookup[metricId]
          if (!clientGoalRowByMetricId[metricId] && metric && reachNext[metricId] == null) {
            reachNext[metricId] = metric.source || 'Organic'
          }
        } else {
          delete reachNext[metricId]
        }
        return reachNext
      })
      return next
    })
  }

  const toggleGoalMetricGroup = (metricIds) => {
    setSelectedGoalMetrics((prev) => {
      const next = new Set(prev)
      const selectedCount = metricIds.filter((metricId) => next.has(metricId)).length
      const shouldSelectAll = selectedCount !== metricIds.length
      metricIds.forEach((metricId) => {
        if (shouldSelectAll) {
          next.add(metricId)
        } else {
          next.delete(metricId)
        }
      })
      setGoalMetricTargets((targetsPrev) => {
        const targetsNext = { ...targetsPrev }
        metricIds.forEach((metricId) => {
          if (shouldSelectAll) {
            const metric = goalMetricLookup[metricId]
            if (metric && targetsNext[metricId] == null) {
              targetsNext[metricId] = metric.defaultTarget
            }
          } else {
            delete targetsNext[metricId]
          }
        })
        return targetsNext
      })
      setGoalMetricPeriods((periodsPrev) => {
        const periodsNext = { ...periodsPrev }
        metricIds.forEach((metricId) => {
          if (shouldSelectAll) {
            const metric = goalMetricLookup[metricId]
            if (metric && periodsNext[metricId] == null) {
              periodsNext[metricId] = metric.defaultPeriod || 'monthly'
            }
          } else {
            delete periodsNext[metricId]
          }
        })
        return periodsNext
      })
      setGoalMetricReach((reachPrev) => {
        const reachNext = { ...reachPrev }
        metricIds.forEach((metricId) => {
          if (shouldSelectAll) {
            const metric = goalMetricLookup[metricId]
            if (metric && reachNext[metricId] == null) {
              reachNext[metricId] = metric.source || 'Organic'
            }
          } else {
            delete reachNext[metricId]
          }
        })
        return reachNext
      })
      return next
    })
  }

  const handleGoalsChooseStrategy = useCallback(() => {
    setGoalsShowSetup(true)
    setTimeout(() => {
      goalsTypeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
      setGoalsTypeOpen(true)
    }, 100)
  }, [])

  const handleGoalsCreateCustom = useCallback(() => {
    setGoalsShowSetup(true)
    setTimeout(() => {
      goalsMetricListRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    }, 100)
  }, [])

  const toggleGoalMetricGroupExpanded = (groupId) => {
    setExpandedGoalGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  useEffect(() => {
    if (editingMetricIds.size === 0) return
    setEditingMetricIds((prev) => {
      const next = new Set(prev)
      let changed = false
      filteredGoalMetricGroups.forEach((group) => {
        if (!expandedGoalGroups.has(group.id)) {
          group.metrics.forEach((m) => {
            if (next.has(m.id)) {
              next.delete(m.id)
              changed = true
            }
          })
        }
      })
      return changed ? next : prev
    })
  }, [expandedGoalGroups, filteredGoalMetricGroups])

  const mapMetricIdsToGoalsList = useCallback((metricIdSet) => {
    const ytdPerformanceReady =
      clientYtdPagePerformanceState.status === 'succeeded' &&
      String(clientYtdPagePerformanceState.clientId ?? '') === String(selectedClientId ?? '')
    const list = Array.from(metricIdSet).map((metricId) => {
      const metric = goalMetricLookup[metricId]
      const clientRow = clientGoalRowByMetricId[metricId]
      const period = goalMetricPeriods[metricId] ?? metric?.defaultPeriod ?? 'monthly'
      const reach = goalMetricReach[metricId] ?? clientRow?.reach ?? metric?.source
      const perf = lookupGoalPerformanceFromYtdIndex(ytdPaidOrganicPerformanceIndex, metric, {
        reach,
        period,
        socialNetworkTypes,
      })
      const targetFromUi = goalMetricTargets[metricId] ?? metric?.defaultTarget ?? 0
      const targetFromClient =
        clientRow?.goal != null && !Number.isNaN(Number(clientRow.goal))
          ? Number(clientRow.goal)
          : null
      const targetFromApi = perf?.goal != null && !Number.isNaN(perf.goal) ? perf.goal : null
      const target = targetFromApi ?? targetFromClient ?? targetFromUi
      const currentFromApi = parseGoalMetricNumber(perf?.current)
      const current = currentFromApi ?? parseGoalMetricNumber(metric?.current) ?? 0
      const progressRatio =
        perf?.progressRatio != null
          ? perf.progressRatio
          : target > 0
            ? current / target
            : null
      const status = goalProgressStatusFromRatio(progressRatio)
      const goalProgressLabel =
        perf?.goalProgress != null && !Number.isNaN(Number(perf.goalProgress))
          ? `${Math.round(Number(perf.goalProgress) * 100)}%`
          : progressRatio != null
            ? `${Math.round(progressRatio * 100)}%`
            : '—'
      const periodStart = goalMetricPeriodStarts[metricId]
      const durationLabel = formatGoalPeriodAnchorLabel(period, periodStart)
      const isPeriodExpired =
        Boolean(periodStart) && isGoalMetricPeriodExpired(periodStart, period)
      const periodEndDate = periodStart ? getGoalMetricPeriodEndDate(periodStart, period) : null
      const periodEndLabel = periodEndDate
        ? periodEndDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : ''
      return {
        id: metricId,
        name: metric?.name ?? metricId,
        platform: metric?.platform,
        source: reach ?? metric?.source,
        format: metric?.format,
        period,
        periodStart,
        durationLabel,
        isPeriodExpired,
        periodEndLabel,
        periodEndedAcknowledged: goalPeriodEndedAcknowledged.has(metricId),
        target,
        current,
        latestValue: perf?.latestValue ?? null,
        status,
        progressRatio,
        goalProgressLabel,
        fromGoalPerformance: Boolean(perf),
      }
    })
    // Goal charts follow GET .../ytd_page — hide saved metrics absent from that payload.
    if (!ytdPerformanceReady) return list
    return list.filter((goal) => goal.fromGoalPerformance)
  }, [
    goalMetricLookup,
    goalMetricTargets,
    goalMetricReach,
    goalMetricPeriods,
    goalMetricPeriodStarts,
    goalPeriodEndedAcknowledged,
    clientGoalRowByMetricId,
    ytdPaidOrganicPerformanceIndex,
    clientYtdPagePerformanceState.status,
    clientYtdPagePerformanceState.clientId,
    selectedClientId,
    socialNetworkTypes,
  ])

  const selectedGoalsList = useMemo(
    () => mapMetricIdsToGoalsList(selectedGoalMetrics),
    [mapMetricIdsToGoalsList, selectedGoalMetrics],
  )

  const committedGoalsList = useMemo(
    () => mapMetricIdsToGoalsList(committedGoalMetrics),
    [mapMetricIdsToGoalsList, committedGoalMetrics],
  )

  const goalsStatusSummary = useMemo(() => {
    return committedGoalsList.reduce(
      (acc, goal) => {
        acc.total += 1
        acc[goal.status] += 1
        return acc
      },
      { total: 0, 'on-track': 0, behind: 0, 'needs-attention': 0, 'at-risk': 0, exceeded: 0 }
    )
  }, [committedGoalsList])

  const goalsStrategyStatus = useMemo(() => {
    const active = committedGoalsList.length
    if (active === 0) return 'no-active'
    const atRisk = goalsStatusSummary?.['at-risk'] ?? 0
    const behind = goalsStatusSummary?.behind ?? 0
    const needsAttention = goalsStatusSummary?.['needs-attention'] ?? 0
    const exceeded = goalsStatusSummary?.exceeded ?? 0
    const total = goalsStatusSummary?.total ?? 0
    if (exceeded >= total && total > 0) return 'achieved'
    if (atRisk >= 1) return 'at-risk'
    if (behind >= 1 || needsAttention >= 1) return 'needs-attention'
    return 'on-track'
  }, [committedGoalsList.length, goalsStatusSummary])

  const goalsTimeRemainingText = useMemo(() => {
    const now = new Date()
    const end = new Date(now)
    switch (goalsTimeRange) {
      case 'quarter': {
        const q = Math.floor(now.getMonth() / 3) + 1
        end.setMonth(q * 3 - 1)
        end.setDate(1)
        const lastDay = new Date(end.getFullYear(), end.getMonth() + 1, 0)
        end.setDate(lastDay.getDate())
        end.setHours(23, 59, 59, 999)
        break
      }
      case 'year':
        end.setMonth(11)
        end.setDate(31)
        end.setHours(23, 59, 59, 999)
        break
      case 'month':
      default:
        end.setMonth(now.getMonth() + 1, 0)
        end.setHours(23, 59, 59, 999)
        break
    }
    const msPerDay = 86400000
    const diffDays = Math.round((end - now) / msPerDay)
    if (diffDays < 0) return 'Overdue'
    if (diffDays === 0) return 'Ends today'
    if (diffDays <= 31) return `${diffDays} days left`
    const months = Math.floor(diffDays / 30)
    if (months === 1) return '1 month to goal'
    if (months < 12) return `${months} months to goal`
    const years = Math.floor(months / 12)
    return `${years} ${years === 1 ? 'year' : 'years'} to goal`
  }, [goalsTimeRange])

  const goalsCategoryCoverage = useMemo(() => {
    return goalsConfiguratorMetricGroups
      .filter((group) => group.metrics.some((m) => selectedGoalMetrics.has(m.id)))
      .map((g) => g.label)
  }, [goalsConfiguratorMetricGroups, selectedGoalMetrics])

  const goalsActivateDisabled = selectedGoalMetrics.size === 0
  const goalsSaving = clientMetricGoalsState.saveStatus === 'loading'
  const goalsPersistBusy = goalsSaving || goalsPersistPending
  const isGoalsReadOnly = goalsActivated && !goalsEditMode
  const isGoalsSetupPreview = !goalsActivated && Boolean(selectedGoalsPreset || goalsShowSetup)

  const hasActiveGoals = goalsActivated && committedGoalMetrics.size > 0
  // Keep the metric list mounted while modifying, even if every checkbox is cleared.
  const showGoalsConfigurator = goalsActivated && (goalsEditMode || selectedGoalMetrics.size > 0)

  const goalsYtdFetchKey = useMemo(() => {
    if (!selectedClientId) return null
    const start = formatYtdPerformanceDate(dateRange.start)
    const end = formatYtdPerformanceDate(dateRange.end)
    return buildYtdPagePerformanceFetchKey(
      selectedClientId,
      start,
      end,
      dashboardReportRangeMode(dateRangePreset),
    )
  }, [selectedClientId, dateRange.start, dateRange.end, dateRangePreset])

  const goalsCatalogApiPending = useMemo(() => {
    if (metricGoalsState.status === 'loading') return true
    if (metricGoalTypesState.status === 'loading') return true
    if (selectedClientId && clientMetricGoalsState.status === 'loading') return true
    return false
  }, [
    metricGoalsState.status,
    metricGoalTypesState.status,
    selectedClientId,
    clientMetricGoalsState.status,
  ])

  const savedGoalsUi = useMemo(() => {
    if (metricGoalsState.status !== 'succeeded') return null
    if (clientMetricGoalsState.status !== 'succeeded') return null
    if (String(clientMetricGoalsState.fetchedClientId ?? '') !== String(selectedClientId ?? '')) {
      return null
    }
    return buildGoalsUiStateFromClientRows(clientMetricGoalsState.rows, goalMetricLookup)
  }, [
    metricGoalsState.status,
    clientMetricGoalsState.status,
    clientMetricGoalsState.fetchedClientId,
    clientMetricGoalsState.rows,
    selectedClientId,
    goalMetricLookup,
  ])

  const goalsAwaitingHydration = Boolean(
    selectedClientId && (savedGoalsUi?.metrics.size ?? 0) > 0 && !goalsActivated,
  )

  const goalsYtdPerformancePending = useMemo(() => {
    if (!hasActiveGoals || !selectedClientId || !goalsYtdFetchKey) return false
    const { status, fetchedFetchKey } = clientYtdPagePerformanceState
    if (status === 'failed') return false
    if (status === 'succeeded' && fetchedFetchKey === goalsYtdFetchKey) return false
    return status === 'loading' || fetchedFetchKey !== goalsYtdFetchKey
  }, [
    hasActiveGoals,
    selectedClientId,
    goalsYtdFetchKey,
    clientYtdPagePerformanceState.status,
    clientYtdPagePerformanceState.fetchedFetchKey,
  ])

  const goalsTabDataLoading =
    goalsCatalogApiPending ||
    goalsYtdPerformancePending ||
    goalsAwaitingHydration ||
    goalsPersistPending ||
    goalsSaving
  const hasAnyConfiguredGoals = selectedGoalMetrics.size > 0

  const goalsConfigExpandedResolved = goalsConfigExpanded ?? !hasActiveGoals

  useEffect(() => {
    if (goalsConfigExpanded === null && hasActiveGoals) {
      setGoalsConfigExpanded(false)
      setGoalsEditMode(false)
    } else if (goalsConfigExpanded === null && !hasActiveGoals) {
      setGoalsConfigExpanded(true)
    }
  }, [hasActiveGoals, goalsConfigExpanded])

  useEffect(() => {
    if (hasActiveGoals && !prevHasActiveGoalsRef.current) {
      prevHasActiveGoalsRef.current = true
      setExpandedGoalGroups((prev) => {
        const next = new Set(prev)
        filteredGoalMetricGroups.forEach((group) => {
          if (group.metrics.some((m) => selectedGoalMetrics.has(m.id))) next.add(group.id)
        })
        return next
      })
    } else if (!hasActiveGoals) {
      prevHasActiveGoalsRef.current = false
    }
  }, [hasActiveGoals, filteredGoalMetricGroups, selectedGoalMetrics])

  const openGoalsConfigForEdit = useCallback(() => {
    setGoalsConfigExpanded(true)
    setGoalsEditMode(true)
  }, [])

  const handleGoalsCancel = useCallback(() => {
    setGoalsConfigExpanded(false)
    setGoalsEditMode(false)
    setEditingMetricIds(new Set())
  }, [])

  const stampGoalPeriodStarts = useCallback((metricIds, { onlyMissing = false } = {}) => {
    const today = toGoalPeriodStartKey()
    setGoalMetricPeriodStarts((prev) => {
      const next = { ...prev }
      metricIds.forEach((id) => {
        if (onlyMissing && next[id]) return
        next[id] = today
      })
      return next
    })
  }, [])

  const acknowledgeGoalPeriodEnded = useCallback((metricId) => {
    setGoalPeriodEndedAcknowledged((prev) => {
      const next = new Set(prev)
      next.add(metricId)
      return next
    })
    setGoalPeriodEndedNoticeMetricId(null)
  }, [])

  const requestExtendGoal = useCallback((metricId) => {
    setGoalExtendConfirmMetricId(metricId)
  }, [])

  const confirmExtendGoal = useCallback(() => {
    if (!goalExtendConfirmMetricId) return
    const metricId = goalExtendConfirmMetricId
    const today = toGoalPeriodStartKey()
    setGoalMetricPeriodStarts((prev) => ({ ...prev, [metricId]: today }))
    setGoalPeriodEndedAcknowledged((prev) => {
      const next = new Set(prev)
      next.delete(metricId)
      return next
    })
    setGoalExtendConfirmMetricId(null)
  }, [goalExtendConfirmMetricId])

  useEffect(() => {
    if (!hasActiveGoals) return
    const missingStarts = Array.from(selectedGoalMetrics).filter((id) => !goalMetricPeriodStarts[id])
    if (missingStarts.length > 0) {
      stampGoalPeriodStarts(missingStarts, { onlyMissing: true })
    }
  }, [hasActiveGoals, selectedGoalMetrics, goalMetricPeriodStarts, stampGoalPeriodStarts])

  useEffect(() => {
    if (!hasActiveGoals) return
    if (goalPeriodEndedNoticeMetricId) return
    const expiredUnacknowledged = selectedGoalsList.find(
      (goal) => goal.isPeriodExpired && !goal.periodEndedAcknowledged,
    )
    if (expiredUnacknowledged) {
      setGoalPeriodEndedNoticeMetricId(expiredUnacknowledged.id)
    }
  }, [hasActiveGoals, selectedGoalsList, goalPeriodEndedNoticeMetricId])

  const goalPeriodEndedNoticeGoal = useMemo(() => {
    if (!goalPeriodEndedNoticeMetricId) return null
    return selectedGoalsList.find((goal) => goal.id === goalPeriodEndedNoticeMetricId) ?? null
  }, [goalPeriodEndedNoticeMetricId, selectedGoalsList])

  const goalExtendConfirmGoal = useMemo(() => {
    if (!goalExtendConfirmMetricId) return null
    return selectedGoalsList.find((goal) => goal.id === goalExtendConfirmMetricId) ?? null
  }, [goalExtendConfirmMetricId, selectedGoalsList])

  const handleMetricGoalDone = useCallback(
    (metricId) => {
      const metric = goalMetricLookup[metricId]
      const targetRaw = goalMetricTargets[metricId]
      if (
        targetRaw === '' ||
        targetRaw == null ||
        (typeof targetRaw === 'number' && Number.isNaN(targetRaw))
      ) {
        window.alert('Please enter a target value before saving.')
        return false
      }

      const today = toGoalPeriodStartKey()
      const periodStart = goalMetricPeriodStarts[metricId] || today
      const period = goalMetricPeriods[metricId] ?? metric?.defaultPeriod ?? 'monthly'
      const pv = periodValueForApi(period, periodStart, goalMetricPeriodValues[metricId])
      if (!goalMetricPeriodStarts[metricId]) {
        setGoalMetricPeriodStarts((prev) => ({ ...prev, [metricId]: today }))
      }
      if (pv) {
        setGoalMetricPeriodValues((prev) => ({ ...prev, [metricId]: pv }))
      }

      setSelectedGoalMetrics((prev) => new Set(prev).add(metricId))
      delete goalMetricEditSnapshotRef.current[metricId]
      setEditingMetricIds((prev) => {
        const next = new Set(prev)
        next.delete(metricId)
        return next
      })
      return true
    },
    [
      goalMetricLookup,
      goalMetricPeriodStarts,
      goalMetricPeriodValues,
      goalMetricPeriods,
      goalMetricTargets,
    ],
  )

  const handleMetricGoalEditStart = useCallback(
    (metricId) => {
      goalMetricEditSnapshotRef.current[metricId] = {
        selected: selectedGoalMetrics.has(metricId),
        target: goalMetricTargets[metricId],
        period: goalMetricPeriods[metricId],
        reach: goalMetricReach[metricId],
        periodStart: goalMetricPeriodStarts[metricId],
        periodValue: goalMetricPeriodValues[metricId],
      }
      setEditingMetricIds((prev) => new Set(prev).add(metricId))
    },
    [
      selectedGoalMetrics,
      goalMetricTargets,
      goalMetricPeriods,
      goalMetricReach,
      goalMetricPeriodStarts,
      goalMetricPeriodValues,
    ],
  )

  const handleMetricGoalCancel = useCallback(
    (metricId) => {
      const snap = goalMetricEditSnapshotRef.current[metricId]
      delete goalMetricEditSnapshotRef.current[metricId]
      const existing = clientGoalRowByMetricId[metricId]

      if (existing && snap) {
        setGoalMetricTargets((prev) => {
          const next = { ...prev }
          if (snap.target !== undefined) next[metricId] = snap.target
          else delete next[metricId]
          return next
        })
        setGoalMetricPeriods((prev) => {
          const next = { ...prev }
          if (snap.period !== undefined) next[metricId] = snap.period
          else delete next[metricId]
          return next
        })
        setGoalMetricReach((prev) => {
          const next = { ...prev }
          if (snap.reach !== undefined) next[metricId] = snap.reach
          else delete next[metricId]
          return next
        })
        setGoalMetricPeriodStarts((prev) => {
          const next = { ...prev }
          if (snap.periodStart !== undefined) next[metricId] = snap.periodStart
          else delete next[metricId]
          return next
        })
        setGoalMetricPeriodValues((prev) => {
          const next = { ...prev }
          if (snap.periodValue !== undefined) next[metricId] = snap.periodValue
          else delete next[metricId]
          return next
        })
      } else {
        setSelectedGoalMetrics((prev) => {
          const next = new Set(prev)
          next.delete(metricId)
          return next
        })
        setGoalMetricTargets((prev) => {
          const next = { ...prev }
          delete next[metricId]
          return next
        })
        setGoalMetricPeriods((prev) => {
          const next = { ...prev }
          delete next[metricId]
          return next
        })
        setGoalMetricReach((prev) => {
          const next = { ...prev }
          delete next[metricId]
          return next
        })
        setGoalMetricPeriodStarts((prev) => {
          const next = { ...prev }
          delete next[metricId]
          return next
        })
        setGoalMetricPeriodValues((prev) => {
          const next = { ...prev }
          delete next[metricId]
          return next
        })
      }

      setEditingMetricIds((prev) => {
        const next = new Set(prev)
        next.delete(metricId)
        return next
      })
    },
    [clientGoalRowByMetricId],
  )

  const handleGoalsSave = useCallback(async (options = {}) => {
    const fromActivation = options?.fromActivation === true
    const metricIds = Array.from(selectedGoalMetrics)
    setGoalsPersistPending(true)

    if (!fromActivation && metricIds.length === 0) {
      try {
        if (selectedClientId) {
          await dispatch(
            saveClientMetricGoals({
              clientId: selectedClientId,
              selectedMetricIds: [],
              goalMetricTargets,
              goalMetricPeriods,
              goalMetricReach,
              goalMetricPeriodStarts: goalMetricPeriodStarts,
              goalMetricPeriodValues: goalMetricPeriodValues,
              goalMetricLookup,
              existingRows: clientMetricGoalsState.rows,
            }),
          ).unwrap()
        }
        hydratedClientMetricGoalsRowsKeyRef.current = null
        resetGoalsSetup()
        setToastMessage('Goals cleared')
        setTimeout(() => setToastMessage(''), 2000)
      } catch (err) {
        setGoalsPersistPending(false)
        const message = typeof err === 'string' ? err : err?.message || 'Failed to save goals'
        window.alert(message)
      }
      return
    }

    const today = toGoalPeriodStartKey()
    const periodStartsForSave = { ...goalMetricPeriodStarts }
    metricIds.forEach((id) => {
      if (!periodStartsForSave[id]) periodStartsForSave[id] = today
    })
    setGoalMetricPeriodStarts(periodStartsForSave)

    const periodValuesForSave = { ...goalMetricPeriodValues }
    metricIds.forEach((metricId) => {
      const metric = goalMetricLookup[metricId]
      const period = goalMetricPeriods[metricId] ?? metric?.defaultPeriod ?? 'monthly'
      const pv = periodValueForApi(
        period,
        periodStartsForSave[metricId],
        periodValuesForSave[metricId],
      )
      if (pv) periodValuesForSave[metricId] = pv
    })
    setGoalMetricPeriodValues(periodValuesForSave)

    const finishLocalSave = () => {
      setCommittedGoalMetrics(new Set(metricIds))
      setGoalsActivated(true)
      setGoalsEditMode(false)
      setShowGoalsCoachmarks(false)
      setShowGoalsProgress(true)
      setGoalsConfigExpanded(false)
      setEditingMetricIds(new Set())
    }

    if (!selectedClientId) {
      finishLocalSave()
      setGoalsPersistPending(false)
      return
    }

    try {
      const result = await dispatch(
        saveClientMetricGoals({
          clientId: selectedClientId,
          selectedMetricIds: metricIds,
          goalMetricTargets,
          goalMetricPeriods,
          goalMetricReach,
          goalMetricPeriodStarts: periodStartsForSave,
          goalMetricPeriodValues: periodValuesForSave,
          goalMetricLookup,
          existingRows: clientMetricGoalsState.rows,
        }),
      ).unwrap()
      if (result?.wrote !== false) {
        hydratedClientMetricGoalsRowsKeyRef.current = null
        await dispatch(
          fetchClientYtdPagePerformance({
            clientId: selectedClientId,
            startDate: dateRange.start,
            endDate: dateRange.end,
            rangeMode: dashboardReportRangeMode(dateRangePreset),
            force: true,
          }),
        ).unwrap()
      }
      finishLocalSave()
      if (fromActivation) {
        setGoalsPreActivationDrawerOpen(false)
        setGoalsShowSetup(false)
        setExpandedGoalGroups((prev) => {
          const next = new Set(prev)
          filteredGoalMetricGroups.forEach((group) => {
            const hasSelected = group.metrics.some((m) => selectedGoalMetrics.has(m.id))
            if (hasSelected) next.add(group.id)
          })
          return next
        })
      }
      setGoalsPersistPending(false)
      setToastMessage('Goals saved')
      setTimeout(() => setToastMessage(''), 2000)
    } catch (err) {
      setGoalsPersistPending(false)
      const message = typeof err === 'string' ? err : err?.message || 'Failed to save goals'
      window.alert(message)
    }
  }, [
    selectedGoalMetrics,
    goalMetricPeriodStarts,
    goalMetricPeriodValues,
    goalMetricPeriods,
    goalMetricTargets,
    goalMetricReach,
    goalMetricLookup,
    selectedClientId,
    clientMetricGoalsState.rows,
    dispatch,
    resetGoalsSetup,
    filteredGoalMetricGroups,
    dateRange.start,
    dateRange.end,
  ])

  const openConfigure = () => {
    setDraftSelectedCharts(new Set(selectedCharts))
    setDraftWidgetOrder([...widgetOrder])
    setDraftCampaignChannel(campaignChannel)
    setDraftShowSohoPulse(showSohoPulse)
    setConfigureSectionExpanded(chartVisibilitySections.reduce((acc, s) => ({ ...acc, [s.id]: true }), {}))
    setIsConfigureOpen(true)
  }

  const toggleConfigureSectionExpanded = (sectionId) => {
    setConfigureSectionExpanded((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }))
  }

  const handlePaidExportSortStateChange = useCallback((state) => {
    paidExportSortRef.current = state
  }, [])

  const openExport = () => {
    const visibleExportCharts = exportableChartIds.filter((id) => selectedCharts.has(id))
    setDraftExportCharts(
      new Set(visibleExportCharts.length > 0 ? visibleExportCharts : exportableChartIds),
    )
    setDraftExportInsights(new Set(insightsSections.map((section) => section.id)))
    setExportFormat('pdf')
    setExportName('')
    setExportSourceTemplateId(null)
    setReportDataScope(REPORT_DATA_SCOPE.CURRENT_VIEW)
    setWidgetDataScopes(createDefaultWidgetDataScopes(allChartIds, REPORT_DATA_SCOPE.CURRENT_VIEW))
    setExportSchedule(loadReportSchedule())
    setExportCustomizeMode(false)
    setExportSectionCollapsed(true)
    setIsExportOpen(true)
  }

  useImperativeHandle(ref, () => ({
    openConfigure,
    openExport,
  }))

  const openCustomizeExport = () => {
    setExportCustomizeMode(true)
    setExportSectionCollapsed(true)
  }

  const openScheduleModal = () => {
    const saved = loadReportSchedule()
    if (saved && typeof saved === 'object') {
      let format = saved.format || 'pdf'
      if (format === 'xls') format = 'csv'
      setScheduleDraft({
        enabled: Boolean(saved.enabled),
        frequency: saved.frequency || 'weekly',
        dayOfWeek: typeof saved.dayOfWeek === 'number' ? saved.dayOfWeek : 1,
        dayOfMonth: typeof saved.dayOfMonth === 'number' ? saved.dayOfMonth : 1,
        time: saved.time || '09:00',
        timezone: saved.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        recipients: Array.isArray(saved.recipients) ? [...saved.recipients] : [],
        recipientInput: '',
        format,
      })
    }
    setIsScheduleOpen(true)
  }

  const saveSchedule = () => {
    const { enabled, frequency, dayOfWeek, dayOfMonth, time, timezone, recipients, format } = scheduleDraft
    if (enabled && (!recipients || recipients.length === 0)) return
    const schedule = {
      enabled: Boolean(enabled),
      frequency: frequency || 'weekly',
      dayOfWeek: frequency === 'weekly' ? dayOfWeek : undefined,
      dayOfMonth: frequency === 'monthly' ? dayOfMonth : undefined,
      time: time || '09:00',
      timezone: timezone || 'UTC',
      recipients: Array.isArray(recipients) ? recipients.filter(isValidEmail) : [],
      format: format || 'pdf',
    }
    setExportSchedule(schedule)
    saveReportSchedule(schedule)
    setIsScheduleOpen(false)
  }

  const addScheduleRecipient = () => {
    const email = scheduleDraft.recipientInput?.trim()
    if (email && isValidEmail(email) && !scheduleDraft.recipients.includes(email)) {
      setScheduleDraft((prev) => ({
        ...prev,
        recipients: [...(prev.recipients || []), email],
        recipientInput: '',
      }))
    }
  }

  const removeScheduleRecipient = (email) => {
    setScheduleDraft((prev) => ({
      ...prev,
      recipients: (prev.recipients || []).filter((e) => e !== email),
    }))
  }

  const applyExportTemplate = (templateId) => {
    if (!templateId || templateId === 'blank') {
      setDraftExportCharts(new Set())
      setExportSourceTemplateId('blank')
      setExportName('')
      setExportTemplateOpen(false)
      return
    }
    const templates = reportTemplates ?? []
    const template = Array.isArray(templates) ? templates.find((t) => t && t.id === templateId) : null
    if (!template?.widgets) {
      setExportTemplateOpen(false)
      return
    }
    const knownIds = new Set(allChartIds)
    const validWidgets = []
    for (const widgetId of template.widgets) {
      if (knownIds.has(widgetId)) {
        validWidgets.push(widgetId)
      } else if (typeof console !== 'undefined' && console.warn) {
        console.warn(`[Report] Widget ID not found, skipping: ${widgetId}`)
      }
    }
    setDraftExportCharts(new Set(validWidgets))
    setExportSourceTemplateId(templateId)
    const rangeLabel = dateRange?.start && dateRange?.end
      ? `${new Date(dateRange.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(dateRange.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : ''
    setExportName(rangeLabel ? `${template.name} (${rangeLabel})` : template.name)
    setExportTemplateOpen(false)
  }

  const closeConfigure = () => {
    setIsConfigureOpen(false)
    setConfigurePlatformFilterOpen(false)
  }

  const closeExport = () => {
    setIsExportOpen(false)
    setExportCustomizeMode(false)
  }

  const closeInsights = () => {
    setIsInsightsOpen(false)
    setInsightsTab('insights')
    setInsightsSeverityFilter('all')
  }

  const handleInsightCta = (cta, cardId) => {
    if (cardId) {
      setInsightReviewedIds((prev) => new Set([...prev, cardId]))
    }
    if (cta === 'Go to Inbox') {
      closeInsights()
      navigate('/ai-community-inbox')
    } else if (cta === 'View Goals' || cta === 'Add to Goals') {
      closeInsights()
      setActiveTab('goals')
    } else if (cta === 'Add to Report') {
      addInsightToReport('insights')
      setToastMessage('Added to report')
      setTimeout(() => setToastMessage(''), 2000)
    } else {
      closeInsights()
      setActiveTab('analytics')
    }
  }

  const applyPlatformPulse = (platforms) => {
    const next = platforms instanceof Set ? new Set(platforms) : new Set(Array.isArray(platforms) ? platforms : [])
    setPlatformPulsePlatforms(next)
    setMessageChannelFilter('all')
    setSentimentFilter('all')
    if (next.size === 0 || next.has('all')) {
      setPaidMediaPlatform('all')
      setTopAdsPlatforms(new Set(['all']))
      setTopPostsPlatform('all')
      setCompetitorPlatform('all')
      setAudienceDemographicPlatform('all')
    } else if (next.size === 1) {
      const only = [...next][0]
      setPaidMediaPlatform(only)
      setTopAdsPlatforms(new Set([only]))
      setTopPostsPlatform(only)
      setCompetitorPlatform(only)
      setAudienceDemographicPlatform(only)
    } else {
      setPaidMediaPlatform('all')
      setTopAdsPlatforms(new Set(next))
      setTopPostsPlatform('all')
      setCompetitorPlatform('all')
      setAudienceDemographicPlatform('all')
    }
  }

  const applyConfigure = () => {
    setSelectedCharts(new Set(draftSelectedCharts))
    setWidgetOrder([...draftWidgetOrder])
    setShowSohoPulse(draftShowSohoPulse)
    const platform = draftCampaignChannel || 'all'
    applyPlatformPulse(platform === 'all' ? new Set() : new Set([platform]))
    saveDashboardPrefs(draftWidgetOrder, draftSelectedCharts, analyticsSectionTab, {
      showSohoPulse: draftShowSohoPulse,
    })
    setIsConfigureOpen(false)
  }

  const sectionIds = useMemo(() => chartVisibilitySections.map((s) => s.id), [])

  const handleConfigureDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const isSectionDrag = sectionIds.includes(active.id)

    setDraftWidgetOrder((prev) => {
      if (isSectionDrag) {
        const section = chartVisibilitySections.find((s) => s.id === active.id)
        if (!section) return prev
        const sectionChartIds = section.charts.map((c) => c.id)
        const chartsToMove = prev.filter((id) => sectionChartIds.includes(id))
        const rest = prev.filter((id) => !sectionChartIds.includes(id))

        let targetIndex = 0
        const overIsSection = sectionIds.includes(over.id)
        if (overIsSection) {
          const overSection = chartVisibilitySections.find((s) => s.id === over.id)
          const overChartIds = overSection?.charts.map((c) => c.id) ?? []
          const firstOverChart = rest.find((id) => overChartIds.includes(id))
          targetIndex = firstOverChart ? rest.indexOf(firstOverChart) : rest.length
        } else {
          targetIndex = rest.indexOf(over.id)
          if (targetIndex === -1) targetIndex = rest.length
        }

        return [...rest.slice(0, targetIndex), ...chartsToMove, ...rest.slice(targetIndex)]
      }

      const oldIndex = prev.indexOf(active.id)
      if (oldIndex === -1) return prev

      let newIndex = prev.indexOf(over.id)
      if (newIndex === -1 && sectionIds.includes(over.id)) {
        const overSection = chartVisibilitySections.find((s) => s.id === over.id)
        const overChartIds = overSection?.charts.map((c) => c.id) ?? []
        const firstOverChart = prev.find((id) => overChartIds.includes(id))
        newIndex = firstOverChart ? prev.indexOf(firstOverChart) : prev.length
      }
      if (newIndex === -1) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const resetConfigureToDefault = () => {
    setDraftSelectedCharts(new Set(allChartIds))
    setDraftWidgetOrder([...DEFAULT_WIDGET_ORDER])
    setDraftShowSohoPulse(true)
    setConfigureSectionExpanded(chartVisibilitySections.reduce((acc, s) => ({ ...acc, [s.id]: true }), {}))
    setShowResetConfirm(false)
  }

  const toggleChartSelection = (chartId) => {
    setDraftSelectedCharts((prev) => {
      const next = new Set(prev)
      if (next.has(chartId)) {
        next.delete(chartId)
      } else {
        next.add(chartId)
      }
      return next
    })
  }

  const toggleSectionSelection = (section) => {
    setDraftSelectedCharts((prev) => {
      const next = new Set(prev)
      const sectionChartIds = section.charts.map((chart) => chart.id)
      const selectedCount = sectionChartIds.filter((chartId) => next.has(chartId)).length
      const shouldSelectAll = selectedCount !== sectionChartIds.length
      sectionChartIds.forEach((chartId) => {
        if (shouldSelectAll) {
          next.add(chartId)
        } else {
          next.delete(chartId)
        }
      })
      return next
    })
  }

  const toggleAllCharts = () => {
    setDraftSelectedCharts((prev) => {
      if (prev.size === allChartIds.length) {
        return new Set()
      }
      return new Set(allChartIds)
    })
  }

  const toggleExportChartSelection = (chartId) => {
    const willSelect = !draftExportCharts.has(chartId)
    setDraftExportCharts((prev) => {
      const next = new Set(prev)
      if (willSelect) {
        next.add(chartId)
      } else {
        next.delete(chartId)
      }
      return next
    })
    if (willSelect) {
      setWidgetDataScope(chartId, reportDataScope)
    }
  }

  const toggleExportSectionSelection = (section) => {
    setDraftExportCharts((prev) => {
      const next = new Set(prev)
      const sectionChartIds = section.charts.map((chart) => chart.id)
      const selectedCount = sectionChartIds.filter((chartId) => next.has(chartId)).length
      const shouldSelectAll = selectedCount !== sectionChartIds.length
      sectionChartIds.forEach((chartId) => {
        if (shouldSelectAll) {
          next.add(chartId)
        } else {
          next.delete(chartId)
        }
      })
      return next
    })
  }

  const toggleExportAllCharts = () => {
    setDraftExportCharts((prev) => {
      if (prev.size === exportableChartIds.length) {
        return new Set()
      }
      return new Set(exportableChartIds)
    })
  }

  const toggleExportInsightSelection = (insightId) => {
    setDraftExportInsights((prev) => {
      const next = new Set(prev)
      if (next.has(insightId)) {
        next.delete(insightId)
      } else {
        next.add(insightId)
      }
      return next
    })
  }

  const addInsightToReport = (insightId) => {
    setSelectedInsights((prev) => {
      const next = new Set(prev)
      next.add(insightId)
      return next
    })
    setToastMessage('Added to report')
    setTimeout(() => setToastMessage(''), 2000)
  }

  const addAllInsightsToReport = () => {
    setSelectedInsights(new Set(insightsSections.map((section) => section.id)))
    setToastMessage('Added to report')
    setTimeout(() => setToastMessage(''), 2000)
  }

  const areSetsEqual = (left, right) => {
    if (left.size !== right.size) return false
    for (const value of left) {
      if (!right.has(value)) return false
    }
    return true
  }

  const arraysEqual = (a, b) => {
    if (!a || !b || a.length !== b.length) return false
    return a.every((v, i) => v === b[i])
  }

  const configureDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const hasDraftChanges =
    !areSetsEqual(draftSelectedCharts, selectedCharts) ||
    !arraysEqual(draftWidgetOrder, widgetOrder) ||
    draftCampaignChannel !== campaignChannel ||
    draftShowSohoPulse !== showSohoPulse
  const hasConfigChanges =
    selectedCharts.size !== allChartIds.length || platformPulsePlatforms.size > 0

  useEffect(() => {
    onHasConfigChanges?.(hasConfigChanges)
  }, [hasConfigChanges, onHasConfigChanges])

  const canHover = () =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(hover: hover)').matches

  const clearKpiHoverTimeout = () => {
    if (kpiHoverTimeoutRef.current) {
      clearTimeout(kpiHoverTimeoutRef.current)
      kpiHoverTimeoutRef.current = null
    }
  }

  const PULSE_CARD_IDS = ['inbox', 'engagement', 'campaign', 'growth']
  const handleKpiEnter = (cardId, event) => {
    if (PULSE_CARD_IDS.includes(cardId)) return
    if (!canHover()) return
    clearKpiHoverTimeout()
    
    const cardElement = event.currentTarget
    kpiCardRefs.current[cardId] = cardElement
    const rect = cardElement.getBoundingClientRect()
    
    setHoveredKpiId(cardId)
    setHoveredKpiRect(rect)

    // When hovering a partially visible card, scroll to bring it fully into view
    const scrollEl = liveNowCardsScrollRef.current
    if (scrollEl) {
      const scrollRect = scrollEl.getBoundingClientRect()
      const isPartiallyVisible =
        rect.left < scrollRect.left || rect.right > scrollRect.right
      if (isPartiallyVisible) {
        const padding = 8
        let targetScroll = scrollEl.scrollLeft
        if (rect.left < scrollRect.left) {
          targetScroll = scrollEl.scrollLeft + (rect.left - scrollRect.left) - padding
        } else if (rect.right > scrollRect.right) {
          targetScroll = scrollEl.scrollLeft + (rect.right - scrollRect.right) + padding
        }
        targetScroll = Math.max(0, Math.min(scrollEl.scrollWidth - scrollEl.clientWidth, targetScroll))

        const start = scrollEl.scrollLeft
        const distance = targetScroll - start
        const duration = 380
        const startTime = performance.now()
        const easeOutQuart = (t) => 1 - (1 - t) ** 4

        const tick = (now) => {
          const elapsed = now - startTime
          const progress = Math.min(elapsed / duration, 1)
          scrollEl.scrollLeft = start + distance * easeOutQuart(progress)
          if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      }
    }
  }

  const updateCardRect = (cardId) => {
    const cardElement = kpiCardRefs.current[cardId]
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect()
      setHoveredKpiRect(rect)
    }
  }

  const handleKpiLeave = () => {
    if (!canHover()) return
    clearKpiHoverTimeout()
    kpiHoverTimeoutRef.current = setTimeout(() => {
      setHoveredKpiId(null)
      setHoveredKpiRect(null)
    }, 150)
  }

  const handleOverlayEnter = () => {
    clearKpiHoverTimeout()
  }

  const handleOverlayLeave = () => {
    if (!canHover()) return
    clearKpiHoverTimeout()
    kpiHoverTimeoutRef.current = setTimeout(() => {
      setHoveredKpiId(null)
      setHoveredKpiRect(null)
    }, 150)
  }

  const toggleKpiPopover = useCallback((cardId) => {
    setOpenKpiCardState((prev) => {
      if (prev.id === cardId) return { id: null, rect: null }
      const el = kpiCardRefs.current[cardId]
      const rect = el?.getBoundingClientRect() ?? null
      return { id: cardId, rect }
    })
  }, [])

  const closeKpiPopover = useCallback(() => {
    setOpenKpiCardState({ id: null, rect: null })
  }, [])

  useEffect(() => {
    if (!openKpiCardId) return
    const handleOutsideClick = (e) => {
      const target = e.target
      const cardEl = kpiCardRefs.current[openKpiCardId]
      if (cardEl?.contains(target) || kpiPopoverRef.current?.contains(target)) return
      closeKpiPopover()
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [openKpiCardId, closeKpiPopover])

  useEffect(() => {
    if (!openKpiCardId) return
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeKpiPopover()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [openKpiCardId, closeKpiPopover])

  useEffect(() => {
    if (!openKpiCardId) return

    const handleScroll = () => {
      closeKpiPopover()
    }

    const syncOpenRect = () => {
      const el = kpiCardRefs.current[openKpiCardId]
      if (!el) {
        closeKpiPopover()
        return
      }
      const rect = el.getBoundingClientRect()
      setOpenKpiCardState((prev) => (prev.id === openKpiCardId ? { id: prev.id, rect } : prev))
    }

    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', syncOpenRect)
    window.visualViewport?.addEventListener('resize', syncOpenRect)
    const dashboardScrollEl = dashboardContentRef.current
    const pulseScrollEl = liveNowCardsScrollRef.current
    dashboardScrollEl?.addEventListener('scroll', handleScroll, { passive: true })
    pulseScrollEl?.addEventListener('scroll', handleScroll, { passive: true })

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncOpenRect) : null
    const cardEl = kpiCardRefs.current[openKpiCardId]
    if (cardEl) resizeObserver?.observe(cardEl)
    if (pulseScrollEl) resizeObserver?.observe(pulseScrollEl)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', syncOpenRect)
      window.visualViewport?.removeEventListener('resize', syncOpenRect)
      dashboardScrollEl?.removeEventListener('scroll', handleScroll)
      pulseScrollEl?.removeEventListener('scroll', handleScroll)
      resizeObserver?.disconnect()
    }
  }, [openKpiCardId, closeKpiPopover])

  const mainTabs = [
    {
      id: 'analytics',
      label: 'Analytics',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
    },
    {
      id: 'goals',
      label: 'Goals',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      ),
    },
  ]

  const formatEngagementRate = (value) => `${(value * 100).toFixed(1)}%`
  const formatShortDate = (value) => {
    const date = parseLocalDateInput(value)
    if (!date) return ''
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  const formatPostDate = useCallback(
    (value) => formatTopPostDisplayDate(value, propertyChartTimezone),
    [propertyChartTimezone],
  )
  const toDateInputValue = (date) => formatLocalDateKey(date)

  const formatRangeLabel = (range) => {
    if (!range?.start || !range?.end) return ''
    return `${formatShortDate(range.start)} - ${formatShortDate(range.end)}`
  }

  const previousPeriod = useMemo(() => getPreviousPeriod(dateRange), [dateRange])
  const previousPeriodTooltip = useMemo(
    () => (previousPeriod ? `Compared to: ${formatPreviousPeriodTooltip(previousPeriod)}` : ''),
    [previousPeriod]
  )

  const formatTimestamp = (date) =>
    date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })

  const getDefaultReportName = () => {
    const rangeLabel = formatRangeLabel(dateRange)
    return rangeLabel ? `Dashboard Report (${rangeLabel})` : 'Dashboard Report'
  }

  const sanitizeFileName = (name) =>
    name
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120)

  const buildExportSections = (chartSet) =>
    chartVisibilitySections
      .filter((section) => !REPORT_EXPORT_EXCLUDED_SECTION_IDS.has(section.id))
      .map((section) => ({
        ...section,
        charts: section.charts.filter(
          (chart) => chartSet.has(chart.id) && !REPORT_EXPORT_EXCLUDED_CHART_IDS.has(chart.id),
        ),
      }))

  const setWidgetDataScope = (chartId, scope) => {
    setWidgetDataScopes((prev) => ({
      ...prev,
      [chartId]: scope,
    }))
  }

  const handleReportDataScopeChange = (scope) => {
    setReportDataScope(scope)
    setWidgetDataScopes(createDefaultWidgetDataScopes(allChartIds, scope))
  }

  const getExportReportSettingsFormProps = (includeTemplate = false) => ({
    includeTemplate,
    exportName,
    defaultReportName: getDefaultReportName(),
    onExportNameChange: (event) => setExportName(event.target.value),
    exportContext,
    onExportContextChange: setExportContext,
    exportCompareOpen,
    onExportCompareOpenChange: setExportCompareOpen,
    exportCompareRef,
    exportFormat,
    onExportFormatChange: setExportFormat,
    reportDataScope,
    onReportDataScopeChange: handleReportDataScopeChange,
    exportScheduleStatus,
    onOpenSchedule: openScheduleModal,
    exportTemplateRef,
    exportTemplateOpen,
    onExportTemplateOpenChange: setExportTemplateOpen,
    exportSourceTemplateId,
    reportTemplates,
    onApplyExportTemplate: applyExportTemplate,
  })

  const exportSettingsSummary = useMemo(
    () =>
      buildExportSettingsSummary({
        reportName: exportName.trim() || getDefaultReportName(),
        exportFormat,
        reportDataScope,
      }),
    [exportName, dateRange, exportFormat, reportDataScope],
  )

  const getMonthToDateRange = () => {
    const end = new Date()
    const start = new Date(end.getFullYear(), end.getMonth(), 1)
    return { start, end }
  }

  const getPreviousMonthRange = () => getLastMonthRange()

  const applyPresetRange = (preset) => {
    if (preset === 'last_30') {
      const end = new Date()
      const start = new Date(end)
      start.setDate(end.getDate() - 29)
      setDateRange({ start, end })
      setDateRangePreset('last_30')
      setShowCustomPicker(false)
      setCustomRange({ start: '', end: '' })
    }

    if (preset === 'month_to_date') {
      const range = getMonthToDateRange()
      setDateRange(range)
      setDateRangePreset('month_to_date')
      setShowCustomPicker(false)
      setCustomRange({ start: '', end: '' })
    }

    if (preset === 'previous_month') {
      const range = getPreviousMonthRange()
      setDateRange(range)
      setDateRangePreset('previous_month')
      setShowCustomPicker(false)
      setCustomRange({ start: '', end: '' })
    }
  }

  // Update overlay position on scroll/resize when hovered
  useEffect(() => {
    if (!hoveredKpiId) return

    const handleUpdate = () => {
      updateCardRect(hoveredKpiId)
    }

    const scrollEl = liveNowCardsScrollRef.current
    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)
    scrollEl?.addEventListener('scroll', handleUpdate)

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
      scrollEl?.removeEventListener('scroll', handleUpdate)
    }
  }, [hoveredKpiId])

  useEffect(() => {
    if (embedded) {
      setIsDocked(false)
      return undefined
    }
    if (!dashboardContentRef.current) return
    let observer

    const createObserver = () => {
      if (observer) observer.disconnect()
      if (!topBarSentinelRef.current) return
      observer = new IntersectionObserver(
        ([entry]) => {
          setIsDocked(!entry.isIntersecting)
        },
        {
          root: dashboardContentRef.current,
          threshold: 0,
          rootMargin: '0px',
        }
      )
      observer.observe(topBarSentinelRef.current)
    }

    createObserver()

    return () => {
      if (observer) observer.disconnect()
    }
  }, [embedded])

  useEffect(() => {
    return () => {
      clearKpiHoverTimeout()
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        clearKpiHoverTimeout()
        setHoveredKpiId(null)
      }
    }

    const handleDocClick = (event) => {
      if (!liveNowCardsRef.current) return
      if (!liveNowCardsRef.current.contains(event.target)) {
        clearKpiHoverTimeout()
        setHoveredKpiId(null)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleDocClick)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleDocClick)
    }
  }, [])

  useEffect(() => {
    if (!dashboardContentRef.current || !topBarRef.current) return

    const updateTopBarHeight = () => {
      const topBarEl = topBarRef.current
      const contentEl = dashboardContentRef.current
      if (!topBarEl || !contentEl) return
      const height = Math.round(topBarEl.getBoundingClientRect().height)
      contentEl.style.setProperty('--dashboard-topbar-h', `${height}px`)
    }

    updateTopBarHeight()
    window.addEventListener('resize', updateTopBarHeight)

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateTopBarHeight)
      : null

    if (resizeObserver) {
      resizeObserver.observe(topBarRef.current)
    }

    return () => {
      window.removeEventListener('resize', updateTopBarHeight)
      if (resizeObserver) resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!isConfigureOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isConfigureOpen])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setDateRangeOpen(false)
      }
      if (platformFilterRef.current && !platformFilterRef.current.contains(event.target)) {
        setPlatformFilterOpen(false)
      }
      if (topPostsViewRef.current && !topPostsViewRef.current.contains(event.target)) {
        setTopPostsViewOpen(false)
      }
      if (topPostsRankModeRef.current && !topPostsRankModeRef.current.contains(event.target)) {
        setTopPostsRankModeOpen(false)
      }
      if (topPostsPlatformFilterRef.current && !topPostsPlatformFilterRef.current.contains(event.target)) {
        setTopPostsPlatformFilterOpen(false)
      }
      if (paidMediaPlatformFilterRef.current && !paidMediaPlatformFilterRef.current.contains(event.target)) {
        setPaidMediaPlatformFilterOpen(false)
      }
      if (configurePlatformFilterRef.current && !configurePlatformFilterRef.current.contains(event.target)) {
        setConfigurePlatformFilterOpen(false)
      }
      if (goalsTypeRef.current && !goalsTypeRef.current.contains(event.target)) {
        setGoalsTypeOpen(false)
      }
      if (goalsTimeRef.current && !goalsTimeRef.current.contains(event.target)) {
        setGoalsTimeOpen(false)
      }
      if (goalsPlatformFilterRef.current && !goalsPlatformFilterRef.current.contains(event.target)) {
        setGoalsPlatformFilterOpen(false)
      }
      if (exportTemplateRef.current && !exportTemplateRef.current.contains(event.target)) {
        setExportTemplateOpen(false)
      }
      if (exportCompareRef.current && !exportCompareRef.current.contains(event.target)) {
        setExportCompareOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!topPostsRankModeOpen && !topPostsViewOpen && !topPostsPlatformFilterOpen) return

    const closeTopPostsMenus = () => {
      setTopPostsRankModeOpen(false)
      setTopPostsViewOpen(false)
      setTopPostsPlatformFilterOpen(false)
    }

    window.addEventListener('scroll', closeTopPostsMenus, true)
    const dashboardScrollEl = dashboardContentRef.current
    dashboardScrollEl?.addEventListener('scroll', closeTopPostsMenus, { passive: true })

    return () => {
      window.removeEventListener('scroll', closeTopPostsMenus, true)
      dashboardScrollEl?.removeEventListener('scroll', closeTopPostsMenus)
    }
  }, [topPostsRankModeOpen, topPostsViewOpen, topPostsPlatformFilterOpen])

  const handlePostPerformanceRankClick = (column) => {
    setTopPostsRankBy(column)
  }


  useEffect(() => {
    dispatch(fetchSocialNetworkTypes())
  }, [dispatch])

  useEffect(() => {
    if (!selectedClientId) {
      dispatch(clearClientDetails())
      return
    }
    dispatch(fetchClientDetailsByClientId(selectedClientId))
  }, [dispatch, selectedClientId])

  useEffect(() => {
    if (!selectedClientId) {
      dispatch(clearAccounts())
      return
    }
    dispatch(fetchSocialNetworkAccounts(selectedClientId))
  }, [dispatch, selectedClientId])

  useEffect(() => {
    if (!selectedClientId) {
      dispatch(clearInboxHealth())
      dispatch(clearDailyPerformance())
      dispatch(clearDailyPostStats())
      return
    }

    // Pulse KPI cards stay visible on Analytics and Goals — always fetch for the selected client.
    const clientId = String(selectedClientId)
    dispatch(
      fetchInboxHealth({
        clientId,
      }),
    )
    dispatch(
      fetchDailyPostStats({
        clientId,
      }),
    )
    const pulseRange = getPulseSnapshotDateRange()
    dispatch(
      fetchClientPulseSocialPerformance({
        clientId,
        startDate: pulseRange.startDate,
      }),
    )
  }, [dispatch, selectedClientId])

  const messagesReportFetchKey = useMemo(() => {
    if (!selectedClientId) return null
    return buildMessagesReportFetchKey(
      selectedClientId,
      formatMessagesReportBoundaryDate(dateRange.start),
      formatMessagesReportBoundaryDate(dateRange.end),
    )
  }, [selectedClientId, dateRange.start, dateRange.end])

  const postHeatmapFetchKey = useMemo(() => {
    if (!selectedClientId) return null
    return buildMessagesReportFetchKey(
      selectedClientId,
      formatPostHeatmapReportDate(dateRange.start),
      formatPostHeatmapReportDate(dateRange.end),
    )
  }, [selectedClientId, dateRange.start, dateRange.end])

  const demographicsFollowersFetchKey = useMemo(() => {
    if (!selectedClientId) return null
    return buildDemographicsFollowersFetchKey(
      selectedClientId,
      formatDemographicsReportDate(dateRange.start),
      formatDemographicsReportDate(dateRange.end),
    )
  }, [selectedClientId, dateRange.start, dateRange.end])

  const topPostsFetchKey = useMemo(() => {
    if (!selectedClientId) return null
    return buildTopPostsFetchKey(
      selectedClientId,
      formatTopPostsReportDate(dateRange.start),
      formatTopPostsReportDate(dateRange.end),
      'desc',
      dashboardReportRangeMode(dateRangePreset),
    )
  }, [selectedClientId, dateRange.start, dateRange.end, dateRangePreset])

  const resolvedTopPostsData = useMemo(() => {
    if (!selectedClientId) return []
    const posts = resolveTopPosts(clientTopPostsState, socialNetworkTypes)
    if (clientTopPostsState.status === 'failed') return []
    if (clientTopPostsState.status === 'loading') {
      if (posts.length > 0 && clientTopPostsState.requestedFetchKey === topPostsFetchKey) {
        return posts
      }
      return []
    }
    if (clientTopPostsState.status !== 'succeeded') return []
    if (clientTopPostsState.fetchedFetchKey !== topPostsFetchKey) return []
    return posts
  }, [
    selectedClientId,
    clientTopPostsState,
    socialNetworkTypes,
    topPostsFetchKey,
  ])

  const periodScopedTopPostsData = useMemo(
    () =>
      filterTopPostsByPropertyDateRange(
        resolvedTopPostsData,
        dateRange.start,
        dateRange.end,
        propertyChartTimezone,
      ),
    [resolvedTopPostsData, dateRange.start, dateRange.end, propertyChartTimezone],
  )

  const storyPerformanceFetchKey = useMemo(() => {
    if (!selectedClientId) return null
    return buildStoryPerformanceFetchKey(
      selectedClientId,
      formatStoryPerformanceDate(dateRange.start),
      formatStoryPerformanceDate(dateRange.end),
    )
  }, [selectedClientId, dateRange.start, dateRange.end])

  const resolvedTopStoriesData = useMemo(() => {
    if (!selectedClientId) return []
    const stories = resolveStoryPerformance(clientStoryPerformanceState)
    if (clientStoryPerformanceState.status === 'failed') return []
    if (clientStoryPerformanceState.status === 'loading') {
      if (stories.length > 0 && clientStoryPerformanceState.requestedFetchKey === storyPerformanceFetchKey) {
        return stories
      }
      return []
    }
    if (clientStoryPerformanceState.status !== 'succeeded') return []
    if (clientStoryPerformanceState.fetchedFetchKey !== storyPerformanceFetchKey) return []
    return stories
  }, [selectedClientId, clientStoryPerformanceState, storyPerformanceFetchKey])

  const filteredTopStoriesData = useMemo(
    () => resolvedTopStoriesData.filter((story) => topStoriesAvailablePlatforms.includes(story.platform)),
    [resolvedTopStoriesData, topStoriesAvailablePlatforms],
  )

  const handleVisibleStoriesChange = useCallback((stories) => {
    setTopStoriesVisibleForExport(Array.isArray(stories) ? stories : [])
  }, [])

  const topPostsPlatformOptions = useMemo(() => {
    const base = platforms || ['instagram', 'facebook', 'linkedin', 'twitter', 'tiktok']
    return ['all', ...base]
  }, [platforms])

  useEffect(() => {
    if (selectedClientId) {
      setTopPostsPlatform('all')
    }
  }, [selectedClientId])

  useEffect(() => {
    setTopPostsVisibleCount(15)
  }, [topPostsFetchKey, topPostsPlatform, topPostsRankBy, topPostsRankMode])

  useEffect(() => {
    if (!selectedClientId) {
      dispatch(clearClientMessagesReport())
      dispatch(clearClientSentimentsReport())
      dispatch(clearClientAlertsReport())
      dispatch(clearClientMessagesTimeOfDay())
      dispatch(clearClientAlertsTimeOfDay())
      dispatch(clearClientMessagesHeatmap())
      dispatch(clearClientDemographicsFollowers())
      dispatch(clearClientPostHeatmap())
      dispatch(clearClientTopPosts())
      dispatch(clearClientSocialPerformance())
      dispatch(clearClientCompetitivePerformance())
      dispatch(clearClientCampaignPerformance())
      dispatch(clearClientStoryPerformance())
      dispatch(clearClientPaidPerformance())
      dispatch(clearClientPaidMediaPerformance())
      dispatch(clearClientYtdPagePerformance())
      return
    }

    const clientId = String(selectedClientId)
    const range = { startDate: dateRange.start, endDate: dateRange.end }
    const plan = clientReportFetchPlan

    if (plan.monitoring) {
      dispatch(fetchClientMessagesReport({ clientId, ...range }))
      dispatch(fetchClientSentimentsReport({ clientId, ...range }))
      dispatch(fetchClientAlertsReport({ clientId, ...range }))
      dispatch(fetchClientMessagesTimeOfDay({ clientId, ...range }))
      dispatch(fetchClientAlertsTimeOfDay({ clientId, ...range }))
      dispatch(fetchClientMessagesHeatmap({ clientId, ...range }))
    }
    if (plan.audience) {
      dispatch(fetchClientDemographicsFollowers({ clientId, ...range }))
    }
    if (plan.content) {
      dispatch(fetchClientPostHeatmap({ clientId, ...range }))
      dispatch(fetchClientTopPosts({
        clientId,
        ...range,
        sortDirection: 'desc',
        rangeMode: dashboardReportRangeMode(dateRangePreset),
      }))
    }
    if (plan.social) {
      dispatch(fetchClientTopPosts({
        clientId,
        ...range,
        sortDirection: 'desc',
        rangeMode: dashboardReportRangeMode(dateRangePreset),
      }))
      dispatch(fetchClientCompetitivePerformance({
        clientId,
        ...range,
        rangeMode: dashboardReportRangeMode(dateRangePreset),
      }))
      dispatch(fetchClientCampaignPerformance({ clientId, ...range }))
      dispatch(fetchClientStoryPerformance({ clientId, ...range }))
    }
    if (plan.paid) {
      dispatch(fetchClientPaidMediaPerformance({ clientId, ...range }))
    }
  }, [
    dispatch,
    selectedClientId,
    dateRange.start,
    dateRange.end,
    dateRangePreset,
    clientReportFetchPlan,
  ])

  useEffect(() => {
    if (!selectedClientId || !clientReportFetchPlan.paid) return
    const paidArgs = {
      clientId: String(selectedClientId),
      startDate: dateRange.start,
      endDate: dateRange.end,
      id: 'all',
      sortBy: paidUiSortToApiSort(paidWidgetSortBy),
      limit: 10,
    }
    const viewLevel = paidViewToLevel(paidWidgetView)
    dispatch(fetchClientPaidPerformance({ ...paidArgs, level: viewLevel }))
    if (viewLevel !== 'campaign') {
      dispatch(fetchClientPaidPerformance({ ...paidArgs, level: 'campaign', prefetch: true }))
    }
  }, [
    dispatch,
    selectedClientId,
    dateRange.start,
    dateRange.end,
    clientReportFetchPlan.paid,
    paidWidgetView,
    paidWidgetSortBy,
  ])

  useEffect(() => {
    if (!selectedClientId) return
    if (!clientReportFetchPlan.social) return
    dispatch(
      fetchClientSocialPerformance({
        clientId: String(selectedClientId),
        startDate: dateRange.start,
        endDate: dateRange.end,
        source: monthlySocialSource,
        rangeMode: dashboardReportRangeMode(dateRangePreset),
      }),
    )
  }, [
    dispatch,
    selectedClientId,
    dateRange.start,
    dateRange.end,
    dateRangePreset,
    monthlySocialSource,
    clientReportFetchPlan.social,
  ])

  useEffect(() => {
    if (!selectedClientId) return
    if (!hasActiveGoals) return
    dispatch(
      fetchClientYtdPagePerformance({
        clientId: selectedClientId,
        startDate: dateRange.start,
        endDate: dateRange.end,
        rangeMode: dashboardReportRangeMode(dateRangePreset),
      }),
    )
  }, [
    dispatch,
    selectedClientId,
    hasActiveGoals,
    dateRange.start,
    dateRange.end,
    dateRangePreset,
  ])

  const sohoPulseCards = useMemo(() => {
    const base = (liveNowCards || []).filter((c) => c.id !== 'paid')

    if (!selectedClientId) return base

    const clientKey = String(selectedClientId)
    const inboxStats = pulseInboxStatsForClient(inboxHealthState, clientKey)
    const postStatsRows = pulseApiRowsForClient(dailyPostStatsState, clientKey)

    const pulseRange = getPulseSnapshotDateRange()
    const pulseSocialKey = buildPulseSocialPerformanceFetchKey(
      selectedClientId,
      pulseRange.startDate,
    )
    const pulsePlatformsReady =
      clientSocialPerformanceState.pulseStatus === 'succeeded' &&
      clientSocialPerformanceState.pulseFetchedFetchKey === pulseSocialKey &&
      Array.isArray(clientSocialPerformanceState.pulsePlatforms)

    const pulsePlatforms = pulseSocialPerformancePlatforms

    const pulseFetchFailed =
      clientSocialPerformanceState.pulseStatus === 'failed' &&
      String(clientSocialPerformanceState.pulseRequestedFetchKey ?? '') === pulseSocialKey

    let connectedPulsePlatformKeys = []
    if (
      socialNetworkAccountsState.status === 'succeeded' &&
      socialNetworkAccountsState.fetchedForClientId === clientKey
    ) {
      connectedPulsePlatformKeys = orderedConnectedChartKeys(
        connectedChartKeysFromSocialAccounts(
          socialNetworkAccountsState.accounts,
          socialNetworkTypes,
        ),
      )
    }

    const pulsePlatformLabel = (platformKey, typeId) => {
      const fromId =
        typeId != null ? socialNetworkTypeIdToLabel(socialNetworkTypes, typeId) : ""
      if (fromId) return fromId
      if (platformKey === "instagram") return "Instagram"
      if (platformKey === "facebook") return "Facebook"
      if (platformKey === "linkedin") return "LinkedIn"
      if (platformKey === "twitter") return "X"
      if (platformKey === "tiktok") return "TikTok"
      return platformKey || ""
    }

    const enrichPulseCard = (card) => {
      if (!card) return card
      const next = { ...card }
      if (card.id === 'engagement' && next.platformEngagementRows) {
        next.platformEngagementRows = ensureConnectedPlatformRows(
          next.platformEngagementRows,
          connectedPulsePlatformKeys,
          pulsePlatformLabel,
          { totalEngagements: 0, dayDelta: 0 },
          (rows) => rows.sort((a, b) => b.totalEngagements - a.totalEngagements),
          socialNetworkTypes,
        )
      }
      if (card.id === 'growth' && next.platformFollowerRows) {
        next.platformFollowerRows = ensureConnectedPlatformRows(
          next.platformFollowerRows,
          connectedPulsePlatformKeys,
          pulsePlatformLabel,
          { totalFollowers: 0, netNew: 0, growth: 0 },
          (rows) => rows.sort((a, b) => (b.growth ?? 0) - (a.growth ?? 0)),
          socialNetworkTypes,
        )
      }
      return next
    }

    const markReady = (built) => ({ ...enrichPulseCard(built), isLoading: false })
    const markLoading = (card) => ({ ...card, isLoading: true })
    const inboxPending = isPulseClientSlicePending(inboxHealthState, clientKey)
    const postStatsPending = isPulseClientSlicePending(dailyPostStatsState, clientKey)

    return base.map((card) => {
      if (card.id === 'inbox') {
        if (inboxPending) return markLoading(card)
        return markReady(buildInboxHealthCard(card, inboxStats, socialNetworkTypes))
      }
      if (card.id === 'engagement') {
        if (pulsePlatformsReady || pulseFetchFailed) {
          return markReady(
            buildEngagementCardFromSocialPerformance(
              card,
              pulsePlatforms,
              socialNetworkTypes,
            ),
          )
        }
        return markLoading(card)
      }
      if (card.id === 'growth') {
        if (pulsePlatformsReady || pulseFetchFailed) {
          return markReady(
            buildGrowthCardFromSocialPerformance(card, pulsePlatforms, socialNetworkTypes),
          )
        }
        return markLoading(card)
      }
      if (card.id === 'campaign') {
        if (postStatsPending) return markLoading(card)
        return markReady(
          buildCampaignCard(
            card,
            postStatsRows,
            socialNetworkTypes,
            connectedPulsePlatformKeys,
          ),
        )
      }
      return markReady(card)
    })
  }, [
    liveNowCards,
    inboxHealthState.status,
    inboxHealthState.stats,
    inboxHealthState.fetchedClientId,
    dailyPostStatsState.status,
    dailyPostStatsState.rows,
    dailyPostStatsState.fetchedClientId,
    clientSocialPerformanceState.status,
    clientSocialPerformanceState.fetchedFetchKey,
    clientSocialPerformanceState.raw,
    clientSocialPerformanceState.platforms,
    clientSocialPerformanceState.pulseStatus,
    clientSocialPerformanceState.pulseFetchedFetchKey,
    clientSocialPerformanceState.pulseRequestedFetchKey,
    clientSocialPerformanceState.pulseRaw,
    clientSocialPerformanceState.pulsePlatforms,
    pulseSocialPerformancePlatforms,
    socialNetworkAccountsState.status,
    socialNetworkAccountsState.fetchedForClientId,
    socialNetworkAccountsState.accounts,
    selectedClientId,
    socialNetworkTypes,
    dateRange.start,
    dateRange.end,
  ])

  const [lastUpdatedPulse, setLastUpdatedPulse] = useState(new Date())
  const [sentimentDrawerOpen, setSentimentDrawerOpen] = useState(false)
  const [selectedTheme, setSelectedTheme] = useState(null)
  const [selectedThemeSentiment, setSelectedThemeSentiment] = useState(null)

  useEffect(() => {
    if (!selectedClientId) return
    const clientKey = String(selectedClientId)
    const pulseFetched =
      (inboxHealthState.status === 'succeeded' &&
        String(inboxHealthState.fetchedClientId) === clientKey) ||
      (dailyPostStatsState.status === 'succeeded' &&
        String(dailyPostStatsState.fetchedClientId) === clientKey) ||
      (clientSocialPerformanceState.pulseStatus === 'succeeded' &&
        String(clientSocialPerformanceState.pulseClientId) === clientKey)
    if (pulseFetched) setLastUpdatedPulse(new Date())
  }, [
    selectedClientId,
    inboxHealthState.status,
    inboxHealthState.fetchedClientId,
    dailyPostStatsState.status,
    dailyPostStatsState.fetchedClientId,
    clientSocialPerformanceState.pulseStatus,
    clientSocialPerformanceState.pulseClientId,
  ])

  const competitorLegend = [
    { name: 'You', key: 'you', colorVar: '--chart-1' },
    { name: competitors[0].name, key: competitors[0].id, colorVar: '--chart-7' },
    { name: competitors[1].name, key: competitors[1].id, colorVar: '--chart-6' },
    { name: competitors[2].name, key: competitors[2].id, colorVar: '--chart-2' },
    { name: competitors[3].name, key: competitors[3].id, colorVar: '--chart-3' },
    { name: competitors[4].name, key: competitors[4].id, colorVar: '--chart-5' },
  ]

  const socialPerformanceTabs = [
    { id: 'engagement', label: 'Engagement' },
    { id: 'growth', label: 'Follower' },
  ]

  const alertCategorySeries = [
    { key: 'negative', label: 'Negative Message', colorVar: '--chart-5', stackId: 'alerts' },
    { key: 'general', label: 'General Question', colorVar: '--chart-1', stackId: 'alerts' },
    { key: 'guest', label: 'Guest on Property', colorVar: '--chart-7', stackId: 'alerts' },
    { key: 'reservation', label: 'Reservation Request', colorVar: '--chart-6', stackId: 'alerts' },
    { key: 'crisis', label: 'Crisis', colorVar: '--chart-3', stackId: 'alerts' },
  ]

  const alertReportingCategorySeries = [
    { key: 'wedding', label: 'Wedding Leads', colorVar: '--alert-reporting-1' },
    { key: 'fb', label: 'F&B', colorVar: '--alert-reporting-2' },
    { key: 'amenities', label: 'Amenities', colorVar: '--alert-reporting-3' },
  ]

  const alertCategoryPieSeries = alertCategorySeries.map((series) => ({
    key: series.label,
    label: series.label,
    colorVar: series.colorVar,
  }))

  const alertReportingCategoryPieSeries = alertReportingCategorySeries.map((series) => ({
    key: series.label,
    label: series.label,
    colorVar: series.colorVar,
  }))

  const filteredAlertsSeries = useMemo(() => {
    const startKey = toDateInputValue(dateRange.start)
    const endKey = toDateInputValue(dateRange.end)
    return alertsSeries.filter((entry) => entry.date >= startKey && entry.date <= endKey)
  }, [alertsSeries, dateRange, toDateInputValue])

  const filteredPreviousAlertsSeries = useMemo(() => {
    if (!previousPeriod?.start || !previousPeriod?.end) return []
    const startKey = toDateInputValue(previousPeriod.start)
    const endKey = toDateInputValue(previousPeriod.end)
    return alertsSeries.filter((entry) => entry.date >= startKey && entry.date <= endKey)
  }, [alertsSeries, previousPeriod, toDateInputValue])

  const optimalPostingSeries = useMemo(() => {
    if (!platformSeries || typeof platformSeries !== 'object') return []
    const platformsToUse =
      platformPulsePlatforms.size > 0 ? Array.from(platformPulsePlatforms) : Object.keys(platformSeries)
    const seriesArrays = platformsToUse
      .map((p) => platformSeries[p])
      .filter(Boolean)
    if (seriesArrays.length === 0) return []
    const map = new Map()
    seriesArrays.forEach((series) => {
      (series || []).forEach((entry) => {
        if (!entry?.date) return
        if (!map.has(entry.date)) {
          map.set(entry.date, {
            date: entry.date,
            impressions: 0,
            engagements: 0,
            linkClicks: 0,
            postsPublished: 0,
          })
        }
        const row = map.get(entry.date)
        row.impressions += entry.impressions ?? 0
        row.engagements += entry.engagements ?? 0
        row.linkClicks += entry.linkClicks ?? 0
        row.postsPublished += entry.postsPublished ?? 0
      })
    })
    const aggregated = Array.from(map.values())
    const startKey = toDateInputValue(dateRange?.start)
    const endKey = toDateInputValue(dateRange?.end)
    if (!startKey || !endKey) return aggregated
    return aggregated.filter((e) => e.date >= startKey && e.date <= endKey)
  }, [platformSeries, platformPulsePlatforms, dateRange, toDateInputValue])

  const optimalPostingCalendarByDay = useMemo(() => {
    const startKey = toDateInputValue(dateRange?.start)
    const endKey = toDateInputValue(dateRange?.end)
    if (!startKey || !endKey) {
      return {
        dates: [],
        matrix: [],
        samples: [],
        hours: [],
        totalPostsUsed: 0,
        totalEngagements: 0,
      }
    }
    return adaptPlatformSeriesToOptimalPostingCalendarHourly(optimalPostingSeries, startKey, endKey)
  }, [optimalPostingSeries, dateRange, toDateInputValue])

  const useApiPostHeatmap = Boolean(selectedClientId)

  const apiPostingHeatmap = useMemo(() => {
    if (!useApiPostHeatmap || !postHeatmapFetchKey) return null
    if (clientPostHeatmapState.status !== 'succeeded') return null
    if (clientPostHeatmapState.fetchedFetchKey !== postHeatmapFetchKey) return null
    return buildPostHeatmapFromReportRows(clientPostHeatmapState.rows, {
      timezone: propertyChartTimezone,
    })
  }, [
    useApiPostHeatmap,
    postHeatmapFetchKey,
    clientPostHeatmapState.status,
    clientPostHeatmapState.fetchedFetchKey,
    clientPostHeatmapState.rows,
    propertyChartTimezone,
  ])

  const emptyAlertCategories = useMemo(
    () =>
      alertCategorySeries.map((series) => ({
        name: series.label,
        count: 0,
        previous: 0,
      })),
    [alertCategorySeries],
  )

  const previousAlertCategories = useMemo(() => {
    const counts = filteredPreviousAlertsSeries.reduce((acc, entry) => {
      acc[entry.category] = (acc[entry.category] || 0) + entry.value
      return acc
    }, {})

    return alertCategorySeries.map((series) => ({
      name: series.label,
      count: counts[series.key] || 0,
    }))
  }, [filteredPreviousAlertsSeries, alertCategorySeries])

  const alertReportingCategories = useMemo(() => {
    const counts = filteredAlertsSeries.reduce((acc, entry) => {
      ;(entry.reportingShares || []).forEach((share) => {
        acc[share.key] = (acc[share.key] || 0) + share.value
      })
      return acc
    }, {})
    return alertReportingCategorySeries.map((series) => ({
      name: series.label,
      count: counts[series.key] || 0,
    }))
  }, [filteredAlertsSeries, alertReportingCategorySeries])

  const totalAlertReportingCategories = alertReportingCategories.reduce(
    (sum, item) => sum + item.count,
    0,
  )

  const alertsReportRows = useMemo(() => {
    if (!selectedClientId || !messagesReportFetchKey) return []
    if (clientAlertsReportState.status !== 'succeeded') return []
    if (clientAlertsReportState.fetchedFetchKey !== messagesReportFetchKey) return []
    return clientAlertsReportState.rows ?? []
  }, [
    selectedClientId,
    messagesReportFetchKey,
    clientAlertsReportState.status,
    clientAlertsReportState.fetchedFetchKey,
    clientAlertsReportState.rows,
  ])

  const useApiAlerts = Boolean(selectedClientId)

  const apiAlertsCharts = useMemo(() => {
    if (!useApiAlerts) return null
    const platformKeysFilter =
      platformPulsePlatforms.size > 0 ? Array.from(platformPulsePlatforms) : null
    return buildAlertsChartsFromRows(alertsReportRows, {
      startDate: dateRange.start,
      endDate: dateRange.end,
      platformKeysFilter,
      socialNetworkTypes,
    })
  }, [
    useApiAlerts,
    alertsReportRows,
    dateRange.start,
    dateRange.end,
    platformPulsePlatforms,
    socialNetworkTypes,
  ])

  const effectiveAlertCategorySeries = useApiAlerts
    ? apiAlertsCharts?.alertCategorySeries ?? []
    : alertCategorySeries

  const effectiveAlertCategoryPieSeries = useApiAlerts
    ? apiAlertsCharts?.alertCategoryPieSeries ?? []
    : alertCategoryPieSeries

  const effectiveAlertCategories = useApiAlerts
    ? apiAlertsCharts?.alertCategories ?? []
    : emptyAlertCategories

  const effectiveAlertReportingCategories = useApiAlerts
    ? apiAlertsCharts?.alertReportingCategories ?? []
    : alertReportingCategories

  const effectiveAlertReportingCategoryPieSeries = useApiAlerts
    ? apiAlertsCharts?.alertReportingCategoryPieSeries ?? []
    : alertReportingCategoryPieSeries

  const effectiveTotalAlertReportingCategories = effectiveAlertReportingCategories.reduce(
    (sum, item) => sum + item.count,
    0,
  )

  const alertTotals = useMemo(
    () => [
      {
        name: 'Total',
        value: effectiveAlertCategories.reduce((sum, item) => sum + item.count, 0),
      },
    ],
    [effectiveAlertCategories],
  )

  const paidMediaPerformanceFetchKey = useMemo(
    () =>
      buildPaidMediaPerformanceFetchKey(
        selectedClientId,
        formatPaidMediaPerformanceDate(dateRange.start),
        formatPaidMediaPerformanceDate(dateRange.end),
      ),
    [selectedClientId, dateRange.start, dateRange.end],
  )

  const paidMediaApiReady = Boolean(
    selectedClientId &&
      clientPaidMediaPerformanceState.status === 'succeeded' &&
      clientPaidMediaPerformanceState.fetchedFetchKey === paidMediaPerformanceFetchKey,
  )

  const resolvedPaidMediaChart = useMemo(() => {
    if (!paidMediaApiReady) return null
    return buildPaidMediaChartView(
      {
        platformRows: clientPaidMediaPerformanceState.platformRows,
        summary: clientPaidMediaPerformanceState.summary,
      },
      socialNetworkTypes,
    )
  }, [
    paidMediaApiReady,
    clientPaidMediaPerformanceState.platformRows,
    clientPaidMediaPerformanceState.summary,
    socialNetworkTypes,
  ])

  const effectivePaidMediaPlatform =
    paidMediaPlatform === 'all' || PAID_MEDIA_AD_PLATFORM_OPTIONS.includes(paidMediaPlatform)
      ? paidMediaPlatform
      : 'all'

  const EMPTY_PAID_TOTALS = { spend: 0, revenue: 0, roas: 0 }

  const paidMediaSeries = resolvedPaidMediaChart
    ? getPaidMediaSeriesForPlatform(resolvedPaidMediaChart, effectivePaidMediaPlatform)
    : []

  const paidMediaTotals = resolvedPaidMediaChart
    ? getPaidMediaTotalsForPlatform(resolvedPaidMediaChart, effectivePaidMediaPlatform)
    : EMPTY_PAID_TOTALS

  const paidMediaPreviousTotals = resolvedPaidMediaChart
    ? effectivePaidMediaPlatform === 'all'
      ? resolvedPaidMediaChart.previousTotals ?? EMPTY_PAID_TOTALS
      : resolvedPaidMediaChart.previousTotalsByPlatform?.[effectivePaidMediaPlatform] ?? EMPTY_PAID_TOTALS
    : EMPTY_PAID_TOTALS

  const paidMediaRevenueDelta = resolvedPaidMediaChart?.revenueChangePercent != null &&
    Number.isFinite(resolvedPaidMediaChart.revenueChangePercent)
    ? Math.round(resolvedPaidMediaChart.revenueChangePercent)
    : paidMediaPreviousTotals.revenue
      ? Math.round(
          ((paidMediaTotals.revenue - paidMediaPreviousTotals.revenue) /
            paidMediaPreviousTotals.revenue) *
            100,
        )
      : 0

  const paidMediaLineSeries = [
    {
      key: 'revenue',
      label: 'Revenue Generated',
      colorVar: '--chart-3',
      formatter: (value) => `$${formatCompactNumber(value)}`,
    },
    {
      key: 'roas',
      label: 'ROAS',
      colorVar: '--chart-1',
      formatter: (value) => `${Number(value).toFixed(1)}x`,
      valueType: 'roas',
    },
    {
      key: 'spend',
      label: 'Ad Spend',
      colorVar: '--chart-5',
      formatter: (value) => `$${formatCompactNumber(value)}`,
    },
  ]

  const engagementRateValue = totals.last30.impressions
    ? totals.last30.engagements / totals.last30.impressions
    : 0
  const previousEngagementRate = totals.previous30.impressions
    ? totals.previous30.engagements / totals.previous30.impressions
    : 0
  const engagementRateDelta = ((engagementRateValue - previousEngagementRate) / Math.max(previousEngagementRate, 0.001)) * 100

  const socialPerformanceFetchKey = useMemo(
    () =>
      buildSocialPerformanceFetchKey(
        selectedClientId,
        formatSocialPerformanceDate(dateRange.start),
        formatSocialPerformanceDate(dateRange.end),
        tableSourceToAggregationMode(monthlySocialSource),
        dashboardReportRangeMode(dateRangePreset),
      ),
    [selectedClientId, dateRange.start, dateRange.end, dateRangePreset, monthlySocialSource],
  )

  const competitivePerformanceFetchKey = useMemo(
    () =>
      buildCompetitivePerformanceFetchKey(
        selectedClientId,
        formatSocialPerformanceDate(dateRange.start),
        formatSocialPerformanceDate(dateRange.end),
        dashboardReportRangeMode(dateRangePreset),
      ),
    [selectedClientId, dateRange.start, dateRange.end, dateRangePreset],
  )

  const resolvedSocialPerformanceRows = useMemo(() => {
    const emptyEngagement = {
      metrics: [
        { key: 'engagementGrowth', label: 'Engagement Growth' },
        { key: 'newEngagements', label: 'Engagements' },
        { key: 'engagementRate', label: 'Engagement Rate' },
      ],
      metricLabel: 'Engagement Growth',
      columnLabels: ['Engagement Growth', 'Engagements', 'Engagement Rate'],
      rows: [],
      strategicInsight: '',
    }
    const emptyGrowth = {
      metrics: [
        { key: 'followerGrowth', label: 'Follower Growth' },
        { key: 'newFollowers', label: 'New Followers' },
        { key: 'totalFollowers', label: 'Total Followers' },
      ],
      metricLabel: 'Follower Growth',
      columnLabels: ['Follower Growth', 'Net New Followers', 'Followers'],
      rows: [],
      strategicInsight: '',
    }
    if (!selectedClientId) return socialPerformanceRows
    if (
      clientCompetitivePerformanceState.fetchedFetchKey === competitivePerformanceFetchKey &&
      (clientCompetitivePerformanceState.status === 'succeeded' ||
        clientCompetitivePerformanceState.status === 'failed')
    ) {
      return buildSocialPerformanceRowsFromCompetitiveReports(
        clientCompetitivePerformanceState.reports ?? [],
      )
    }
    return {
      engagement: emptyEngagement,
      growth: emptyGrowth,
    }
  }, [
    selectedClientId,
    socialPerformanceRows,
    clientCompetitivePerformanceState.status,
    clientCompetitivePerformanceState.fetchedFetchKey,
    clientCompetitivePerformanceState.reports,
    competitivePerformanceFetchKey,
  ])

  const currentSocialPerformance = resolvedSocialPerformanceRows[socialPerformanceTab]

  const campaignPerformanceFetchKey = useMemo(
    () =>
      buildCampaignPerformanceFetchKey(
        selectedClientId,
        formatCampaignPerformanceDate(dateRange.start),
        formatCampaignPerformanceDate(dateRange.end),
      ),
    [selectedClientId, dateRange.start, dateRange.end],
  )

  const paidLevel = paidViewToLevel(paidWidgetView)
  const paidApiSortBy = paidUiSortToApiSort(paidWidgetSortBy)

  const paidPerformanceFetchKey = useMemo(
    () =>
      selectedClientId
        ? buildPaidPerformanceFetchKey(
            selectedClientId,
            formatPaidPerformanceDate(dateRange.start),
            formatPaidPerformanceDate(dateRange.end),
            { level: paidLevel, sortBy: paidApiSortBy, id: 'all' },
          )
        : null,
    [selectedClientId, dateRange.start, dateRange.end, paidLevel, paidApiSortBy],
  )

  const paidLevelCached = Boolean(
    paidPerformanceFetchKey && clientPaidPerformanceState.loadedKeys?.[paidPerformanceFetchKey],
  )
  const paidLevelPaging =
    clientPaidPerformanceState.pagingByLevel?.[paidLevel] ?? clientPaidPerformanceState.paging

  const paidPerformanceApiReady = Boolean(
    selectedClientId &&
      (paidLevelCached ||
        (clientPaidPerformanceState.fetchedFetchKey === paidPerformanceFetchKey &&
          (clientPaidPerformanceState.status === 'succeeded' ||
            clientPaidPerformanceState.status === 'loadingMore'))),
  )

  const resolvedPaidTopAds = useMemo(() => {
    if (!paidPerformanceApiReady) return []
    return mapPaidPerformanceAds(clientPaidPerformanceState.ads ?? [], { keepAllRows: true })
  }, [paidPerformanceApiReady, clientPaidPerformanceState.ads])

  const resolvedPaidCampaigns = useMemo(() => {
    return mapPaidPerformanceCampaigns(
      clientPaidPerformanceState.campaigns ?? [],
      clientPaidPerformanceState.ads ?? [],
      { keepAllRows: true },
    )
  }, [
    clientPaidPerformanceState.campaigns,
    clientPaidPerformanceState.ads,
  ])

  const resolvedPaidPerformanceCampaigns = useMemo(() => {
    return mapPaidPerformanceCampaigns(
      clientPaidPerformanceState.campaigns ?? [],
      clientPaidPerformanceState.ads ?? [],
      { keepAllRows: true },
    )
  }, [
    clientPaidPerformanceState.campaigns,
    clientPaidPerformanceState.ads,
  ])

  const handlePaidLoadMore = useCallback(() => {
    if (!selectedClientId) return
    const paging = clientPaidPerformanceState.pagingByLevel?.[paidLevel]
      ?? clientPaidPerformanceState.paging
    if (!paging?.hasMore || clientPaidPerformanceState.status === 'loadingMore') return
    dispatch(fetchClientPaidPerformance({
      clientId: String(selectedClientId),
      startDate: dateRange.start,
      endDate: dateRange.end,
      level: paidLevel,
      id: 'all',
      sortBy: paidApiSortBy,
      after: paging.after,
      next: paging.next,
      append: true,
      limit: 10,
    }))
  }, [
    dispatch,
    selectedClientId,
    dateRange.start,
    dateRange.end,
    paidLevel,
    paidApiSortBy,
    clientPaidPerformanceState.pagingByLevel,
    clientPaidPerformanceState.paging,
    clientPaidPerformanceState.status,
  ])

  const filteredPaidTopAdsForExport = useMemo(() => {
    if (!resolvedPaidTopAds.length) return []
    const showAll =
      !topAdsPlatforms ||
      topAdsPlatforms.size === 0 ||
      topAdsPlatforms.has('all')
    if (showAll) return resolvedPaidTopAds
    return resolvedPaidTopAds.filter((ad) => topAdsPlatforms.has(ad.platformKey))
  }, [resolvedPaidTopAds, topAdsPlatforms])

  const paidMediaTopAdsEnabledViews = [
    ...(isChartVisible('topPerformingAds') ? ['ads'] : []),
    ...(isChartVisible('topCampaigns') ? ['campaigns'] : []),
    ...(isChartVisible('topObjectives') ? ['objectives'] : []),
    ...(isChartVisible('advertisingPerformance') ? ['performance'] : []),
  ]

  const enabledPaidExportChartIds = useMemo(
    () =>
      PAID_MEDIA_EXPORT_CHART_IDS.filter((chartId) => {
        if (chartId === 'paidMediaPerformance') return selectedCharts.has('paidMediaPerformance')
        if (chartId === 'topPerformingAds') return selectedCharts.has('topPerformingAds')
        if (chartId === 'topObjectives') return selectedCharts.has('topObjectives')
        if (chartId === 'topCampaigns') return selectedCharts.has('topCampaigns')
        if (chartId === 'advertisingPerformance') return selectedCharts.has('advertisingPerformance')
        return false
      }),
    [selectedCharts],
  )

  const resolvedCampaignChartData = useMemo(() => {
    if (!selectedClientId) return null

    const events = clientCampaignPerformanceState.events ?? []
    const platformFilter =
      platformPulsePlatforms.size > 0 ? platformPulsePlatforms : null

    if (clientCampaignPerformanceState.status === 'failed') return []

    if (clientCampaignPerformanceState.status === 'loading') {
      if (
        events.length > 0 &&
        clientCampaignPerformanceState.requestedFetchKey === campaignPerformanceFetchKey
      ) {
        return buildCampaignPerformanceChartData(events, platformFilter, socialNetworkTypes)
      }
      return []
    }

    if (clientCampaignPerformanceState.status !== 'succeeded') return []

    if (clientCampaignPerformanceState.fetchedFetchKey !== campaignPerformanceFetchKey) return []

    return buildCampaignPerformanceChartData(events, platformFilter, socialNetworkTypes)
  }, [
    selectedClientId,
    clientCampaignPerformanceState.status,
    clientCampaignPerformanceState.requestedFetchKey,
    clientCampaignPerformanceState.fetchedFetchKey,
    clientCampaignPerformanceState.events,
    campaignPerformanceFetchKey,
    platformPulsePlatforms,
    socialNetworkTypes,
  ])

  const effectiveCampaignEventData = useMemo(
    () => resolvedCampaignChartData ?? [],
    [resolvedCampaignChartData],
  )

  const socialPerformancePlatformFilter = useMemo(() => {
    const connected = connectedPropertyPlatformKeysList
    const selected =
      platformPulsePlatforms.size > 0 ? Array.from(platformPulsePlatforms) : null

    if (Array.isArray(connected)) {
      if (selected?.length) {
        const narrowed = selected.filter((key) => connected.includes(key))
        return narrowed.length > 0 ? narrowed : connected
      }
      return connected
    }

    return selected
  }, [connectedPropertyPlatformKeysList, platformPulsePlatforms])

  const socialPerformanceChartOptions = useMemo(
    () => ({
      socialNetworkTypes,
      aggregationSource: monthlySocialSource,
      ...(Array.isArray(socialPerformancePlatformFilter)
        ? { platformKeysFilter: socialPerformancePlatformFilter }
        : {}),
    }),
    [socialNetworkTypes, socialPerformancePlatformFilter, monthlySocialSource],
  )

  const monthlySocialTableOptions = useMemo(
    () => ({ ...socialPerformanceChartOptions }),
    [socialPerformanceChartOptions],
  )

  const emptyMonthlySocialApiTables = useMemo(
    () => buildMonthlySocialTablesBySource([], socialNetworkTypes, monthlySocialTableOptions),
    [socialNetworkTypes, monthlySocialTableOptions],
  )

  const resolvedMonthlySocialApiTables = useMemo(() => {
    if (!selectedClientId) return null
    if (
      clientSocialPerformanceState.status === 'succeeded' &&
      clientSocialPerformanceState.fetchedFetchKey === socialPerformanceFetchKey
    ) {
      return buildMonthlySocialTablesBySource(
        socialPerformancePlatforms,
        socialNetworkTypes,
        monthlySocialTableOptions,
      )
    }
    return emptyMonthlySocialApiTables
  }, [
    selectedClientId,
    clientSocialPerformanceState.status,
    clientSocialPerformanceState.fetchedFetchKey,
    socialPerformancePlatforms,
    socialPerformanceFetchKey,
    socialNetworkTypes,
    monthlySocialTableOptions,
    emptyMonthlySocialApiTables,
  ])

  const monthlySocialRowsBySource = useMemo(() => {
    if (!selectedClientId) {
      return {
        all: dashboardView.monthlySocialRowsBySource?.all ?? monthlySocialRows ?? [],
        organic: dashboardView.monthlySocialRowsBySource?.organic ?? [],
        paid: dashboardView.monthlySocialRowsBySource?.paid ?? [],
      }
    }
    const tables = resolvedMonthlySocialApiTables ?? emptyMonthlySocialApiTables
    const cache = clientSocialPerformanceState.byFetchKey || {}
    const start = formatSocialPerformanceDate(dateRange.start)
    const end = formatSocialPerformanceDate(dateRange.end)
    const rangeMode = dashboardReportRangeMode(dateRangePreset)
    const rowsForSource = (source) => {
      const cacheKey = buildSocialPerformanceFetchKey(
        selectedClientId,
        start,
        end,
        tableSourceToAggregationMode(source),
        rangeMode,
      )
      const cached = cache[cacheKey]
      const cachedRaw = cached?.raw
      if (cachedRaw != null) {
        const platforms = normalizeSocialPerformancePayload(cachedRaw, socialNetworkTypes, {
          startDate: start,
          endDate: end,
          aggregationMode: tableSourceToAggregationMode(source),
        })
        if (platforms.length > 0) {
          return buildMonthlySocialTable(platforms, socialNetworkTypes, {
            ...monthlySocialTableOptions,
            aggregationSource: source,
          }).rows
        }
      }
      return tables[source]?.rows ?? []
    }
    return {
      all: rowsForSource('all'),
      organic: rowsForSource('organic'),
      paid: rowsForSource('paid'),
    }
  }, [
    selectedClientId,
    dashboardView.monthlySocialRowsBySource,
    monthlySocialRows,
    resolvedMonthlySocialApiTables,
    emptyMonthlySocialApiTables,
    clientSocialPerformanceState.byFetchKey,
    dateRange.start,
    dateRange.end,
    dateRangePreset,
    socialNetworkTypes,
    monthlySocialTableOptions,
  ])

  const resolvedMonthlySocial = useMemo(() => {
    if (!selectedClientId) {
      return {
        columns: monthlySocialColumns,
        rows: monthlySocialRowsBySource.all,
      }
    }
    const primary = resolvedMonthlySocialApiTables?.all ?? emptyMonthlySocialApiTables.all
    return {
      columns: primary?.columns ?? monthlySocialColumns,
      rows: primary?.rows ?? [],
    }
  }, [
    selectedClientId,
    monthlySocialColumns,
    monthlySocialRowsBySource.all,
    resolvedMonthlySocialApiTables,
    emptyMonthlySocialApiTables,
  ])

  const resolvedPlatformComparisonData = useMemo(() => {
    if (!selectedClientId) return null

    const platforms = socialPerformancePlatforms

    if (clientSocialPerformanceState.status === 'failed') return []

    if (clientSocialPerformanceState.status === 'loading') {
      if (
        platforms.length > 0 &&
        clientSocialPerformanceState.requestedFetchKey === socialPerformanceFetchKey
      ) {
        return buildPlatformPerformanceComparisonData(platforms, socialPerformanceChartOptions)
      }
      return []
    }

    if (clientSocialPerformanceState.status !== 'succeeded') return []

    if (clientSocialPerformanceState.fetchedFetchKey !== socialPerformanceFetchKey) return []

    return buildPlatformPerformanceComparisonData(platforms, socialPerformanceChartOptions)
  }, [
    selectedClientId,
    clientSocialPerformanceState.status,
    clientSocialPerformanceState.requestedFetchKey,
    clientSocialPerformanceState.fetchedFetchKey,
    socialPerformancePlatforms,
    socialPerformanceFetchKey,
    socialPerformanceChartOptions,
  ])

  const effectiveAlertsChartData = useApiAlerts
    ? apiAlertsCharts?.alertsChartData ?? []
    : []

  const effectiveAlertsSeriesForSpikes = useApiAlerts
    ? apiAlertsCharts?.alertsSeries ?? []
    : filteredAlertsSeries

  const totalAlerts = alertTotals[0]?.value || 0
  const previousTotalAlerts = useApiAlerts
    ? apiAlertsCharts?.previousTotalAlerts ?? 0
    : totals.previous30.alerts || 0
  const alertsChartsEmpty = totalAlerts === 0
  const negativeAlerts =
    effectiveAlertCategories.find((entry) => entry.name === 'Negative Message')?.count || 0
  const previousNegativeAlerts = useApiAlerts
    ? effectiveAlertCategories.find((entry) => entry.name === 'Negative Message')?.previous || 0
    : Math.round(previousTotalAlerts * 0.25)

  const alertSpikeDates = Object.entries(
    effectiveAlertsSeriesForSpikes.reduce((acc, entry) => {
      const date = entry.date
      const value = entry.value ?? 0
      acc[date] = (acc[date] || 0) + value
      return acc
    }, {})
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([date]) => formatShortDate(date))
    .join(', ')

  const campaignSeries = [
    {
      key: 'impressions',
      label: 'Total Impressions',
      colorVar: '--chart-1',
      formatter: formatCompactNumber,
      yAxisId: 'left',
    },
    {
      key: 'engagements',
      label: 'Total Engagements',
      colorVar: '--chart-3',
      formatter: formatCompactNumber,
      yAxisId: 'right',
    },
  ]

  const audienceAgeGenderSeries = [
    { key: 'female', label: 'Female', colorVar: '--chart-7' },
    { key: 'male', label: 'Male', colorVar: '--chart-2' },
  ]

  const audienceTopCitiesSeries = [
    { key: 'facebook', label: 'Facebook', colorVar: '--chart-2', formatter: formatCompactNumber },
    { key: 'instagram', label: 'Instagram', colorVar: '--chart-1', formatter: formatCompactNumber },
  ]

  const platformLabels = { all: 'All', instagram: 'Instagram', facebook: 'Facebook', linkedin: 'LinkedIn', twitter: 'X', tiktok: 'TikTok' }
  const platformFilterOptions = platforms || ['instagram', 'facebook', 'linkedin', 'twitter', 'tiktok']
  const isAllPlatformsSelected = platformPulsePlatforms.size === 0
  const isPlatformPulseSelected = (platform) =>
    isAllPlatformsSelected || platformPulsePlatforms.has(platform)
  const platformPulseFilterLabel = isAllPlatformsSelected
    ? 'All'
    : `${platformPulsePlatforms.size} selected`
  const handlePlatformPulseAllToggle = (e) => {
    if (e.target.checked) {
      applyPlatformPulse(new Set())
    }
  }
  const handlePlatformPulseToggle = (platform) => {
    if (isAllPlatformsSelected) {
      applyPlatformPulse(new Set(platformFilterOptions.filter((p) => p !== platform)))
      return
    }
    const next = new Set(platformPulsePlatforms)
    if (next.has(platform)) {
      next.delete(platform)
      applyPlatformPulse(next.size > 0 ? next : new Set())
    } else {
      next.add(platform)
      applyPlatformPulse(next.size === platformFilterOptions.length ? new Set() : next)
    }
  }
  const platformChartColors = ['--chart-1', '--chart-2', '--chart-5', '--chart-6', '--chart-7']

  const campaignChartHasMetrics = useMemo(
    () => campaignChartRowsHaveMetrics(effectiveCampaignEventData),
    [effectiveCampaignEventData],
  )

  const campaignPerformanceEmptyMessage = useMemo(() => {
    const platformLabel =
      platformPulsePlatforms.size > 0
        ? Array.from(platformPulsePlatforms).map((p) => platformLabels[p] || p).join(', ')
        : null

    if (effectiveCampaignEventData.length === 0) {
      return platformLabel
        ? `No post events for ${platformLabel} in this date range`
        : 'No data available'
    }

    if (!campaignChartHasMetrics) {
      return platformLabel
        ? `No impressions or engagements for ${platformLabel} in this date range. Try another platform or clear the filter.`
        : 'No impressions or engagements for this date range'
    }

    return 'No data available'
  }, [effectiveCampaignEventData, campaignChartHasMetrics, platformPulsePlatforms])

  const campaignPerformanceForceEmpty =
    effectiveCampaignEventData.length === 0 || !campaignChartHasMetrics

  const messagesReportRows = useMemo(() => {
    if (!selectedClientId || !messagesReportFetchKey) return []
    if (clientMessagesReportState.status !== 'succeeded') return []
    if (clientMessagesReportState.fetchedFetchKey !== messagesReportFetchKey) return []
    return clientMessagesReportState.rows ?? []
  }, [
    selectedClientId,
    messagesReportFetchKey,
    clientMessagesReportState.status,
    clientMessagesReportState.fetchedFetchKey,
    clientMessagesReportState.rows,
  ])

  const useApiMessagesMonitoring = Boolean(selectedClientId)

  const apiMonitoringCharts = useMemo(() => {
    if (!useApiMessagesMonitoring) return null
    const platformKeysFilter =
      platformPulsePlatforms.size > 0 ? Array.from(platformPulsePlatforms) : null
    return buildMonitoringChartsFromMessagesRows(
      messagesReportRows,
      socialNetworkTypes,
      {
        startDate: dateRange.start,
        endDate: dateRange.end,
        platformKeysFilter,
      },
    )
  }, [
    useApiMessagesMonitoring,
    messagesReportRows,
    socialNetworkTypes,
    dateRange.start,
    dateRange.end,
    platformPulsePlatforms,
  ])

  const messagesReportReady =
    !useApiMessagesMonitoring ||
    (clientMessagesReportState.status === 'succeeded' &&
      clientMessagesReportState.fetchedFetchKey === messagesReportFetchKey)

  const baseMonitoringSeries = useApiMessagesMonitoring
    ? (messagesReportReady ? apiMonitoringCharts?.monitoringSeries ?? [] : [])
    : monitoringSeries

  const baseMonitoringShare = useApiMessagesMonitoring
    ? (messagesReportReady ? apiMonitoringCharts?.monitoringShare ?? [] : [])
    : monitoringShare

  const monitoringShareForDisplay = useMemo(() => {
    if (messageChannelFilter === 'all') return baseMonitoringShare
    return baseMonitoringShare.filter((item) => item.name === messageChannelFilter)
  }, [baseMonitoringShare, messageChannelFilter])

  const monitoringSeriesForDisplay = baseMonitoringSeries

  const sentimentsReportRows = useMemo(() => {
    if (!selectedClientId || !messagesReportFetchKey) return []
    if (clientSentimentsReportState.status !== 'succeeded') return []
    if (clientSentimentsReportState.fetchedFetchKey !== messagesReportFetchKey) return []
    return clientSentimentsReportState.rows ?? []
  }, [
    selectedClientId,
    messagesReportFetchKey,
    clientSentimentsReportState.status,
    clientSentimentsReportState.fetchedFetchKey,
    clientSentimentsReportState.rows,
  ])

  const apiSentimentCharts = useMemo(() => {
    if (!useApiMessagesMonitoring) return null
    const platformKeysFilter =
      platformPulsePlatforms.size > 0 ? Array.from(platformPulsePlatforms) : null
    return buildSentimentChartsFromRows(
      sentimentsReportRows,
      socialNetworkTypes,
      {
        startDate: dateRange.start,
        endDate: dateRange.end,
        platformKeysFilter,
      },
    )
  }, [
    useApiMessagesMonitoring,
    sentimentsReportRows,
    socialNetworkTypes,
    dateRange.start,
    dateRange.end,
    platformPulsePlatforms,
  ])

  const sentimentsReportReady =
    !useApiMessagesMonitoring ||
    (clientSentimentsReportState.status === 'succeeded' &&
      clientSentimentsReportState.fetchedFetchKey === messagesReportFetchKey)

  const baseSentimentSeries = useApiMessagesMonitoring
    ? (sentimentsReportReady ? apiSentimentCharts?.sentimentSeries ?? [] : [])
    : sentimentSeries

  const baseSentimentShare = useApiMessagesMonitoring
    ? (sentimentsReportReady ? apiSentimentCharts?.sentimentShare ?? [] : [])
    : sentimentShare

  const sentimentShareForDisplay = useMemo(() => {
    if (sentimentFilter === 'all') return baseSentimentShare
    return baseSentimentShare.filter((item) => item.name === sentimentFilter)
  }, [baseSentimentShare, sentimentFilter])

  const sentimentSeriesForDisplay = baseSentimentSeries

  const sentimentTotalMessages = useApiMessagesMonitoring
    ? apiSentimentCharts?.sentimentCounts?.total ?? 0
    : totals.last30.messages

  const positivePercent =
    baseSentimentShare.find((entry) => entry.name === 'Positive')?.value || 0
  const negativePercent =
    baseSentimentShare.find((entry) => entry.name === 'Negative')?.value || 0
  const netSentiment = Math.round(positivePercent - negativePercent)

  const sentimentSpikeDates = [...baseSentimentSeries]
    .sort((a, b) => b.negative - a.negative)
    .slice(0, 2)
    .map((entry) => formatShortDate(entry.date))
    .join(', ')

  const audienceCountriesPlatformKeys = useMemo(() => {
    if (!selectedClientId) {
      if (platformPulsePlatforms.size > 0) return Array.from(platformPulsePlatforms)
      return platforms || ['instagram', 'facebook']
    }
    let keys = connectedPropertyPlatformKeysList ?? []
    if (platformPulsePlatforms.size > 0) {
      keys = keys.filter((key) => platformPulsePlatforms.has(key))
    }
    return keys
  }, [
    selectedClientId,
    connectedPropertyPlatformKeysList,
    platformPulsePlatforms,
    platforms,
  ])

  const audienceTopCountriesFbIgSeries = [
    {
      key: 'facebook',
      label: 'Facebook',
      colorVar: '--chart-2',
      formatter: formatCompactNumber,
      yAxisId: 'left',
    },
    {
      key: 'instagram',
      label: 'Instagram',
      colorVar: '--chart-1',
      formatter: formatCompactNumber,
      yAxisId: 'right',
    },
  ]

  const audienceDemographicPlatformEffective = audienceDemographicPlatform

  const useApiAudienceInsights = Boolean(selectedClientId)

  const audienceInsightsPlatformFilter = useMemo(() => {
    if (!selectedClientId) {
      return platformPulsePlatforms.size > 0 ? Array.from(platformPulsePlatforms) : null
    }
    const keys = connectedPropertyPlatformKeysList ?? []
    if (keys.length === 0) return []
    if (platformPulsePlatforms.size > 0) {
      const narrowed = keys.filter((key) => platformPulsePlatforms.has(key))
      return narrowed.length > 0 ? narrowed : keys
    }
    return keys
  }, [selectedClientId, connectedPropertyPlatformKeysList, platformPulsePlatforms])

  const apiAudienceInsights = useMemo(() => {
    if (!useApiAudienceInsights || !demographicsFollowersFetchKey) return null
    if (clientDemographicsFollowersState.status !== 'succeeded') return null
    if (clientDemographicsFollowersState.fetchedFetchKey !== demographicsFollowersFetchKey) {
      return null
    }
    return buildAudienceInsightsFromDemographicsRows(
      clientDemographicsFollowersState.rows,
      socialNetworkTypes,
      { platformKeysFilter: audienceInsightsPlatformFilter },
    )
  }, [
    useApiAudienceInsights,
    demographicsFollowersFetchKey,
    clientDemographicsFollowersState.status,
    clientDemographicsFollowersState.fetchedFetchKey,
    clientDemographicsFollowersState.rows,
    socialNetworkTypes,
    audienceInsightsPlatformFilter,
  ])

  const isDashboardSliceLoading = (slice, fetchKey) =>
    Boolean(
      selectedClientId &&
        fetchKey &&
        slice?.status === 'loading' &&
        slice?.requestedFetchKey === fetchKey,
    )

  /** True until slice succeeded for this fetch key (or failed). Avoids flashing empty data on first paint. */
  const isDashboardSlicePending = (slice, fetchKey) => {
    if (!selectedClientId || !fetchKey) return false
    if (slice?.status === 'failed') return false
    if (slice?.loadedKeys?.[fetchKey]) return false
    if (slice?.status === 'succeeded' && slice?.fetchedFetchKey === fetchKey) return false
    return true
  }

  const socialPerformanceLoading = isDashboardSlicePending(
    clientSocialPerformanceState,
    socialPerformanceFetchKey,
  )
  const competitivePerformanceLoading = isDashboardSlicePending(
    clientCompetitivePerformanceState,
    competitivePerformanceFetchKey,
  )
  const campaignPerformanceLoading = isDashboardSlicePending(
    clientCampaignPerformanceState,
    campaignPerformanceFetchKey,
  )
  const topStoriesInitialLoading = Boolean(
    selectedClientId &&
      isDashboardSlicePending(clientStoryPerformanceState, storyPerformanceFetchKey),
  )
  const messagesReportLoading = isDashboardSlicePending(clientMessagesReportState, messagesReportFetchKey)
  const sentimentsReportLoading = isDashboardSlicePending(clientSentimentsReportState, messagesReportFetchKey)
  const alertsReportLoading = isDashboardSlicePending(clientAlertsReportState, messagesReportFetchKey)
  const monitoringTimeOfDayLoading =
    isDashboardSlicePending(clientMessagesTimeOfDayState, messagesReportFetchKey) ||
    isDashboardSlicePending(clientAlertsTimeOfDayState, messagesReportFetchKey)
  const postHeatmapLoading =
    useApiPostHeatmap && isDashboardSlicePending(clientPostHeatmapState, postHeatmapFetchKey)
  const messagesHeatmapLoading = isDashboardSlicePending(clientMessagesHeatmapState, messagesReportFetchKey)
  const paidMediaPerformanceLoading = isDashboardSlicePending(
    clientPaidMediaPerformanceState,
    paidMediaPerformanceFetchKey,
  )
  const paidPerformanceLoading = Boolean(
    selectedClientId &&
      isDashboardSlicePending(clientPaidPerformanceState, paidPerformanceFetchKey) &&
      clientPaidPerformanceState.status !== 'loadingMore',
  )

  const audienceDemographicsLoading = Boolean(
    useApiAudienceInsights &&
      demographicsFollowersFetchKey &&
      isDashboardSlicePending(clientDemographicsFollowersState, demographicsFollowersFetchKey),
  )

  const topPostsInitialLoading = Boolean(
    selectedClientId &&
      isDashboardSlicePending(clientTopPostsState, topPostsFetchKey),
  )

  const audienceDemographicData = useMemo(() => {
    if (useApiAudienceInsights) {
      return apiAudienceInsights?.demographicData ?? null
    }
    const byPlatform = audienceDemographicsByPlatform?.[audienceDemographicPlatformEffective]
    return byPlatform ?? null
  }, [
    useApiAudienceInsights,
    apiAudienceInsights,
    audienceDemographicsByPlatform,
    audienceDemographicPlatformEffective,
  ])

  const effectiveAudienceTopCities = useMemo(() => {
    if (useApiAudienceInsights) {
      return apiAudienceInsights?.topCities ?? []
    }
    return audienceTopCities
  }, [useApiAudienceInsights, apiAudienceInsights, audienceTopCities])

  const effectiveAudienceTopCountries = useMemo(() => {
    if (useApiAudienceInsights) {
      return apiAudienceInsights?.topCountries ?? []
    }
    return audienceTopCountries
  }, [useApiAudienceInsights, apiAudienceInsights, audienceTopCountries])

  const displayAudienceTopCountries = useMemo(() => {
    if (selectedClientId && audienceCountriesPlatformKeys.length === 0) return []
    if (!audienceCountriesPlatformKeys.length) return effectiveAudienceTopCountries
    return filterTopCountriesRowsByPlatformKeys(
      effectiveAudienceTopCountries,
      audienceCountriesPlatformKeys,
    )
  }, [
    selectedClientId,
    audienceCountriesPlatformKeys,
    effectiveAudienceTopCountries,
  ])

  const displayAudienceTopCountriesChart = useMemo(
    () => filterTopCountriesRowsByPlatformKeys(displayAudienceTopCountries, ['facebook', 'instagram']),
    [displayAudienceTopCountries],
  )

  const effectiveAudienceTopCountriesSeries = audienceTopCountriesFbIgSeries

  const audienceAgeGenderPlatformKeys = apiAudienceInsights?.ageGenderPlatformKeys ?? []

  const audienceAgeGenderSummary = useMemo(() => {
    const data = audienceDemographicData?.ageGender || audienceAgeGender
    return buildAgeGenderInsight(data, audienceAgeGenderPlatformKeys, platformLabels)
  }, [audienceDemographicData, audienceAgeGender, audienceAgeGenderPlatformKeys, platformLabels])

  const audienceAgeGenderSourceLabel = buildAgeGenderSourceLabel()

  const audienceTopCitiesInsight = useMemo(
    () => buildTopCitiesInsight(effectiveAudienceTopCities),
    [effectiveAudienceTopCities],
  )

  const audienceTopCountriesInsight = useMemo(
    () => buildTopCountriesInsight(displayAudienceTopCountriesChart, platformLabels),
    [displayAudienceTopCountriesChart, platformLabels],
  )

  const audienceGeographicInsight = useMemo(
    () => buildGeographicInsight(audienceDemographicData?.geographic),
    [audienceDemographicData?.geographic],
  )

  const monitoringPlatformsForCharts = useMemo(() => {
    if (useApiMessagesMonitoring && apiMonitoringCharts?.platformKeys?.length) {
      let keys = apiMonitoringCharts.platformKeys
      if (platformPulsePlatforms.size > 0) {
        keys = keys.filter((key) => platformPulsePlatforms.has(key))
      }
      return keys
    }
    return platformPulsePlatforms.size > 0
      ? Array.from(platformPulsePlatforms)
      : platforms || ['instagram', 'facebook']
  }, [
    useApiMessagesMonitoring,
    apiMonitoringCharts?.platformKeys,
    platformPulsePlatforms,
    platforms,
  ])

  const monitoringLineSeries = monitoringPlatformsForCharts.map((p, i) => ({
    key: p,
    label: platformLabels[p] || p,
    colorVar: platformChartColors[i % platformChartColors.length],
    formatter: formatCompactNumber,
  }))

  const monitoringPieSeries = monitoringPlatformsForCharts.map((p, i) => ({
    key: platformLabels[p] || p,
    label: platformLabels[p] || p,
    colorVar: platformChartColors[i % platformChartColors.length],
    formatter: formatCompactNumber,
  }))

  const sentimentLineSeries = [
    { key: 'positive', label: 'Positive', colorVar: '--chart-3', formatter: (v) => formatNumber(v ?? 0) },
    { key: 'neutral', label: 'Neutral', colorVar: '--chart-muted', formatter: (v) => formatNumber(v ?? 0) },
    { key: 'negative', label: 'Negative', colorVar: '--chart-5', formatter: (v) => formatNumber(v ?? 0) },
    { key: 'unknown', label: 'Unknown', colorVar: '--chart-4', formatter: (v) => formatNumber(v ?? 0) },
    { key: 'mixed', label: 'Mixed', colorVar: '--chart-2', formatter: (v) => formatNumber(v ?? 0) },
  ]

  const formatSentimentShareLabel = (shareValue) => {
    const n = Number(shareValue)
    if (!Number.isFinite(n) || n < 0) return '0%'
    return `${n.toFixed(1)}%`
  }
  const sentimentPieSeries = [
    { key: 'Positive', label: 'Positive', colorVar: '--chart-3', formatter: formatSentimentShareLabel },
    { key: 'Neutral', label: 'Neutral', colorVar: '--chart-muted', formatter: formatSentimentShareLabel },
    { key: 'Negative', label: 'Negative', colorVar: '--chart-5', formatter: formatSentimentShareLabel },
    { key: 'Unknown', label: 'Unknown', colorVar: '--chart-4', formatter: formatSentimentShareLabel },
    { key: 'Mixed', label: 'Mixed', colorVar: '--chart-2', formatter: formatSentimentShareLabel },
  ]

  const timeOfDaySeriesMessages = TIME_OF_DAY_CHART_SERIES
  const timeOfDaySeriesAlerts = TIME_OF_DAY_CHART_SERIES

  const messagesTimeOfDayRows = useMemo(() => {
    if (!selectedClientId || !messagesReportFetchKey) return []
    if (clientMessagesTimeOfDayState.status !== 'succeeded') return []
    if (clientMessagesTimeOfDayState.fetchedFetchKey !== messagesReportFetchKey) return []
    return clientMessagesTimeOfDayState.rows ?? []
  }, [
    selectedClientId,
    messagesReportFetchKey,
    clientMessagesTimeOfDayState.status,
    clientMessagesTimeOfDayState.fetchedFetchKey,
    clientMessagesTimeOfDayState.rows,
  ])

  const alertsTimeOfDayRows = useMemo(() => {
    if (!selectedClientId || !messagesReportFetchKey) return []
    if (clientAlertsTimeOfDayState.status !== 'succeeded') return []
    if (clientAlertsTimeOfDayState.fetchedFetchKey !== messagesReportFetchKey) return []
    return clientAlertsTimeOfDayState.rows ?? []
  }, [
    selectedClientId,
    messagesReportFetchKey,
    clientAlertsTimeOfDayState.status,
    clientAlertsTimeOfDayState.fetchedFetchKey,
    clientAlertsTimeOfDayState.rows,
  ])

  const timeOfDayPlatformFilter = useMemo(
    () => (platformPulsePlatforms.size > 0 ? Array.from(platformPulsePlatforms) : null),
    [platformPulsePlatforms],
  )

  const apiMessagesTimeOfDay = useMemo(() => {
    if (!useApiMessagesMonitoring) return null
    return buildTimeOfDayFromRows(messagesTimeOfDayRows, {
      platformKeysFilter: timeOfDayPlatformFilter,
      socialNetworkTypes,
    })
  }, [useApiMessagesMonitoring, messagesTimeOfDayRows, timeOfDayPlatformFilter, socialNetworkTypes])

  const apiAlertsTimeOfDay = useMemo(() => {
    if (!useApiMessagesMonitoring) return null
    return buildTimeOfDayFromRows(alertsTimeOfDayRows, {
      platformKeysFilter: timeOfDayPlatformFilter,
      socialNetworkTypes,
    })
  }, [useApiMessagesMonitoring, alertsTimeOfDayRows, timeOfDayPlatformFilter, socialNetworkTypes])

  const effectiveTimeOfDayMessages = useApiMessagesMonitoring
    ? apiMessagesTimeOfDay?.entries ?? []
    : timeOfDayMessages

  const effectiveTimeOfDayAlerts = useApiMessagesMonitoring
    ? apiAlertsTimeOfDay?.entries ?? []
    : timeOfDayAlerts

  const heatmapReportRows = useMemo(() => {
    if (!selectedClientId || !messagesReportFetchKey) return []
    if (clientMessagesHeatmapState.status !== "succeeded") return []
    if (clientMessagesHeatmapState.fetchedFetchKey !== messagesReportFetchKey) return []
    return clientMessagesHeatmapState.rows ?? []
  }, [
    selectedClientId,
    messagesReportFetchKey,
    clientMessagesHeatmapState.status,
    clientMessagesHeatmapState.fetchedFetchKey,
    clientMessagesHeatmapState.rows,
  ])

  const apiMessageHeatmap = useMemo(() => {
    if (!useApiMessagesMonitoring) return null
    if (clientMessagesHeatmapState.status !== "succeeded") return null
    if (clientMessagesHeatmapState.fetchedFetchKey !== messagesReportFetchKey) return null
    return buildMessageHeatmapFromRows(heatmapReportRows, socialNetworkTypes, {
      startDate: dateRange.start,
      endDate: dateRange.end,
      platformKeysFilter: timeOfDayPlatformFilter,
    })
  }, [
    useApiMessagesMonitoring,
    heatmapReportRows,
    socialNetworkTypes,
    dateRange.start,
    dateRange.end,
    timeOfDayPlatformFilter,
    clientMessagesHeatmapState.status,
    clientMessagesHeatmapState.fetchedFetchKey,
    messagesReportFetchKey,
  ])

  const effectiveHeatmapWeeklyHours = useApiMessagesMonitoring
    ? apiMessageHeatmap?.weeklyHours ?? []
    : messageHeatmapHours

  const effectiveHeatmapWeeklyDays = useApiMessagesMonitoring
    ? apiMessageHeatmap?.weeklyDays ?? []
    : messageHeatmapDays

  const effectiveHeatmapWeeklyValues = useApiMessagesMonitoring
    ? apiMessageHeatmap?.weeklyValues ?? []
    : messageHeatmapValues

  const effectiveHeatmapCalendarDates = useApiMessagesMonitoring
    ? apiMessageHeatmap?.calendarDates ?? []
    : messageVolumeDaily.dates

  const effectiveHeatmapDailyHourly = useApiMessagesMonitoring
    ? apiMessageHeatmap?.hourlyDailyMatrix ?? { matrix: [], hours: [] }
    : messageVolumeDailyHourly

  const effectiveHeatmapMonthlyHourly = useApiMessagesMonitoring
    ? apiMessageHeatmap?.monthlyHourlyBundle ?? { matrix: [], hours: [], dates: [] }
    : messageHeatmapMonthlyHourly

  const timeOfDayMessageData = effectiveTimeOfDayMessages.map((entry) => ({
    name: entry.name,
    value: entry.value,
  }))

  const timeOfDayAlertData = effectiveTimeOfDayAlerts.map((entry) => ({
    name: entry.name,
    value: entry.value,
  }))

  const getTopTimeOfDay = (entries = []) =>
    entries.reduce((best, entry) => {
      const value = Number(entry?.value) || 0
      if (value <= 0) return best
      if (!best || value > best.value) return { ...entry, value }
      return best
    }, null)

  const topMessagesTimeOfDay = getTopTimeOfDay(effectiveTimeOfDayMessages)
  const topAlertsTimeOfDay = getTopTimeOfDay(effectiveTimeOfDayAlerts)
  const messagesTimeOfDayEmpty = !topMessagesTimeOfDay
  const alertsTimeOfDayEmpty = !topAlertsTimeOfDay

  const alertTotalSeries = [
    { key: 'Total', label: 'Total', colorVar: '--chart-1' },
  ]

  const alertCategoryCounts = (name) =>
    effectiveAlertCategories.find((entry) => entry.name === name)?.count || 0

  const previousAlertCategoryCount = (name) =>
    previousAlertCategories.find((entry) => entry.name === name)?.count || 0

  const ALERT_SUMMARY_TOTAL_ACCENT = 'var(--chart-2)'

  const handleMessageFilterClick = (segmentName) => {
    setMessageChannelFilter((prev) => (prev === segmentName ? 'all' : segmentName))
  }

  const handleSentimentFilterClick = (segmentName) => {
    setSentimentFilter((prev) => (prev === segmentName ? 'all' : segmentName))
  }

  const metricConfig = {
    response: {
      label: 'Response Time',
      tooltip: 'Average time to first reply per message',
      axisLabel: 'Hours',
      format: (value) => `${value.toFixed(1)}h`,
      better: 'lower',
      field: 'responseTimeHours',
    },
    resolution: {
      label: 'Resolution Rate',
      tooltip: '% of threads marked resolved within 24 hours',
      axisLabel: 'Percentage (%)',
      format: (value) => `${value.toFixed(1)}%`,
      better: 'higher',
      field: 'resolutionRatePercent',
    },
    volume: {
      label: 'Message Volume',
      tooltip: 'Total inbound messages received per day',
      axisLabel: 'Count',
      format: (value) => value.toFixed(0),
      better: 'higher',
      field: 'messageVolume',
    },
  }

  const competitorSeries = useMemo(() => {
    const selectedMetric = metricConfig[competitorMetric]
    const filtered = competitorPlatform === 'all'
      ? competitorDaily
      : competitorDaily.filter((row) => row.channel === competitorPlatform)

    const byDate = new Map()
    filtered.forEach((row) => {
      if (!byDate.has(row.date)) {
        byDate.set(row.date, {
          day: row.date,
          you: [],
          riviera: [],
          ocean: [],
          palm: [],
          sunset: [],
          marina: [],
        })
      }
      const entry = byDate.get(row.date)
      if (row.brand === 'you') entry.you.push(row[selectedMetric.field])
      if (row.brand === 'riviera') entry.riviera.push(row[selectedMetric.field])
      if (row.brand === 'ocean') entry.ocean.push(row[selectedMetric.field])
      if (row.brand === 'palm') entry.palm.push(row[selectedMetric.field])
      if (row.brand === 'sunset') entry.sunset.push(row[selectedMetric.field])
      if (row.brand === 'marina') entry.marina.push(row[selectedMetric.field])
    })

    const reduceMetric = (values) => {
      if (!values.length) return 0
      if (selectedMetric.field === 'messageVolume') {
        return values.reduce((sum, v) => sum + v, 0)
      }
      return values.reduce((sum, v) => sum + v, 0) / values.length
    }

    const data = Array.from(byDate.values())
      .sort((a, b) => new Date(a.day) - new Date(b.day))
      .slice(-30)
      .map((entry) => ({
        day: entry.day,
        you: reduceMetric(entry.you),
        riviera: reduceMetric(entry.riviera),
        ocean: reduceMetric(entry.ocean),
        palm: reduceMetric(entry.palm),
        sunset: reduceMetric(entry.sunset),
        marina: reduceMetric(entry.marina),
      }))

    const averages = (key) => data.reduce((sum, row) => sum + row[key], 0) / Math.max(data.length, 1)
    const youAvg = averages('you')
    const competitorAvg = ['riviera', 'ocean', 'palm', 'sunset', 'marina']
      .map(averages)
      .reduce((sum, val) => sum + val, 0) / 5

    const compareOrder = selectedMetric.better === 'lower'
      ? (a, b) => a.value - b.value
      : (a, b) => b.value - a.value

    const topEntry = ['riviera', 'ocean', 'palm', 'sunset', 'marina']
      .map((key) => ({
        key,
        name: competitorLegend.find((item) => item.key === key)?.name || key,
        value: averages(key),
      }))
      .sort(compareOrder)[0]

    const delta = selectedMetric.better === 'lower'
      ? ((competitorAvg - youAvg) / Math.max(competitorAvg, 1)) * 100
      : ((youAvg - competitorAvg) / Math.max(competitorAvg, 1)) * 100

    const peakDay = data.reduce((best, row) => {
      if (!best) return row
      return row.you > best.you ? row : best
    }, null)

    const insight = selectedMetric.better === 'lower'
      ? `You responded ${Math.abs(delta).toFixed(0)}% faster than competitors this month. ${peakDay?.day} saw a delay due to message spike (+60% volume).`
      : `You outperformed competitors by ${Math.abs(delta).toFixed(0)}% this month. ${peakDay?.day} showed the strongest activity across channels.`

    return {
      data,
      avg: {
        you: selectedMetric.format(youAvg),
        competitor: selectedMetric.format(competitorAvg),
        top: topEntry?.name || '?',
        topValue: selectedMetric.format(topEntry?.value || 0),
        delta: `${delta >= 0 ? '+' : '-'}${Math.abs(delta).toFixed(0)}%`,
        deltaPositive: delta >= 0,
      },
      axisLabel: selectedMetric.axisLabel,
      tooltip: selectedMetric.tooltip,
      insight,
    }
  }, [competitorMetric, competitorPlatform])

  const competitorChartSeries = useMemo(
    () =>
      competitorLegend.map((item) => ({
        key: item.key,
        label: item.name,
        colorVar: item.colorVar,
        formatter: metricConfig[competitorMetric].format,
      })),
    [competitorMetric]
  )
  const activeMonitoringSeries = messageChannelFilter === 'all'
    ? monitoringLineSeries
    : monitoringLineSeries.filter((item) => item.label === messageChannelFilter)
  const totalMonitoringShare = monitoringShareForDisplay.reduce((sum, item) => sum + item.value, 0)
  const getMonitoringShare = (name) =>
    baseMonitoringShare.find((entry) => entry.name === name) || { name, value: 0 }
  const getMonitoringSharePrevious = (name) =>
    monitoringSharePrevious?.find((entry) => entry.name === name) || { name, value: 0 }
  const formatMonitoringPercent = (value) =>
    totalMonitoringShare ? formatPercent(value / totalMonitoringShare, 1) : '0%'
  const topMessagePlatform = monitoringShareForDisplay?.length
    ? monitoringShareForDisplay.reduce((a, b) => (a.value >= b.value ? a : b), monitoringShareForDisplay[0])?.name
    : baseMonitoringShare?.length
      ? baseMonitoringShare.reduce((a, b) => (a.value >= b.value ? a : b), baseMonitoringShare[0])?.name
      : 'Instagram'
  const monitoringShareWithPrevious = monitoringShareForDisplay.map((item) => ({
    ...item,
    previous: getMonitoringSharePrevious(item.name)?.value || 0,
  }))
  const previousTotalMessages = totals.previous30.messages || 0
  const getSentimentShare = (name) =>
    baseSentimentShare.find((entry) => entry.name === name) || { name, value: 0 }
  const formatSentimentPercentFromCount = (count) => {
    const total = Number(sentimentTotalMessages) || 0
    const n = Number(count) || 0
    if (total <= 0 || n < 0) return '0%'
    return `${((n / total) * 100).toFixed(1)}%`
  }
  const sentimentPositiveShare = getSentimentShare('Positive')
  const sentimentNeutralShare = getSentimentShare('Neutral')
  const sentimentNegativeShare = getSentimentShare('Negative')
  const sentimentUnknownShare = getSentimentShare('Unknown')
  const sentimentMixedShare = getSentimentShare('Mixed')
  const sentimentPrevious = totals.previous30.sentiment || {}
  const sentimentPositiveCount = useApiMessagesMonitoring
    ? apiSentimentCharts?.sentimentCounts?.positive ?? 0
    : Math.round((sentimentTotalMessages * sentimentPositiveShare.value) / 100)
  const sentimentNeutralCount = useApiMessagesMonitoring
    ? apiSentimentCharts?.sentimentCounts?.neutral ?? 0
    : Math.round((sentimentTotalMessages * sentimentNeutralShare.value) / 100)
  const sentimentNegativeCount = useApiMessagesMonitoring
    ? apiSentimentCharts?.sentimentCounts?.negative ?? 0
    : Math.round((sentimentTotalMessages * sentimentNegativeShare.value) / 100)
  const sentimentUnknownCount = useApiMessagesMonitoring
    ? apiSentimentCharts?.sentimentCounts?.unknown ?? 0
    : Math.round((sentimentTotalMessages * sentimentUnknownShare.value) / 100)
  const sentimentMixedCount = useApiMessagesMonitoring
    ? apiSentimentCharts?.sentimentCounts?.mixed ?? 0
    : Math.round((sentimentTotalMessages * sentimentMixedShare.value) / 100)
  const activeSentimentSeries = sentimentFilter === 'all'
    ? sentimentLineSeries
    : sentimentLineSeries.filter((item) => item.label === sentimentFilter)
  const formatDeltaPercent = (current, previous) => {
    if (!previous) return '0% vs previous period'
    const delta = Math.round(((current - previous) / previous) * 100)
    return `${delta >= 0 ? '+' : ''}${delta}% vs previous period`
  }

  const campaignPerformance = [
    { campaign: 'Winter Escape Package', engagementRate: 0.031, linkClicks: 1240, revenue: 48500, platform: 'instagram' },
    { campaign: 'Spa Weekend Launch', engagementRate: 0.028, linkClicks: 980, revenue: 39200, platform: 'facebook' },
    { campaign: 'Holiday Dining Push', engagementRate: 0.026, linkClicks: 860, revenue: 31800, platform: 'instagram' },
    { campaign: 'Suite Upgrade Promo', engagementRate: 0.024, linkClicks: 740, revenue: 28400, platform: 'facebook' },
    { campaign: 'Local Getaway Retarget', engagementRate: 0.022, linkClicks: 690, revenue: 25100, platform: 'instagram' },
    { campaign: 'B2B Corporate Retreats', engagementRate: 0.019, linkClicks: 420, revenue: 18600, platform: 'linkedin' },
    { campaign: 'Executive Travel Packages', engagementRate: 0.017, linkClicks: 380, revenue: 15200, platform: 'linkedin' },
    { campaign: 'Last-Minute Deals Promo', engagementRate: 0.025, linkClicks: 620, revenue: 22800, platform: 'twitter' },
    { campaign: 'Weekend Brunch Campaign', engagementRate: 0.023, linkClicks: 520, revenue: 19200, platform: 'twitter' },
    { campaign: 'Behind-the-Scenes Tour', engagementRate: 0.032, linkClicks: 1350, revenue: 52000, platform: 'tiktok' },
    { campaign: 'Viral Cooking Demo', engagementRate: 0.029, linkClicks: 1080, revenue: 41800, platform: 'tiktok' },
  ]

  const filteredTopPostsData = useMemo(() => {
    let filtered = periodScopedTopPostsData
    if (platformPulsePlatforms.size > 0) {
      filtered = filtered.filter((post) => platformPulsePlatforms.has(post.platform))
    }
    if (topPostsPlatform !== 'all') {
      filtered = filtered.filter((post) => topPostMatchesPlatformFilter(post, topPostsPlatform))
    }
    return filtered.slice().sort((a, b) => {
      const aVal = getTopPostSortValue(a, topPostsRankBy)
      const bVal = getTopPostSortValue(b, topPostsRankBy)
      const cmp = aVal - bVal
      return topPostsRankMode === 'top' ? -cmp : cmp
    })
  }, [periodScopedTopPostsData, topPostsPlatform, topPostsRankBy, topPostsRankMode, platformPulsePlatforms])

  const topPostsTotalAvailable = filteredTopPostsData.length
  const topPostsShowLoadMore =
    topPostsTotalAvailable > topPostsVisibleCount
  const isPostTopicComingSoon = true
  const isPostTopicView = !isPostTopicComingSoon && topPostsView === 'postTopics'
  const postPerformanceGroups = isPostTopicView ? POST_TOPIC_PERFORMANCE_GROUPS : POST_PERFORMANCE_GROUPS
  const rankedTopicPerformance = useMemo(() => {
    const topicMetric = POST_TOPIC_RANK_METRICS.some((metric) => metric.key === topPostsRankBy)
      ? topPostsRankBy
      : 'engagements'
    return getRankedTopicPerformance([], {
      metric: topicMetric,
      performanceGroup: topPostsRankMode,
      platform: 'all',
      platformPulsePlatforms,
    })
  }, [platformPulsePlatforms, topPostsRankBy, topPostsRankMode])
  const totalCampaignEvents = effectiveCampaignEventData.length
  const totalCampaignPosts = useMemo(
    () => sumCampaignChartPostCount(effectiveCampaignEventData),
    [effectiveCampaignEventData],
  )

  const derivedInsightCards = useMemo(() => {
    const formatDelta = (curr, prev) => {
      if (!prev) return '0% vs previous period'
      const delta = Math.round(((curr - prev) / prev) * 100)
      return `${delta >= 0 ? '+' : ''}${delta}% vs previous period`
    }
    const cards = []
    let id = 0
    const add = (section, title, context, severity, iconType, viewCta, actionCta) => {
      cards.push({ id: `insight-${++id}`, section, title, context, severity, iconType, viewCta, actionCta })
    }
    add('audience', 'Audience distribution', `${topMessagePlatform} drives most of your inbound conversations. Focus your content and ads there to reach the largest share of your audience.`, 'low', 'audience', 'View Demographics', 'Add to Goals')
    add('campaign', 'Campaign engagement', `Engagement is ${formatDelta(totals.last30.engagements, totals.previous30.engagements)}. This shows how actively your audience interacts with your campaigns—review which creatives or targeting drove the change.`, 'medium', 'campaign', 'Analyze Campaigns', 'Add to Goals')
    add('campaign', 'Campaign events', `${totalCampaignEvents} events ran with ${formatNumber(totals.last30.impressions)} impressions. Events help you track campaign milestones and measure performance across the funnel.`, 'low', 'campaign', 'View Events', 'Add to Report')
    add('inbox', 'Positive sentiment', `${formatPercent(sentimentPositiveShare.value, 1)} of messages show positive sentiment. Strong sentiment indicates customer satisfaction and helps you spot where your brand is resonating.`, 'low', 'inbox', 'Go to Inbox', 'Add to Report')
    add('inbox', 'Alert volume', `Alert volume is ${formatDelta(totals.last30.alerts, totals.previous30.alerts)}. Higher volumes often need faster attention—check your inbox to stay on top of urgent items and avoid missed replies.`, totals.last30.alerts > (totals.previous30.alerts || 0) ? 'high' : 'medium', 'inbox', 'Review Alerts', 'Go to Inbox')
    add('inbox', 'Platform activity', `${topMessagePlatform} drives the highest share of inbound conversations. Prioritize response time here to maintain engagement where your audience is most active.`, 'low', 'inbox', 'Go to Inbox', 'Add to Report')
    add('inbox', 'Prioritize responses', `${topMessagePlatform} has the highest message volume. Responding here first reduces wait times and keeps customers satisfied when demand is strongest.`, 'high', 'inbox', 'Go to Inbox', 'Add to Report')
    add('inbox', 'Staff coverage', 'Message volume spikes in late‑day hours. Adding staff during these peaks can reduce response times and lower the risk of backlog or missed alerts.', 'high', 'inbox', 'Review Alerts', 'Go to Inbox')
    add('paid', 'Paid media ROAS', `Your ads return ${paidMediaTotals.roas.toFixed(1)}x for every dollar spent. ROAS above 1x means profitable campaigns—consider scaling winning ad sets to grow revenue.`, 'low', 'paid', 'View Paid Media', 'Optimize Ads')
    add('paid', 'Paid media revenue', `Revenue of ${formatCompactNumber(paidMediaTotals.revenue)} from ${formatCompactNumber(paidMediaTotals.spend)} in spend. Tracking this ratio helps ensure your paid campaigns stay profitable and scalable.`, 'medium', 'paid', 'View Paid Media', 'Optimize Ads')
    add('paid', 'Paid media performance', `Paid campaigns are performing at ${paidMediaTotals.roas.toFixed(1)}x ROAS. Stable or improving ROAS suggests your targeting and creative are working well.`, 'low', 'paid', 'View Paid Media', 'Optimize Ads')
    add('paid', 'Focus on ROAS', `Your best ad sets average ${paidMediaTotals.roas.toFixed(1)}x ROAS. Shifting budget toward these performers can improve overall campaign efficiency and returns.`, 'medium', 'paid', 'View Paid Media', 'Optimize Ads')
    add('follower', 'Engagement rate', `Engagement rate is ${formatPercent(engagementRateValue, 1)}. This measures how actively your audience interacts with your content—benchmark against industry averages to gauge performance.`, 'low', 'growth', 'Analyze Top Posts', 'Add to Goals')
    add('follower', 'Engagement growth', `Engagements are ${formatDelta(totals.last30.engagements, totals.previous30.engagements)}. This reflects how actively your audience interacts with your content—review top posts to understand what’s driving the change.`, 'medium', 'growth', 'Analyze Top Posts', 'Add to Goals')
    if (selectedGoalsList.length > 0) {
      const behind = (goalsStatusSummary.behind || 0) + (goalsStatusSummary['needs-attention'] || 0)
      const atRisk = goalsStatusSummary['at-risk'] || 0
      const exceeded = goalsStatusSummary.exceeded || 0
      const behindOrAtRisk = behind + atRisk
      add('follower', 'Goal progress', `${goalsStatusSummary['on-track']} goals on track, ${behindOrAtRisk} behind, ${exceeded} exceeded. Focus on the behind goals to keep your strategy aligned and on schedule.`, behindOrAtRisk > 0 ? 'high' : 'low', 'growth', 'View Goals', 'Add to Goals')
    }
    return cards
  }, [
    totals,
    sentimentPositiveShare,
    topMessagePlatform,
    paidMediaTotals,
    engagementRateValue,
    selectedGoalsList,
    goalsStatusSummary,
    totalCampaignEvents,
  ])

  const isChartDataReadyForExport = useMemo(
    () => {
      const baseReady = createDashboardChartDataReadiness({
        selectedClientId,
        slices: {
          socialPerformance: clientSocialPerformanceState,
          competitivePerformance: clientCompetitivePerformanceState,
          topPosts: clientTopPostsState,
          postHeatmap: clientPostHeatmapState,
          campaignPerformance: clientCampaignPerformanceState,
          storyPerformance: clientStoryPerformanceState,
          paidPerformance: clientPaidPerformanceState,
          paidMediaPerformance: clientPaidMediaPerformanceState,
          demographicsFollowers: clientDemographicsFollowersState,
          messagesReport: clientMessagesReportState,
          sentimentsReport: clientSentimentsReportState,
          alertsReport: clientAlertsReportState,
          messagesTimeOfDay: clientMessagesTimeOfDayState,
          alertsTimeOfDay: clientAlertsTimeOfDayState,
          messagesHeatmap: clientMessagesHeatmapState,
          ytdPagePerformance: clientYtdPagePerformanceState,
        },
        fetchKeys: {
          socialPerformance: socialPerformanceFetchKey,
          competitivePerformance: competitivePerformanceFetchKey,
          topPosts: topPostsFetchKey,
          postHeatmap: postHeatmapFetchKey,
          campaignPerformance: campaignPerformanceFetchKey,
          storyPerformance: storyPerformanceFetchKey,
          paidPerformance: paidPerformanceFetchKey,
          paidMediaPerformance: paidMediaPerformanceFetchKey,
          demographicsFollowers: demographicsFollowersFetchKey,
          messagesReport: messagesReportFetchKey,
          sentimentsReport: messagesReportFetchKey,
          alertsReport: messagesReportFetchKey,
          messagesTimeOfDay: messagesReportFetchKey,
          alertsTimeOfDay: messagesReportFetchKey,
          messagesHeatmap: messagesReportFetchKey,
          ytdPagePerformance: goalsYtdFetchKey,
        },
      })

      return (chartId) => {
        if (!baseReady(chartId)) return false
        const run = exportContextRunRef.current
        const allData = run?.reportDataScope === REPORT_DATA_SCOPE.ALL_DATA
          || (run?.allDataChartIds || []).includes(chartId)
        if (!allData || !selectedClientId) return true

        if (chartId === 'monthlySocialPerformance' || chartId === 'platformPerformanceComparison') {
          const start = formatSocialPerformanceDate(dateRange.start)
          const end = formatSocialPerformanceDate(dateRange.end)
          const rangeMode = dashboardReportRangeMode(dateRangePreset)
          const cache = clientSocialPerformanceState.byFetchKey || {}
          return ['all', 'organic', 'paid'].every((source) => {
            const key = buildSocialPerformanceFetchKey(
              selectedClientId,
              start,
              end,
              tableSourceToAggregationMode(source),
              rangeMode,
            )
            return Boolean(cache[key])
          })
        }

        if (
          chartId === 'topPerformingAds'
          || chartId === 'topCampaigns'
          || chartId === 'topObjectives'
          || chartId === 'advertisingPerformance'
        ) {
          const start = formatPaidPerformanceDate(dateRange.start)
          const end = formatPaidPerformanceDate(dateRange.end)
          const sortBy = paidUiSortToApiSort(paidWidgetSortBy)
          const loaded = clientPaidPerformanceState.loadedKeys || {}
          return PAID_REPORT_LEVELS.every((level) => {
            const key = buildPaidPerformanceFetchKey(selectedClientId, start, end, {
              level,
              sortBy,
              id: 'all',
            })
            return Boolean(loaded[key])
          })
        }

        return true
      }
    },
    [
      selectedClientId,
      clientSocialPerformanceState,
      clientCompetitivePerformanceState,
      clientTopPostsState,
      clientPostHeatmapState,
      clientCampaignPerformanceState,
      clientStoryPerformanceState,
      clientPaidPerformanceState,
      clientPaidMediaPerformanceState,
      clientDemographicsFollowersState,
      clientMessagesReportState,
      clientSentimentsReportState,
      clientAlertsReportState,
      clientMessagesTimeOfDayState,
      clientAlertsTimeOfDayState,
      clientMessagesHeatmapState,
      clientYtdPagePerformanceState,
      socialPerformanceFetchKey,
      competitivePerformanceFetchKey,
      topPostsFetchKey,
      messagesReportFetchKey,
      postHeatmapFetchKey,
      campaignPerformanceFetchKey,
      storyPerformanceFetchKey,
      paidPerformanceFetchKey,
      paidMediaPerformanceFetchKey,
      demographicsFollowersFetchKey,
      goalsYtdFetchKey,
      dateRange.start,
      dateRange.end,
      dateRangePreset,
      paidWidgetSortBy,
    ],
  )

  isChartDataReadyRef.current = isChartDataReadyForExport

  const isChartDataReadyLive = useCallback((chartId) => {
    return isChartDataReadyRef.current(chartId)
  }, [])

  const buildExportDataContext = useCallback(() => {
    const run = exportContextRunRef.current
    const useAllFilters = shouldUseAllDashboardFiltersForExport(
      run.reportDataScope,
      run.widgetDataScopes,
    )
    const topPostsUseAllData = (run.allDataChartIds || []).includes("topPosts")
    const topStoriesUseAllData = (run.allDataChartIds || []).includes("topStories")
    const exportTopStories = topStoriesUseAllData
      ? filteredTopStoriesData
      : (topStoriesVisibleForExport.length
          ? topStoriesVisibleForExport
          : filteredTopStoriesData.slice(0, 10))
    const exportTopPostsBase = topPostsUseAllData
      ? periodScopedTopPostsData.slice().sort((a, b) => {
          const aVal = getTopPostSortValue(a, topPostsRankBy)
          const bVal = getTopPostSortValue(b, topPostsRankBy)
          const cmp = aVal - bVal
          return topPostsRankMode === "top" ? -cmp : cmp
        })
      : filteredTopPostsData
    const exportTopPosts = topPostsUseAllData
      ? exportTopPostsBase
      : exportTopPostsBase.slice(0, topPostsVisibleCount)
    const exportPaidTopAds = useAllFilters ? resolvedPaidTopAds : filteredPaidTopAdsForExport
    const exportPaidCampaigns = useAllFilters
      ? resolvedPaidCampaigns
      : resolvedPaidCampaigns.filter((row) => {
          const showAll =
            !topAdsPlatforms ||
            topAdsPlatforms.size === 0 ||
            topAdsPlatforms.has('all')
          if (showAll) return true
          return topAdsPlatforms.has(row.platformKey)
        })
    const exportPaidPerformanceCampaigns = useAllFilters
      ? resolvedPaidPerformanceCampaigns
      : resolvedPaidPerformanceCampaigns.filter((row) => {
          const showAll =
            !topAdsPlatforms ||
            topAdsPlatforms.size === 0 ||
            topAdsPlatforms.has('all')
          if (showAll) return true
          return topAdsPlatforms.has(row.platformKey)
        })
    const exportPlatformSeries = useAllFilters ? platformSeries : platformSeriesForComparison
    let exportPlatformComparisonRows = resolvedPlatformComparisonData
    if (
      useAllFilters &&
      selectedClientId &&
      Array.isArray(socialPerformancePlatforms) &&
      socialPerformancePlatforms.length
    ) {
      exportPlatformComparisonRows = buildPlatformPerformanceComparisonData(
        socialPerformancePlatforms,
        socialPerformanceChartOptions,
      )
    }

    let exportMonthlySocialRowsBySource = monthlySocialRowsBySource
    if (useAllFilters && selectedClientId) {
      const cache = clientSocialPerformanceState.byFetchKey || {}
      const start = formatSocialPerformanceDate(dateRange.start)
      const end = formatSocialPerformanceDate(dateRange.end)
      const rangeMode = dashboardReportRangeMode(dateRangePreset)
      const connectedKeys = Array.isArray(connectedPropertyPlatformKeysList)
        ? connectedPropertyPlatformKeysList
        : null
      const unfilteredOptions = {
        socialNetworkTypes,
        aggregationSource: 'all',
        ...(connectedKeys ? { platformKeysFilter: connectedKeys } : {}),
      }
      const rowsForSource = (source) => {
        const cacheKey = buildSocialPerformanceFetchKey(
          selectedClientId,
          start,
          end,
          tableSourceToAggregationMode(source),
          rangeMode,
        )
        const cached = cache[cacheKey]
        if (cached?.raw != null) {
          const platforms = normalizeSocialPerformancePayload(cached.raw, socialNetworkTypes, {
            startDate: start,
            endDate: end,
            aggregationMode: tableSourceToAggregationMode(source),
          })
          if (platforms.length > 0) {
            return buildMonthlySocialTable(platforms, socialNetworkTypes, {
              ...unfilteredOptions,
              aggregationSource: source,
            }).rows
          }
        }
        return monthlySocialRowsBySource[source] || []
      }
      exportMonthlySocialRowsBySource = {
        all: rowsForSource('all'),
        organic: rowsForSource('organic'),
        paid: rowsForSource('paid'),
      }
    }

    const ctx = {
      platformSeries: exportPlatformSeries,
      platformComparisonRows: exportPlatformComparisonRows,
      campaignPerformance,
      campaignEventRows: effectiveCampaignEventData,
      campaignBarSeries: campaignSeries.map((s) => ({ key: s.key, label: s.label })),
      monthlySocialColumns: resolvedMonthlySocial.columns,
      monthlySocialRows: useAllFilters
        ? (exportMonthlySocialRowsBySource.all || resolvedMonthlySocial.rows)
        : resolvedMonthlySocial.rows,
      monthlySocialSource,
      monthlySocialComparePeriod,
      monthlySocialRowsBySource: exportMonthlySocialRowsBySource,
      monthlySocialConnectedPlatformKeys: Array.isArray(connectedPropertyPlatformKeysList)
        ? connectedPropertyPlatformKeysList
        : null,
      competitorDaily,
      socialPerformanceByTab: resolvedSocialPerformanceRows,
      socialPerformanceTab,
      competitorBenchmarkInsight:
        resolvedSocialPerformanceRows[socialPerformanceTab]?.strategicInsight ||
        competitorSeries?.insight ||
        '',
      competitorBenchmarkAvg: competitorSeries?.avg,
      competitorChartData: competitorSeries?.data,
      topPosts: exportTopPosts,
      topPostsTotalAvailable: exportTopPostsBase.length,
      propertyChartTimezone,
      topPostsVisibleCount,
      topStories: exportTopStories,
      topStoriesTotalAvailable: filteredTopStoriesData.length,
      paidMediaTotals,
      paidMediaSeries,
      paidMediaRangeLabel: formatRangeLabel(dateRange),
      paidMediaRevenueDelta,
      paidMediaTopAds: exportPaidTopAds,
      paidMediaRawAds: paidPerformanceApiReady ? (clientPaidPerformanceState.ads ?? []) : [],
      paidMediaRawCampaigns: paidPerformanceApiReady ? (clientPaidPerformanceState.campaigns ?? []) : [],
      paidMediaObjectives: paidPerformanceApiReady ? (clientPaidPerformanceState.objectives ?? []) : [],
      paidMediaCampaigns: exportPaidCampaigns,
      paidMediaPerformanceCampaigns: exportPaidPerformanceCampaigns,
      audienceAgeGender,
      audienceTopCities,
      exportAudienceAgeGender:
        audienceDemographicData?.ageGender ??
        (Array.isArray(audienceAgeGender) ? audienceAgeGender : []),
      exportAudienceTopCities: effectiveAudienceTopCities,
      exportAudienceTopCountries: displayAudienceTopCountriesChart,
      audienceAgeGenderSummary,
      audienceAgeGenderSourceLabel,
      audienceTopCitiesInsight,
      audienceTopCountriesInsight,
      audienceGeographicInsight,
      audienceGeographic: audienceDemographicData?.geographic ?? [],
      monitoringSeries: monitoringSeriesForDisplay,
      monitoringShare: baseMonitoringShare,
      monitoringLineSeries: monitoringLineSeries.map((s) => ({ key: s.key, label: s.label, colorVar: s.colorVar })),
      monitoringPieSeries,
      totalMonitoringShare,
      topMessagePlatform,
      messagesKpis: [
        { label: "Total", value: formatNumber(totalMonitoringShare) },
        ...baseMonitoringShare.slice(0, 5).map((item) => ({
          label: item.name,
          value: formatNumber(item.value ?? 0),
        })),
      ],
      sentimentSeries: sentimentSeriesForDisplay,
      sentimentShare: baseSentimentShare,
      sentimentCounts: {
        total: sentimentTotalMessages,
        positive: sentimentPositiveCount,
        neutral: sentimentNeutralCount,
        negative: sentimentNegativeCount,
        unknown: sentimentUnknownCount,
        mixed: sentimentMixedCount,
      },
      sentimentPanelTitle: "Message Sentiment",
      sentimentPanelSubtitle: "AI-powered sentiment categorization",
      sentimentTotalMessages,
      sentimentPositivePercent: positivePercent,
      sentimentNegativePercent: negativePercent,
      sentimentSpikeDates,
      sentimentLineSeries: sentimentLineSeries.map((s) => ({ key: s.key, label: s.label, colorVar: s.colorVar })),
      sentimentPieSeries,
      netSentiment,
      sentimentKpis: [
        { label: "Total", value: formatNumber(sentimentTotalMessages) },
        { label: "Positive", value: formatNumber(sentimentPositiveCount), meta: `(${formatSentimentPercentFromCount(sentimentPositiveCount)})` },
        { label: "Neutral", value: formatNumber(sentimentNeutralCount), meta: `(${formatSentimentPercentFromCount(sentimentNeutralCount)})` },
        { label: "Negative", value: formatNumber(sentimentNegativeCount), meta: `(${formatSentimentPercentFromCount(sentimentNegativeCount)})` },
        { label: "Unknown", value: formatNumber(sentimentUnknownCount), meta: `(${formatSentimentPercentFromCount(sentimentUnknownCount)})` },
        { label: "Mixed", value: formatNumber(sentimentMixedCount), meta: `(${formatSentimentPercentFromCount(sentimentMixedCount)})` },
      ],
      alertsSeries: useApiAlerts
        ? (apiAlertsCharts?.alertsSeries ?? [])
        : filteredAlertsSeries,
      alertsChartData: effectiveAlertsChartData,
      alertCategories: effectiveAlertCategories,
      alertCategorySeries: effectiveAlertCategorySeries,
      alertBarSeries: effectiveAlertCategorySeries.map((s) => ({ key: s.key, label: s.label, colorVar: s.colorVar })),
      alertCategoryPieSeries: effectiveAlertCategoryPieSeries,
      totalAlerts,
      previousTotalAlerts,
      alertSpikeDates,
      alertsPanelTitle: "Alerts",
      alertsPanelSubtitle: "Customer service situations emailed to on-property staff",
      alertsKpis: [
        {
          label: "Total Alerts",
          value: formatNumber(totalAlerts),
          footnote: formatDeltaPercent(totalAlerts, previousTotalAlerts),
          colorVar: "--chart-2",
        },
        ...effectiveAlertCategories.slice(0, 5).map((c) => {
          const pieSeries = effectiveAlertCategoryPieSeries.find(
            (s) => s.label === c.name || s.key === c.name,
          )
          return {
            label: c.name,
            value: formatNumber(c.count ?? 0),
            footnote: formatDeltaPercent(
              c.count ?? 0,
              useApiAlerts ? c.previous ?? 0 : c.previous ?? previousAlertCategoryCount(c.name),
            ),
            colorVar: pieSeries?.colorVar || "--chart-2",
          }
        }),
      ],
      monthlySocialStrategicInsight: "",
      timeOfDayMessages: timeOfDayMessageData,
      timeOfDayAlerts: timeOfDayAlertData,
      timeOfDayMessageSeries: timeOfDaySeriesMessages,
      timeOfDayAlertSeries: timeOfDaySeriesAlerts,
      timeOfDayPanelTitle: "Time of Day",
      timeOfDayPanelSubtitle: "",
      messageHeatmapCalendarDates: effectiveHeatmapCalendarDates,
      messageHeatmapDays: effectiveHeatmapWeeklyDays,
      messageHeatmapHours: effectiveHeatmapWeeklyHours,
      messageHeatmapValues: effectiveHeatmapWeeklyValues,
      messageHeatmapWeeklyDays: effectiveHeatmapWeeklyDays,
      messageHeatmapWeeklyHours: effectiveHeatmapWeeklyHours,
      messageHeatmapWeeklyValues: effectiveHeatmapWeeklyValues,
      messageHeatmapHourlyDailyMatrix: effectiveHeatmapDailyHourly,
      messageHeatmapMonthlyHourlyBundle: effectiveHeatmapMonthlyHourly,
      activeGoals: committedGoalsList.map((g) => ({
        id: g.id,
        name: g.name,
        platform: g.platform,
        source: g.source,
        format: g.format,
        target: g.target,
        current: g.current,
        latestValue: g.latestValue,
        period: g.period,
        periodStart: g.periodStart,
        durationLabel: g.durationLabel,
        periodEndLabel: g.periodEndLabel,
        status: g.status,
        progressRatio: g.progressRatio,
        goalProgressLabel: g.goalProgressLabel,
        isPeriodExpired: g.isPeriodExpired,
      })),
      goalsStatusSummary,
      goalsStrategyStatus,
      goalsTimeRemainingText,
      postHeatmap: apiPostingHeatmap,
      optimalPostingSeries,
      exportAllDataChartIds: run.allDataChartIds || [],
      paidWidgetView,
      paidWidgetSortBy: paidExportSortRef.current?.sortBy || paidWidgetSortBy,
      paidWidgetSortDir: paidExportSortRef.current?.sortDir || 'desc',
      paidPerformanceTableSortBy: paidExportSortRef.current?.tableSortBy || 'impressions',
      paidPerformanceTableSortDir: paidExportSortRef.current?.tableSortDir || 'desc',
    }
    ctx.messagesInsight = buildChartInsightText("messages", ctx)
    ctx.sentimentInsight = buildChartInsightText("messageSentiment", ctx)
    ctx.alertsInsight = buildChartInsightText("alerts", ctx)
    ctx.goalsInsight = buildChartInsightText("goalPerformance", ctx)
    return ctx
  },
    [
      platformSeriesForComparison,
      resolvedPlatformComparisonData,
      campaignPerformance,
      effectiveCampaignEventData,
      campaignSeries,
      resolvedMonthlySocial,
      monthlySocialSource,
      monthlySocialComparePeriod,
      monthlySocialRowsBySource,
      competitorDaily,
      competitorSeries,
      resolvedSocialPerformanceRows,
      socialPerformanceTab,
      filteredTopPostsData,
      paidMediaTotals,
      paidMediaSeries,
      paidMediaRevenueDelta,
      dateRange,
      filteredPaidTopAdsForExport,
      resolvedPaidTopAds,
      resolvedPaidCampaigns,
      resolvedPaidPerformanceCampaigns,
      topAdsPlatforms,
      audienceAgeGender,
      audienceTopCities,
      audienceDemographicData,
      effectiveAudienceTopCities,
      displayAudienceTopCountriesChart,
      monitoringSeriesForDisplay,
      baseMonitoringShare,
      monitoringLineSeries,
      monitoringPieSeries,
      totalMonitoringShare,
      sentimentSeriesForDisplay,
      baseSentimentShare,
      sentimentLineSeries,
      sentimentPieSeries,
      netSentiment,
      sentimentTotalMessages,
      sentimentPositiveCount,
      sentimentNeutralCount,
      sentimentNegativeCount,
      sentimentUnknownCount,
      sentimentMixedCount,
      positivePercent,
      negativePercent,
      sentimentSpikeDates,
      alertSpikeDates,
      previousTotalAlerts,
      topMessagePlatform,
      audienceAgeGenderSummary,
      audienceAgeGenderSourceLabel,
      audienceTopCitiesInsight,
      audienceTopCountriesInsight,
      audienceGeographicInsight,
      currentSocialPerformance,
      filteredAlertsSeries,
      useApiAlerts,
      apiAlertsCharts,
      effectiveAlertsChartData,
      effectiveAlertCategories,
      effectiveAlertCategorySeries,
      effectiveAlertCategoryPieSeries,
      totalAlerts,
      timeOfDayMessageData,
      timeOfDayAlertData,
      timeOfDaySeriesMessages,
      timeOfDaySeriesAlerts,
      messageHeatmapDays,
      messageHeatmapHours,
      effectiveHeatmapWeeklyDays,
      effectiveHeatmapWeeklyHours,
      effectiveHeatmapWeeklyValues,
      effectiveHeatmapDailyHourly,
      effectiveHeatmapMonthlyHourly,
      committedGoalsList,
      goalsStatusSummary,
      goalsStrategyStatus,
      goalsTimeRemainingText,
      apiPostingHeatmap,
      optimalPostingSeries,
      periodScopedTopPostsData,
      filteredTopPostsData,
      resolvedTopStoriesData,
      filteredTopStoriesData,
      topStoriesVisibleForExport,
      topPostsRankBy,
      topPostsRankMode,
      topPostsVisibleCount,
      socialPerformancePlatforms,
      socialPerformanceChartOptions,
      connectedPropertyPlatformKeysList,
      platformSeries,
      selectedClientId,
      clientSocialPerformanceState.byFetchKey,
      dateRangePreset,
      socialNetworkTypes,
      paidWidgetView,
      paidWidgetSortBy,
      paidPerformanceApiReady,
      clientPaidPerformanceState.ads,
      clientPaidPerformanceState.campaigns,
      clientPaidPerformanceState.objectives,
    ],
  )

  buildExportDataContextRef.current = buildExportDataContext
  const buildExportDataContextLive = useCallback(() => buildExportDataContextRef.current(), [])

  const prefetchAllClientReportData = useCallback(() => {
    if (!selectedClientId) return
    const clientId = String(selectedClientId)
    const range = { startDate: dateRange.start, endDate: dateRange.end }
    const rangeMode = dashboardReportRangeMode(dateRangePreset)
    const exportAllData = reportDataScope === REPORT_DATA_SCOPE.ALL_DATA
    dispatch(fetchClientMessagesReport({ clientId, ...range }))
    dispatch(fetchClientSentimentsReport({ clientId, ...range }))
    dispatch(fetchClientAlertsReport({ clientId, ...range }))
    dispatch(fetchClientMessagesTimeOfDay({ clientId, ...range }))
    dispatch(fetchClientAlertsTimeOfDay({ clientId, ...range }))
    dispatch(fetchClientMessagesHeatmap({ clientId, ...range }))
    dispatch(fetchClientDemographicsFollowers({ clientId, ...range }))
    dispatch(fetchClientPostHeatmap({ clientId, ...range }))
    dispatch(fetchClientTopPosts({
      clientId,
      ...range,
      sortDirection: 'desc',
      rangeMode,
    }))
    dispatch(fetchClientStoryPerformance({ clientId, ...range }))
    dispatch(fetchClientCampaignPerformance({ clientId, ...range }))
    dispatch(fetchClientSocialPerformance({
      clientId,
      ...range,
      source: monthlySocialSource,
      rangeMode,
    }))
    if (exportAllData) {
      ;['all', 'organic', 'paid'].forEach((source) => {
        if (source === monthlySocialSource) return
        dispatch(fetchClientSocialPerformance({
          clientId,
          ...range,
          source,
          rangeMode,
          prefetch: true,
        }))
      })
    }
    dispatch(fetchClientCompetitivePerformance({
      clientId,
      ...range,
      rangeMode,
    }))
    const paidSortBy = paidUiSortToApiSort(paidWidgetSortBy)
    const currentPaidLevel = paidViewToLevel(paidWidgetView)
    const paidLimit = exportAllData ? PAID_REPORT_ALL_DATA_PAGE_SIZE : 10
    dispatch(fetchClientPaidPerformance({
      clientId,
      ...range,
      level: currentPaidLevel,
      id: 'all',
      sortBy: paidSortBy,
      limit: paidLimit,
      force: exportAllData,
    }))
    if (exportAllData) {
      PAID_REPORT_LEVELS.forEach((level) => {
        if (level === currentPaidLevel) return
        dispatch(fetchClientPaidPerformance({
          clientId,
          ...range,
          level,
          id: 'all',
          sortBy: paidSortBy,
          limit: paidLimit,
          prefetch: true,
          force: true,
        }))
      })
    }
    dispatch(fetchClientPaidMediaPerformance({ clientId, ...range }))
    dispatch(fetchMetricGoals())
    if (selectedClientId) {
      dispatch(fetchClientMetricGoals({ clientId: String(selectedClientId) }))
      dispatch(
        fetchClientYtdPagePerformance({
          clientId: String(selectedClientId),
          startDate: dateRange.start,
          endDate: dateRange.end,
          rangeMode,
        }),
      )
    }
  }, [
    dispatch,
    selectedClientId,
    dateRange.start,
    dateRange.end,
    dateRangePreset,
    monthlySocialSource,
    paidWidgetView,
    paidWidgetSortBy,
    reportDataScope,
  ])

  const exportReportDownload = useCallback(async () => {
    if (draftExportCharts.size === 0 || exportBusy) return

    const reportName = exportName.trim() || getDefaultReportName()
    const safeName = sanitizeFileName(reportName) || 'Dashboard Report'
    let chartIdList = [...draftExportCharts].filter((id) => exportableChartIds.includes(id))
    if (reportDataScope === REPORT_DATA_SCOPE.ALL_DATA) {
      ALWAYS_VISIBLE_SOCIAL_WIDGET_IDS.forEach((id) => {
        if (exportableChartIds.includes(id)) chartIdList.push(id)
      })
      if (exportableChartIds.includes('goalPerformance')) {
        chartIdList.push('goalPerformance')
      }
      exportableChartIds.forEach((id) => {
        chartIdList.push(id)
      })
      chartIdList = [...new Set(chartIdList)]
    }
    chartIdList = expandPaidMediaChartIdsForAllData(chartIdList, {
      reportDataScope,
      widgetDataScopes,
      enabledPaidChartIds: exportableChartIds.filter((id) =>
        PAID_MEDIA_EXPORT_CHART_IDS.includes(id),
      ),
    })
    const paidExportCount = chartIdList.filter((id) => PAID_MEDIA_EXPORT_CHART_IDS.includes(id)).length
    const keepPaidMediaTabs = paidExportCount > 1
    exportContextRunRef.current = {
      reportDataScope,
      widgetDataScopes,
      allDataChartIds: chartIdList.filter((id) =>
        chartExportUsesAllData(id, reportDataScope, widgetDataScopes),
      ),
    }
    const filteredExportCharts = new Set(
      dedupeExportChartIds(
        [...new Set(chartIdList)].filter((id) => exportableChartIds.includes(id)),
        { keepPaidMediaTabs },
      ),
    )
    const exportSections = buildExportSections(filteredExportCharts)
    const exportChartCount = exportSections.reduce((sum, section) => sum + section.charts.length, 0)
    if (exportChartCount === 0) return

    const reportExport = await import('./utils/dashboardReportExport')
    const waitForPaint = reportExport['waitForPaint']
    const createReportArtifact = reportExport['createDashboardReportArtifact']
    const downloadReportBlob = reportExport['downloadDashboardReportBlob']
    const cleanupReportCapture = reportExport['cleanupDashboardCaptureArtifacts']

    const exportRange = formatRangeLabel(dateRange) || 'Current range'
    const reportGeneratedAt = formatTimestamp(new Date())
    const reportMeta = {}
    if (exportSourceTemplateId) reportMeta.sourceTemplateId = exportSourceTemplateId
    if (exportContext?.mode === 'compare' && exportContext?.secondary?.dateRange) {
      reportMeta.compareType = exportContext.compareType || 'time'
      reportMeta.compareRange = exportContext.secondary.dateRange
    }

    lockDashboardReportViewport(dashboardContentRef.current)
    document.body.classList.add('dashboard-report-capture-mode')
    const exportNavRestore = {
      activeTab,
      analyticsSectionTab,
      topPostsVisibleCount,
    }

    flushSync(() => {
      setExportBusy(true)
      setExportProgress({
        phase: 'data',
        current: 0,
        total: exportChartCount,
        percent: 2,
        message: 'Generating Report…',
        chartsAdded: 0,
        chartsRemaining: exportChartCount,
      })
      setReadyAnalyticsSections(new Set(ANALYTICS_FETCH_SECTION_IDS))
      setExportPaidMediaAllViews(keepPaidMediaTabs)
      setExportPaidMediaCaptureView(null)
      setActiveTab('analytics')
      setAnalyticsSectionTab('all')
    })

    prefetchAllClientReportData()
    await waitForPaint(220)

    const onPrepareChart = async (chartId, opts) => {
      const meta = CHART_LOOKUP[chartId]
      const paidView = PAID_MEDIA_WIDGET_VIEW_BY_CHART_ID[chartId]

      if (meta?.sectionId === 'goals') {
        flushSync(() => setActiveTab('goals'))
        await waitForPaint(120)
        return
      }

      flushSync(() => {
        setActiveTab('analytics')
        setAnalyticsSectionTab('all')
        if (
          chartReportSkipsDomCapture(chartId) &&
          chartId !== 'audienceInsights' &&
          chartId !== 'alerts' &&
          chartId !== 'messages' &&
          chartId !== 'messageSentiment'
        ) {
          return
        }
        if (
          chartId === 'topPerformingAds' &&
          reportDataScope === REPORT_DATA_SCOPE.CURRENT_VIEW
        ) {
          const currentView = paidExportSortRef.current?.view || 'ads'
          setExportPaidMediaCaptureView(currentView)
        } else if (paidView) {
          setExportPaidMediaCaptureView(paidView)
        } else if (chartId === 'paidMediaPerformance') {
          setExportPaidMediaCaptureView(null)
        }
        if (chartId === 'socialPerformanceBenchmark') {
          setSocialPerformanceTab(opts?.tab === 'growth' ? 'growth' : socialPerformanceTab)
        }
      })
      await waitForPaint(120)
    }

    try {
      const { blob, filename } = await createReportArtifact({
        format: exportFormat,
        safeName,
        reportName,
        exportRange,
        reportGeneratedAt,
        selectedClientName,
        exportSections,
        reportMeta,
        getDataContext: buildExportDataContextLive,
        isChartDataReady: isChartDataReadyLive,
        onPrepareChart: exportFormat === 'pdf' ? onPrepareChart : undefined,
        onProgress: setExportProgress,
      })

      downloadReportBlob(blob, filename)
      closeExport()
    } catch (err) {
      console.error(err)
      window.alert('Unable to generate the report. Please try again.')
    } finally {
      if (typeof cleanupReportCapture === 'function') cleanupReportCapture()
      document.body.classList.remove('dashboard-report-capture-mode')
      if (exportNavRestore) {
        flushSync(() => {
          setActiveTab(exportNavRestore.activeTab)
          setAnalyticsSectionTab(exportNavRestore.analyticsSectionTab)
          setTopPostsVisibleCount(exportNavRestore.topPostsVisibleCount)
        })
      }
      setExportBusy(false)
      setExportProgress(null)
      setExportPaidMediaCaptureView(null)
      setExportPaidMediaAllViews(false)
    }
  }, [
    draftExportCharts,
    exportBusy,
    exportName,
    dateRange,
    selectedClientName,
    exportSourceTemplateId,
    exportContext,
    exportFormat,
    reportDataScope,
    widgetDataScopes,
    enabledPaidExportChartIds,
    buildExportDataContextLive,
    isChartDataReadyLive,
    activeTab,
    analyticsSectionTab,
    topPostsVisibleCount,
    closeExport,
    prefetchAllClientReportData,
  ])

  const handleExport = () => {
    void exportReportDownload()
  }

  const PLATFORM_CARD_CONFIG = {
    instagram: { id: 'instagram', label: 'Instagram', icon: socialIcon('instagram.svg') },
    facebook: { id: 'facebook', label: 'Facebook', icon: socialIcon('facebook.svg') },
    linkedin: { id: 'linkedin', label: 'LinkedIn', icon: socialIcon('linkedin.svg') },
    twitter: { id: 'twitter', label: 'Twitter/X', icon: socialIcon('x.png') },
    tiktok: { id: 'tiktok', label: 'TikTok', icon: socialIcon('tiktok.svg') },
  }

  const MAX_INSIGHT_PLATFORM_ICONS = 1

  const buildPlatformBreakdownTooltip = (breakdown, cardId) => {
    if (!breakdown) return ''
    const entries = Object.entries(breakdown)
      .filter(([, v]) => v > 0)
      .map(([p, v]) => {
        const label = PLATFORM_CARD_CONFIG[p]?.label || p
        const formatted = cardId === 'paid'
          ? `$${formatCompactNumber(v)}`
          : cardId === 'growth'
            ? (v >= 0 ? `+${formatCompactNumber(v)}` : formatCompactNumber(v))
            : formatCompactNumber(v)
        return `${label}: ${formatted}`
      })
    return entries.join(' • ')
  }

  const renderPlatformIcons = (card) => {
    const breakdown = card.platformBreakdown
    if (!breakdown) return null
    const platformsByValue = Object.entries(breakdown)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => (b || 0) - (a || 0))
    if (platformsByValue.length === 0) return null
    const topPlatforms = platformsByValue
      .slice(0, MAX_INSIGHT_PLATFORM_ICONS)
      .map(([p]) => p)
    const tooltip = buildPlatformBreakdownTooltip(breakdown, card.id)
    return (
      <div className="liveNowCardPlatformIcons" title={tooltip}>
        {topPlatforms.map((p) => {
          const config = PLATFORM_CARD_CONFIG[p]
          if (!config?.icon) return null
          return (
            <img
              key={p}
              src={resolvePublicAssetUrl(config.icon)}
              alt={config.label || p}
              className="liveNowCardPlatformIcon"
            />
          )
        })}
      </div>
    )
  }

  const COUNT_METRIC_IDS = ['inbox', 'campaign', 'growth']
  const isCountMetric = (id) => COUNT_METRIC_IDS.includes(id)

  const renderDeltaRow = (card) => {
    const data = card.sparklineData || []
    const hasData = data.length >= 2
    const trend = card.sparklineTrend || 'stable'
    const trendClass = trend === 'up' ? 'liveNowCardDelta--up' : trend === 'down' ? 'liveNowCardDelta--down' : 'liveNowCardDelta--neutral'

    let deltaText
    let deltaClass
    if (card.id === 'growth') {
      const net = Number(card.followerNewValue)
      const value = Number.isFinite(net) ? net : 0
      const arrow = value >= 0 ? '▲' : '▼'
      const abs = Math.abs(value)
      const formatted = abs >= 1000 ? `${(abs / 1000).toFixed(1)}K` : String(abs)
      deltaText = `${arrow} ${formatted}`
      deltaClass = value > 0 ? 'liveNowCardDelta--up' : value < 0 ? 'liveNowCardDelta--down' : 'liveNowCardDelta--neutral'
    } else if (hasData) {
      if (isCountMetric(card.id)) {
        const first = data[0] || 0
        const last = data[data.length - 1] || 0
        const absDelta = last - first
        const arrow = absDelta >= 0 ? '▲' : '▼'
        const formatted = Math.abs(absDelta) >= 1000
          ? `${(Math.abs(absDelta) / 1000).toFixed(1)}K`
          : String(Math.abs(absDelta))
        deltaText = `${arrow} ${formatted}`
      } else {
        const pct = card.sparklinePercentChange
        deltaText = pct != null ? `${pct >= 0 ? '▲' : '▼'} ${Math.abs(pct).toFixed(1)}%` : '—'
      }
      deltaClass = trendClass
    } else {
      deltaText = '—'
      deltaClass = 'liveNowCardDelta--neutral'
    }

    return (
      <span className={`liveNowCardDelta ${deltaClass}`}>
        {deltaText}
      </span>
    )
  }

  const getGrowthNetClass = (card) => {
    const netGrowth = Number(card.netGrowthToday) || 0
    if (netGrowth > 0) return 'liveNowCardDelta--up'
    if (netGrowth < 0) return 'liveNowCardDelta--down'
    return 'liveNowCardDelta--neutral'
  }

  const renderGrowthSecondaryMetric = (card) => {
    if (card.secondaryValue) {
      return (
        <span className="liveNowCardNetGrowth">
          <span className="liveNowCardNetGrowthLabel">Net growth</span>
          <span className={`liveNowCardDelta liveNowCardSecondaryMetric ${getGrowthNetClass(card)}`}>
            {card.secondaryValue}
          </span>
        </span>
      )
    }
    return <span className="liveNowCardValueSuffix">{card.subLabel || 'Net growth'}</span>
  }

  const renderLiveNowCardMetric = (card) => {
    if (card.id === 'growth') {
      return (
        <div className="liveNowCardMetric">
          <div className="liveNowCardValueRow">
            <span className="liveNowCardValueMain dashboard-tabular-nums">
              {card.value}
            </span>
            {renderDeltaRow(card)}
          </div>
          {renderGrowthSecondaryMetric(card)}
        </div>
      )
    }

    return (
      <div className="liveNowCardMetric">
        <div className="liveNowCardValueRow">
          <span className="liveNowCardValueMain dashboard-tabular-nums">{card.value}</span>
          {renderDeltaRow(card)}
        </div>
        {card.subLabel && <span className="liveNowCardValueSuffix">{card.subLabel}</span>}
      </div>
    )
  }

  const renderGoalProgress = (card) => {
    const progress = card.goalProgress
    if (progress == null) return null
    const pct = Math.min(100, Math.round(progress * 100))
    const fill =
      progress >= 0.8
        ? 'var(--dashboard-metric-growth, #059669)'
        : progress >= 0.5
          ? '#f59e0b'
          : 'var(--dashboard-metric-negative, #dc2626)'
    return (
      <div
        className="liveNowCardGoalProgress"
        title={`${pct}% to goal`}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{ '--kpi-goal-pct': `${pct}%`, '--kpi-goal-fill': fill }}
      />
    )
  }

  const liveNowIcons = {
    campaign: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 11h4l10-6v14l-10-6H3z" />
        <path d="M21 10v4" />
        <path d="M7 14v4" />
      </svg>
    ),
    engagement: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 21s-7-4.35-7-9a4 4 0 0 1 7-2.65A4 4 0 0 1 19 12c0 4.65-7 9-7 9z" />
      </svg>
    ),
    inbox: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
        <path d="M7 12h10" />
        <path d="M9 8h6" />
      </svg>
    ),
    paid: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v10" />
        <path d="M9.5 9.5c0-1.38 1.34-2.5 3-2.5s3 1.12 3 2.5-1.34 2.5-3 2.5-3 1.12-3 2.5 1.34 2.5 3 2.5 3-1.12 3-2.5" />
      </svg>
    ),
    growth: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 17l6-6 4 4 7-7" />
        <path d="M14 8h6v6" />
      </svg>
    ),
  }

  const liveNowCardsWithIcons = sohoPulseCards.map((card) => ({
    ...card,
    icon: liveNowIcons[card.id],
    platformBreakdownTooltip: buildPlatformBreakdownTooltip(card.platformBreakdown, card.id),
  }))

  /** Pulse header: "Mar 31, 12:44 PM GMT+5:30" — Intl.DateTimeFormat (safe in Node/V8). */
  const formatPulseSnapshotUpdated = (date) => {
    const d = date instanceof Date ? date : new Date(date)
    if (Number.isNaN(d.getTime())) return '—'
    try {
      return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(d)
    } catch {
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    }
  }

  return (
    <div className={`dashboardMain${embedded ? ' dashboardMain--embedded' : ''}`} data-property-id={propertyId || undefined}>
      <svg className="sohoGradientDefs" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="sohoInsightGradient" x1="4" y1="4" x2="20" y2="20">
            <stop offset="0%" stopColor="#DC4563" />
            <stop offset="100%" stopColor="#AC1FAE" />
          </linearGradient>
        </defs>
      </svg>
      <div
        ref={dashboardContentRef}
        className={`dashboardContent ${isDocked ? 'dashboardContent--docked' : ''}`}
      >
        <div className="dashboardContentInner">
        {!embedded ? (
        <div className="page-header">
          <h2 className="page-title">{propertyName || 'Dashboard'}</h2>
        </div>
        ) : null}
        {showSohoPulse && (
        <div className="liveNowSection">
          <div className="liveNowContainer">
            <div className="liveNowHeader">
              <div className="liveNowHeaderLeft">
                <div className="liveNowTitle">
                  <span className="liveNowIndicator"></span>
                  <span className="liveNowTitleMain">Soho Pulse</span>
                  <span className="liveNowTitleSubtle">
                    Daily Performance Snapshot • Updated {formatPulseSnapshotUpdated(lastUpdatedPulse)}
                  </span>
                </div>
              </div>
            </div>
            <div className="liveNowCardsScroll" ref={liveNowCardsScrollRef}>
              <div className="liveNowCards" ref={liveNowCardsRef}>
                {liveNowCardsWithIcons.map((card) => {
                  const isOpen = openKpiCardId === card.id
                  return (
                  <div
                    key={card.id}
                    ref={(el) => {
                      if (el) kpiCardRefs.current[card.id] = el
                    }}
                    className={`kpiCardWrap ${isOpen ? 'kpiCardWrap--open' : ''} ${card.isLoading ? 'kpiCardWrap--loading' : ''}`}
                    role="button"
                    tabIndex={card.isLoading ? -1 : 0}
                    aria-busy={card.isLoading ? 'true' : undefined}
                    aria-expanded={isOpen}
                    aria-controls={`kpi-popover-${card.id}`}
                    aria-label={`${card.title} - click to view details`}
                    onMouseEnter={(e) => {
                      if (card.isLoading) return
                      handleKpiEnter(card.id, e)
                    }}
                    onMouseLeave={handleKpiLeave}
                    onClick={(e) => {
                      e.preventDefault()
                      if (card.isLoading) return
                      toggleKpiPopover(card.id)
                    }}
                    onKeyDown={(e) => {
                      if (card.isLoading) return
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        toggleKpiPopover(card.id)
                      }
                    }}
                  >
                    <div className={`liveNowCard${card.goalProgress != null ? ' liveNowCard--withGoal' : ''}${card.isLoading ? ' liveNowCard--loading' : ''}`}>
                      <div className="liveNowCardContent">
                        <div className="liveNowCardLeft">
                          <div className="liveNowCardTitleRow">
                            <div className="liveNowCardTitleWithInfo">
                              <span className="liveNowCardTitleText">{card.title}</span>
                              <KpiInfoTooltip
                                content={pulseCardTooltipText(card)}
                                aria-label={`${card.title} definition`}
                                portaled
                                placement="bottom"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            </div>
                            {renderPlatformIcons(card)}
                          </div>
                          {card.isLoading ? (
                            <div className="liveNowCardMetric liveNowCardMetric--loading">
                              <SohoLoader size="sm" aria-label={`Loading ${card.title}`} />
                            </div>
                          ) : (
                            renderLiveNowCardMetric(card)
                          )}
                        </div>
                        {renderGoalProgress(card)}
                      </div>
                    </div>
                  </div>
                )})}
              </div>
            </div>
          </div>
        </div>
        )}

        </div>

        <div ref={topBarSentinelRef} className="dashboardTopBarSentinel" aria-hidden="true" />
        <div
          ref={topBarRef}
          className={`dashboardTopBar ${embedded ? 'dashboardTopBar--embeddedSticky' : ''} ${isDocked ? 'dashboardTopBar--docked' : ''} ${activeTab === 'analytics' && visibleAnalyticsTabs.length > 0 ? 'dashboardTopBar--hasSubTabs' : ''}`}
        >
          <div className="dashboardTopBarMainRow">
            <div className="dashboardTopBarLeft">
              <div className="dashboardTabs">
              {mainTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`dashboardTab ${activeTab === tab.id ? 'dashboardTab--active' : ''}`}
                    onClick={() => handleMainTabClick(tab.id)}
                  >
                    <span className="dashboardTabIcon">{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
            {!embedded ? (
            <div className="dashboardTopBarRight">
            <div ref={dateRangeRef} className="dashboardFilterSelect dateRangeSelector">
              <button
                className="dashboardFilterSelectButton dateRangeButton"
                type="button"
                onClick={() => setDateRangeOpen((prev) => !prev)}
                data-tooltip="Date"
                aria-label="Date range"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <span className="dateRangeLabel">Date</span>
                <span className="dateRangeValue">
                  {dateRangePreset === 'last_30'
                    ? 'Last 30 days'
                    : dateRangePreset === 'month_to_date'
                      ? 'This month'
                      : dateRangePreset === 'previous_month'
                        ? 'Last month'
                        : formatRangeLabel(dateRange)}
                </span>
              </button>
              {dateRangeOpen && (
                <div className="dashboardFilterSelectMenu dateRangeMenu dashboardFilterSelectMenu--alignRight">
                  <button
                    className={`dashboardFilterSelectOption ${dateRangePreset === 'last_30' ? 'dashboardFilterSelectOption--active' : ''}`}
                    type="button"
                    onClick={() => {
                      applyPresetRange('last_30')
                      setDateRangeOpen(false)
                    }}
                  >
                    Last 30 days
                  </button>
                  <button
                    className={`dashboardFilterSelectOption ${dateRangePreset === 'month_to_date' ? 'dashboardFilterSelectOption--active' : ''}`}
                    type="button"
                    onClick={() => {
                      applyPresetRange('month_to_date')
                      setDateRangeOpen(false)
                    }}
                  >
                    This month
                  </button>
                  <button
                    className={`dashboardFilterSelectOption ${dateRangePreset === 'previous_month' ? 'dashboardFilterSelectOption--active' : ''}`}
                    type="button"
                    onClick={() => {
                      applyPresetRange('previous_month')
                      setDateRangeOpen(false)
                    }}
                  >
                    Last month
                  </button>
                  <button
                    className={`dashboardFilterSelectOption ${dateRangePreset === 'custom' ? 'dashboardFilterSelectOption--active' : ''}`}
                    type="button"
                    onClick={() => {
                      setDateRangePreset('custom')
                      setCustomRange({
                        start: toDateInputValue(dateRange.start),
                        end: toDateInputValue(dateRange.end),
                      })
                      setShowCustomPicker(true)
                    }}
                  >
                    Custom range
                  </button>
                  {showCustomPicker && (
                    <div className="dateRangeCustom">
                      <div className="dateRangeCustomTitle">Select custom dates</div>
                      <div className="dateRangeInputs">
                        <label className="dateRangeInput">
                          <span>Start</span>
                          <input
                            type="date"
                            max={localDateTodayKey()}
                            value={customRange.start || toDateInputValue(dateRange.start)}
                            onChange={(event) =>
                              setCustomRange((prev) => ({ ...prev, start: event.target.value }))
                            }
                          />
                        </label>
                        <label className="dateRangeInput">
                          <span>End</span>
                          <input
                            type="date"
                            max={localDateTodayKey()}
                            value={customRange.end || toDateInputValue(dateRange.end)}
                            onChange={(event) =>
                              setCustomRange((prev) => ({ ...prev, end: event.target.value }))
                            }
                          />
                        </label>
                      </div>
                      <div className="dateRangeActions">
                        <button
                          className="dateRangeCancel"
                          type="button"
                          onClick={() => {
                            setShowCustomPicker(false)
                            setDateRangeOpen(false)
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="dateRangeApply"
                          type="button"
                          onClick={() => {
                            const startValue = customRange.start || toDateInputValue(dateRange.start)
                            const endValue = customRange.end || toDateInputValue(dateRange.end)
                            const start = parseLocalDateInput(startValue)
                            const endDay = parseLocalDateInput(endValue)
                            if (!start || !endDay) return
                            const today = parseLocalDateInput(new Date())
                            const clampedEndDay = endDay > today ? today : endDay
                            const clampedStart = start > clampedEndDay ? clampedEndDay : start
                            setDateRange({
                              start: clampedStart,
                              end: endOfLocalDay(clampedEndDay),
                            })
                            setDateRangePreset('custom')
                            setDateRangeOpen(false)
                            setShowCustomPicker(false)
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div ref={platformFilterRef} className="dashboardFilterSelect dashboardTopBarPlatformFilter">
              <button
                className="dashboardFilterSelectButton"
                type="button"
                onClick={() => setPlatformFilterOpen((prev) => !prev)}
                data-tooltip="Platform filter"
                aria-label="Filter by platform"
              >
                <span className="dashboardFilterSelectLabel">Platform</span>
                <span className="dashboardFilterSelectValue">
                  {platformPulseFilterLabel}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {platformFilterOpen && (
                <div
                  className="dashboardFilterSelectMenu dashboardFilterSelectMenu--alignRight dashboardFilterSelectMenu--multiselect"
                  role="listbox"
                  aria-label="Platform filter"
                  aria-multiselectable="true"
                >
                  <label className="dashboardFilterMultiselectOption">
                    <IndeterminateCheckbox
                      className="dashboardFilterMultiselectCheckbox"
                      checked={isAllPlatformsSelected}
                      indeterminate={!isAllPlatformsSelected && platformPulsePlatforms.size < platformFilterOptions.length}
                      onChange={handlePlatformPulseAllToggle}
                    />
                    <span>All platforms</span>
                  </label>
                  {platformFilterOptions.map((platform) => (
                    <label key={platform} className="dashboardFilterMultiselectOption">
                      <input
                        type="checkbox"
                        className="dashboardFilterMultiselectCheckbox"
                        checked={isPlatformPulseSelected(platform)}
                        onChange={() => handlePlatformPulseToggle(platform)}
                      />
                      <span>{platformLabels[platform] || platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="dashboardTopBarActions">
              <button
                className="dashboardActionButton dashboardActionButton--secondary dashboardHeaderIconButton"
                type="button"
                onClick={openExport}
                aria-label="Export"
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 3v12" />
                  <path d="M7 10l5 5 5-5" />
                  <path d="M5 21h14" />
                </svg>
              </button>
              <button
                className="dashboardActionButton dashboardActionButton--secondary dashboardHeaderIconButton"
                type="button"
                onClick={openConfigure}
                aria-label="Configure charts"
              >
                <span className="dashboardActionButtonIcon">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <line x1="4" y1="18" x2="20" y2="18" />
                    <circle cx="9" cy="6" r="2" />
                    <circle cx="15" cy="12" r="2" />
                    <circle cx="11" cy="18" r="2" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
            ) : null}
          </div>
          {activeTab === 'analytics' && hasAnyVisibleSection && (
            <div className="analyticsSubTabsRow">
              <div className="analyticsSubTabs">
                {visibleAnalyticsTabs.map((tab) => {
                  const comingSoon = isComingSoonAnalyticsTab(tab.id)
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      className={`analyticsSubTab ${analyticsSectionTab === tab.id ? 'analyticsSubTab--active' : ''} ${comingSoon ? 'analyticsSubTab--comingSoon coming-soon-host coming-soon-host--highlight-outline coming-soon-host--reveal-wide' : ''}`}
                      onClick={() => {
                        if (comingSoon) {
                          setAnalyticsSectionTab(tab.id)
                          return
                        }
                        setAnalyticsSectionTab(tab.id)
                        saveDashboardPrefs(widgetOrder, selectedCharts, tab.id, {
                          showSohoPulse,
                        })
                      }}
                    >
                      <span>{tab.label}</span>
                      {comingSoon && <ComingSoonPill placement="inline-end" />}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <div className="dashboardContentInner">
        <div className="dashboardBody">
          {activeTab === 'analytics' && !hasAnyVisibleSection && (
            <div className="analyticsEmptyState">
              <p className="analyticsEmptyStateText">No analytics sections visible. Configure your dashboard to see charts and widgets.</p>
              <button
                type="button"
                className="dashboardActionButton analyticsEmptyStateButton"
                onClick={openConfigure}
              >
                Configure Dashboard
              </button>
            </div>
          )}
          {activeTab === 'analytics' && hasAnyVisibleSection && isComingSoonAnalyticsTab(analyticsSectionTab) && (
            <div className="analyticsComingSoonState">
              <p className="analyticsComingSoonTitle">Coming Soon</p>
              <p className="analyticsComingSoonText">
                {visibleAnalyticsTabs.find((t) => t.id === analyticsSectionTab)?.label || 'This section'} is under development.
              </p>
            </div>
          )}
          {activeTab === 'analytics' && hasAnyVisibleSection && !isComingSoonAnalyticsTab(analyticsSectionTab) && (analyticsSectionTab === 'social' || analyticsSectionTab === 'all') && showSocialSection && (
            <div className="dashboardSection dashboardSection--social">
              <div className="dashboardSectionHeader">
                <div className="dashboardSectionTitleRow">
                  <div className="dashboardSectionTitle">Social Performance</div>
                  <KpiInfoTooltip
                    content="Post performance, competitor benchmarking, and top posts."
                    aria-label="Social Performance info"
                    portaled
                    placement="right"
                  />
                </div>
              </div>

              {orderedSocialRenderItems.map((item) => {
                if (item === 'goalPerformance') {
                  if (!hasActiveGoals) return null
                  return (
                    <div key="goalPerformance" className="goalPerformanceSection">
                      <GoalPerformanceTable
                        goals={committedGoalsList}
                        formatGoalValue={formatGoalValue}
                        loading={
                          goalsAwaitingHydration ||
                          (committedGoalMetrics.size > 0 && goalsYtdPerformancePending)
                        }
                        onManageGoals={() => setActiveTab('goals')}
                      />
                    </div>
                  )
                }
                if (item === 'grid') {
                  return (
                    <div key="grid" className="dashboardPerformanceGrid">
                  {widgetOrder.indexOf('campaignPerformance') < widgetOrder.indexOf('platformPerformanceComparison') ? (
                    <>
                      {isChartVisible('campaignPerformance') && (
                        <div className="campaignPerformanceSection dashboardPerformanceGridPrimary">
                          <ChartPanel
                    dataChartId="campaignPerformance"
                    loading={campaignPerformanceLoading}
                    title={(
                      <span className="campaignPerformanceTitle">
                        Event Performance
                        <span className="campaignPerformanceTitleMeta">
                          Events: {formatNumber(totalCampaignEvents)} | Posts: {formatNumber(totalCampaignPosts)}
                        </span>
                      </span>
                    )}
                    subtitle="Track post performance across key hospitality events."
                  >
                    <div className="campaignPerformanceChart">
                      <CampaignPerformanceBarChart
                        key={Array.from(platformPulsePlatforms).sort().join(',') || 'all'}
                        data={effectiveCampaignEventData}
                        xKey="eventName"
                        series={campaignSeries}
                        height={300}
                        showTooltip={false}
                        showTrend={false}
                        forceEmpty={campaignPerformanceForceEmpty}
                        emptyMessage={campaignPerformanceEmptyMessage}
                        legend={
                          campaignPerformanceForceEmpty
                            ? null
                            : (
                              <div className="campaignPerformanceLegend">
                                <div className="campaignPerformanceLegendItem">
                                  <span className="campaignPerformanceLegendSwatch campaignPerformanceLegendSwatch--impressions" />
                                  <span>Total Impressions</span>
                                </div>
                                <div className="campaignPerformanceLegendItem">
                                  <span className="campaignPerformanceLegendSwatch campaignPerformanceLegendSwatch--engagements" />
                                  <span>Total Engagements</span>
                                </div>
                              </div>
                            )
                        }
                      />
                    </div>
                          </ChartPanel>
                        </div>
                      )}
                      {isChartVisible('platformPerformanceComparison') && (
                        <div className="campaignPerformanceSection dashboardPerformanceGridSecondary">
                          <PlatformPerformanceComparison
                            apiChartData={resolvedPlatformComparisonData}
                            loading={socialPerformanceLoading}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {isChartVisible('platformPerformanceComparison') && (
                        <div className="campaignPerformanceSection dashboardPerformanceGridSecondary">
                          <PlatformPerformanceComparison
                            apiChartData={resolvedPlatformComparisonData}
                            loading={socialPerformanceLoading}
                          />
                        </div>
                      )}
                      {isChartVisible('campaignPerformance') && (
                        <div className="campaignPerformanceSection dashboardPerformanceGridPrimary">
                          <ChartPanel
                            dataChartId="campaignPerformance"
                            loading={campaignPerformanceLoading}
                            title={(
                              <span className="campaignPerformanceTitle">
                                Event Performance
                                <span className="campaignPerformanceTitleMeta">
                                  Events: {formatNumber(totalCampaignEvents)} | Posts: {formatNumber(totalCampaignPosts)}
                                </span>
                              </span>
                            )}
                            subtitle="Track post performance across key hospitality events."
                          >
                            <div className="campaignPerformanceChart">
                              <CampaignPerformanceBarChart
                                key={Array.from(platformPulsePlatforms).sort().join(',') || 'all'}
                                data={effectiveCampaignEventData}
                                xKey="eventName"
                                series={campaignSeries}
                                height={300}
                                showTooltip={false}
                                showTrend={false}
                                forceEmpty={campaignPerformanceForceEmpty}
                                emptyMessage={campaignPerformanceEmptyMessage}
                                legend={
                                  campaignPerformanceForceEmpty
                                    ? null
                                    : (
                                      <div className="campaignPerformanceLegend">
                                        <div className="campaignPerformanceLegendItem">
                                          <span className="campaignPerformanceLegendSwatch campaignPerformanceLegendSwatch--impressions" />
                                          <span>Total Impressions</span>
                                        </div>
                                        <div className="campaignPerformanceLegendItem">
                                          <span className="campaignPerformanceLegendSwatch campaignPerformanceLegendSwatch--engagements" />
                                          <span>Total Engagements</span>
                                        </div>
                                      </div>
                                    )
                                }
                              />
                            </div>
                          </ChartPanel>
                        </div>
                      )}
                    </>
                  )}
                </div>
                  )
                }
                if (item === 'monthlySocialPerformance') {
                  return (
                    <div key="monthlySocialPerformance" className="monthlySocialSection">
                      <ChartPanel
                    dataChartId="monthlySocialPerformance"
                    loading={socialPerformanceLoading}
                    title="Social Performance"
                    subtitle="All data is sourced from native platforms and aligned with Meta Business Suite reporting standards."
                    actions={(
                      <div className="platformFilterWrapper">
                        <MonthlySocialPerformanceFilterBar
                          comparePeriod={monthlySocialComparePeriod}
                          onComparePeriodChange={setMonthlySocialComparePeriod}
                          source={monthlySocialSource}
                          onSourceChange={setMonthlySocialSource}
                        />
                      </div>
                    )}
                  >
                    <MonthlySocialPerformancePanel
                      columns={resolvedMonthlySocial.columns}
                      rowsBySource={monthlySocialRowsBySource}
                      comparePeriod={monthlySocialComparePeriod}
                      onComparePeriodChange={setMonthlySocialComparePeriod}
                      source={monthlySocialSource}
                      onSourceChange={setMonthlySocialSource}
                      hideFilterBar
                      resolveIconUrl={(row) => {
                        const icon =
                          platformIconBySection[row.label] ||
                          platformIconBySection[row.platform] ||
                          platformIconBySection[String(row.label || '').toLowerCase()]
                        return icon ? resolvePublicAssetUrl(icon) : null
                      }}
                    />
                      </ChartPanel>
                    </div>
                  )
                }
                if (item === 'socialPerformanceBenchmark') {
                  return (
                    <SocialPerformanceBenchmark
                      key="socialPerformanceBenchmark"
                      tabs={socialPerformanceTabs}
                      activeTab={socialPerformanceTab}
                      onTabChange={setSocialPerformanceTab}
                      section={currentSocialPerformance}
                      loading={competitivePerformanceLoading}
                      visiblePlatforms={
                        platformPulsePlatforms.size === 0
                          ? platformFilterOptions
                          : platformFilterOptions.filter((platform) => platformPulsePlatforms.has(platform))
                      }
                    />
                  )
                }
                if (item === 'topPosts') {
                  return (
                    <ChartPanel
                      key="topPosts"
                      dataChartId="topPosts"
                  loading={!isPostTopicView && topPostsInitialLoading}
                  className="topPostsSection postPerformanceSection"
                  title="Post Performance"
                  subtitle={
                    isPostTopicView
                      ? 'Compare your highest- and lowest-performing post topics by metric and platform.'
                      : 'Explore your highest- and lowest-performing posts by metric and platform.'
                  }
                  actions={(
                    <div className="topPostsHeaderActions">
                      <div className="topPostsControls">
                      <div ref={topPostsViewRef} className="dashboardFilterSelect topPostsViewSelect">
                        <button
                          className="dashboardFilterSelectButton topPostsViewButton"
                          type="button"
                          onClick={() => setTopPostsViewOpen((prev) => !prev)}
                        >
                          <span className="dashboardFilterSelectLabel">View by</span>
                          <span className="dashboardFilterSelectValue">
                            {isPostTopicView ? 'Post Topic' : 'Posts'}
                          </span>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </button>
                        {topPostsViewOpen && (
                          <div className="dashboardFilterSelectMenu topPostsViewMenu">
                            {['posts', 'postTopics'].map((view) => {
                              // Post Topic stays out of the menu until it is implemented.
                              if (view === 'postTopics' && isPostTopicComingSoon) return null
                              return (
                              <button
                                key={view}
                                type="button"
                                className={`dashboardFilterSelectOption ${topPostsView === view ? 'dashboardFilterSelectOption--active' : ''}`}
                                onClick={() => {
                                  setTopPostsView(view)
                                  if (view === 'postTopics') {
                                    setTopPostsPlatform('all')
                                    setTopPostsRankBy('engagements')
                                  } else if (topPostsRankBy === 'engagements') {
                                    setTopPostsRankBy('engagement')
                                  }
                                  setTopPostsViewOpen(false)
                                }}
                              >
                                {view === 'posts' ? 'Posts' : 'Post Topic'}
                              </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                      {!isPostTopicView && (
                        <div className="platformFilterWrapper">
                          <div className="platformFilterTabs">
                            {(topPostsPlatformOptions).map((platform) => (
                              <button
                                key={platform}
                                type="button"
                                className={`platformFilterTab ${topPostsPlatform === platform ? 'platformFilterTab--active' : ''}`}
                                onClick={() => setTopPostsPlatform(platform)}
                              >
                                {platformLabels[platform] || platform.charAt(0).toUpperCase() + platform.slice(1)}
                              </button>
                            ))}
                          </div>
                          <div ref={topPostsPlatformFilterRef} className="dashboardFilterSelect platformFilterDropdown">
                            <button
                              className="dashboardFilterSelectButton"
                              type="button"
                              onClick={() => setTopPostsPlatformFilterOpen((prev) => !prev)}
                            >
                              <span className="dashboardFilterSelectLabel">Platform</span>
                              <span className="dashboardFilterSelectValue">
                                {platformLabels[topPostsPlatform] || topPostsPlatform.charAt(0).toUpperCase() + topPostsPlatform.slice(1)}
                              </span>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M6 9l6 6 6-6" />
                              </svg>
                            </button>
                            {topPostsPlatformFilterOpen && (
                              <div className="dashboardFilterSelectMenu">
                                {(topPostsPlatformOptions).map((platform) => (
                                  <button
                                    key={platform}
                                    type="button"
                                    className={`dashboardFilterSelectOption ${topPostsPlatform === platform ? 'dashboardFilterSelectOption--active' : ''}`}
                                    onClick={() => {
                                      setTopPostsPlatform(platform)
                                      setTopPostsPlatformFilterOpen(false)
                                    }}
                                  >
                                    {platformLabels[platform] || platform.charAt(0).toUpperCase() + platform.slice(1)}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    </div>
                  )}
                >
                  <div className="topPostsWrapper">
                      <div className="topPostsSortHeader">
                        <div className="topPostsSortHeader__left postPerformanceRankMetricControls">
                          <div className="postPerformanceRankMetricTabs">
                            <span className="topPostsSortLabel">{isPostTopicView ? 'Rank topics by' : 'Rank by'}</span>
                            {(isPostTopicView ? POST_TOPIC_RANK_METRICS.map((metric) => metric.key) : POST_PERFORMANCE_RANK_COLUMNS).map((column) => (
                              <button
                                key={column}
                                type="button"
                                className={`topPostsSortCol topPostsSortCol--performance ${topPostsRankBy === column ? 'topPostsSortCol--active' : ''}`}
                                onClick={() => handlePostPerformanceRankClick(column)}
                              >
                                {postPerformanceRankColumnLabel(column)}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="postPerformanceRankSummary">
                          <span className="postPerformanceRankSummaryLabel">Showing</span>
                          <div
                            ref={topPostsRankModeRef}
                            className="dashboardFilterSelect postPerformanceGroupSelect"
                          >
                            <button
                              type="button"
                              className="dashboardFilterSelectButton postPerformanceGroupButton"
                              aria-haspopup="listbox"
                              aria-expanded={topPostsRankModeOpen}
                              aria-label={`Performance group: ${topPostsRankMode === 'top' ? 'Top 15' : 'Bottom 15'}`}
                              onClick={() => setTopPostsRankModeOpen((prev) => !prev)}
                            >
                              <span className="dashboardFilterSelectValue">
                                {topPostsRankMode === 'top' ? 'Top 15' : 'Bottom 15'}
                              </span>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <path d="M6 9l6 6 6-6" />
                              </svg>
                            </button>
                            {topPostsRankModeOpen && (
                              <div
                                className="dashboardFilterSelectMenu postPerformanceGroupMenu"
                                role="listbox"
                                aria-label="Performance group"
                              >
                                {postPerformanceGroups.map((group) => (
                                  <button
                                    key={group.value}
                                    type="button"
                                    role="option"
                                    aria-selected={topPostsRankMode === group.value}
                                    className={`dashboardFilterSelectOption postPerformanceGroupOption ${topPostsRankMode === group.value ? 'dashboardFilterSelectOption--active' : ''}`}
                                    onClick={() => {
                                      setTopPostsRankMode(group.value)
                                      setTopPostsRankModeOpen(false)
                                    }}
                                  >
                                    <span className="postPerformanceGroupOptionLabel">{group.label}</span>
                                    <span className="postPerformanceGroupOptionDescription">{group.description}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className="postPerformanceRankSummaryTotal">
                            {isPostTopicView
                              ? `of ${rankedTopicPerformance.eligibleTotal} ${rankedTopicPerformance.eligibleTotal === 1 ? 'topic' : 'topics'}`
                              : `of ${topPostsTotalAvailable} ${topPostsTotalAvailable === 1 ? 'post' : 'posts'}`}
                          </span>
                        </div>
                      </div>
                  {!isPostTopicView
                    ? (() => {
                        const visiblePosts = filteredTopPostsData.slice(0, topPostsVisibleCount)
                        const topPostsRefreshing =
                          Boolean(selectedClientId) &&
                          resolvedTopPostsData.length > 0 &&
                          clientTopPostsState.status === 'loading'
                        const postsEmpty = !topPostsInitialLoading && visiblePosts.length === 0
                        return (
                          <>
                            {topPostsRefreshing && (
                              <div className="topPostsRefreshingState" role="status" aria-live="polite">
                                Updating…
                              </div>
                            )}
                            {postsEmpty ? (
                              <div className="topPostsEmptyState" role="status">
                                No posts found for this platform and date range.
                              </div>
                            ) : (
                              <HorizontalScrollCards
                                key={`post-performance-${topPostsView}-${topPostsRankBy}-${topPostsRankMode}-${topPostsPlatform}`}
                                className="topPostsCards topPostsCards--scroll"
                                showNavArrows
                              >
                                {!topPostsInitialLoading && visiblePosts.map((post) => (
                                <div key={post.postId ?? post.postIndex} className="topPostCard">
                                  {(() => {
                                    const featuredMetricValue = formatPostPerformanceMetricValue(
                                      'engagement',
                                      post.engagementRate,
                                    )
                                    const platformKey = topPostDashboardPlatformKey(post)
                                    const platformIconPath =
                                      platformIconBySection[platformKey] ||
                                      platformIconBySection[post.platform] ||
                                      platformIconBySection[post.socialNetwork] ||
                                      platformIconBySection[String(post.platform || '').toLowerCase()] ||
                                      platformIconBySection[String(post.socialNetwork || '').toLowerCase()]
                                    const platformIconSrc = platformIconPath
                                      ? resolvePublicAssetUrl(platformIconPath)
                                      : null
                                    const platformIconLabel =
                                      platformLabels[platformKey] ||
                                      platformLabels[post.platform] ||
                                      (platformKey
                                        ? platformKey.charAt(0).toUpperCase() + platformKey.slice(1)
                                        : null)
                                    const overlayParts = splitPostOverlayText(post.postText, post.topic)
                                    const overlayFullText = [overlayParts.title, overlayParts.copy]
                                      .filter(Boolean)
                                      .join('\n\n')

                                    const captionOverlay = (overlayParts.title || overlayParts.copy) ? (
                                      <div className="topPostCaptionOverlay" aria-hidden="true">
                                        {overlayParts.title ? (
                                          <span className="topPostCaptionOverlayTitle">{overlayParts.title}</span>
                                        ) : null}
                                        {overlayParts.copy ? (
                                          <span
                                            className={`topPostCaptionOverlayCopy${overlayParts.title ? '' : ' topPostCaptionOverlayCopy--solo'}`}
                                          >
                                            {overlayParts.copy}
                                          </span>
                                        ) : null}
                                      </div>
                                    ) : null

                                    return (
                                      <>
                                        <div
                                          className={`topPostThumbnail ${post.image ? 'topPostThumbnail--hasImage' : ''}`}
                                        >
                                          {post.image ? (
                                            <TopPostThumbnailMedia src={post.image} />
                                          ) : (
                                            <div className="topPostThumbnailPlaceholder" />
                                          )}
                                          {post.contentType && (
                                            <span className={`topPostContentTypeBadge topPostContentTypeBadge--${post.contentType.toLowerCase()}`}>
                                              {post.contentType}
                                            </span>
                                          )}
                                          {platformIconSrc ? (
                                            <span
                                              className="topPostPlatformIconBadge"
                                              data-tooltip={platformIconLabel || undefined}
                                              aria-label={platformIconLabel || 'Platform'}
                                            >
                                              <img
                                                src={platformIconSrc}
                                                alt=""
                                                className="kpiHoverLayerPlatformIcon"
                                                loading="lazy"
                                              />
                                            </span>
                                          ) : null}
                                          {overlayFullText && captionOverlay ? (
                                            <HoverTooltip
                                              content={overlayFullText}
                                              placement="below"
                                              align="start"
                                              variant="light"
                                              wrapClassName="topPostCaptionTooltipWrap"
                                              ariaLabel={overlayFullText}
                                              maxWidth={280}
                                              gap={6}
                                            >
                                              {captionOverlay}
                                            </HoverTooltip>
                                          ) : (
                                            captionOverlay
                                          )}
                                        </div>
                                        <div className="topPostContent">
                                          <div className="topPostMetaRow">
                                            <span className="topPostDate">{formatPostDate(post.date)}</span>
                                            {(post.permalink || post.postLink || post.post_media_link) && (
                                              <a
                                                href={post.permalink || post.postLink || post.post_media_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="topPostViewLink"
                                              >
                                                View post
                                              </a>
                                            )}
                                          </div>
                                          <div className="topPostEngagementSection">
                                            <div className="topPostEngagementLabel">Engagement rate</div>
                                            <div className="topPostEngagementValue">{featuredMetricValue}</div>
                                          </div>
                                    <div className="topPostMetrics">
                                      <div className="topPostMetric">
                                        <span className="topPostMetricLabel">LIKES</span>
                                        <span className="topPostMetricValue">{(post.likes ?? 0).toLocaleString()}</span>
                                      </div>
                                      <div className="topPostMetric">
                                        <span className="topPostMetricLabel">COMMENTS</span>
                                        <span className="topPostMetricValue">{(post.comments ?? 0).toLocaleString()}</span>
                                      </div>
                                      <div className="topPostMetric">
                                        <span className="topPostMetricLabel">SHARES</span>
                                        <span className="topPostMetricValue">{(post.shares ?? 0).toLocaleString()}</span>
                                      </div>
                                      <div className="topPostMetric">
                                        <span className="topPostMetricLabel">SAVES</span>
                                        <span className="topPostMetricValue">{(post.saves ?? 0).toLocaleString()}</span>
                                      </div>
                                      <div className="topPostMetric">
                                        <span className="topPostMetricLabel">IMPRESSIONS</span>
                                        <span className="topPostMetricValue">{(post.impressions ?? 0).toLocaleString()}</span>
                                      </div>
                                      <div className="topPostMetric">
                                        <span className="topPostMetricLabel">POST CLICKS</span>
                                        <span className="topPostMetricValue">{(post.postClicks ?? 0).toLocaleString()}</span>
                                      </div>
                                    </div>
                                  </div>
                                      </>
                                    )
                                  })()}
                                </div>
                              ))}
                              {topPostsShowLoadMore && (
                                <button
                                  type="button"
                                  className="topPostLoadMoreTile"
                                  onClick={() =>
                                    setTopPostsVisibleCount((c) =>
                                      Math.min(c + 15, topPostsTotalAvailable)
                                    )
                                  }
                                >
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                  </svg>
                                  Load 15 more
                                </button>
                              )}
                              </HorizontalScrollCards>
                            )}
                          </>
                        )
                      })()
                    : rankedTopicPerformance.metricEligibleTotal === 0 ? (
                      <div className="topPostsEmptyState" role="status">
                        No topic data is available for this metric and the selected filters.
                      </div>
                    ) : (
                      <HorizontalScrollCards
                        key={`post-performance-${topPostsView}-${topPostsRankBy}-${topPostsRankMode}-${topPostsPlatform}`}
                        className="topPostsCards topPostsCards--scroll"
                        showNavArrows
                      >
                        {rankedTopicPerformance.topics.map((topicRecord) => (
                          <PostTopicCard
                            key={topicRecord.topic}
                            topic={topicRecord.topic}
                            totalEngagements={topicRecord.totalEngagements}
                            engagementRate={topicRecord.engagementRate}
                            postCount={topicRecord.postCount}
                            leadingPlatform={topicRecord.leadingPlatform}
                            platformBreakdown={topicRecord.platformBreakdown}
                            platformLabels={platformLabels}
                            showLeadingPlatform
                          />
                        ))}
                      </HorizontalScrollCards>
                    )}
                  </div>
                    </ChartPanel>
                  )
                }
                if (item === 'topStories') {
                  return (
                    <TopStories
                      key="topStories"
                      data={filteredTopStoriesData}
                      availablePlatforms={topStoriesAvailablePlatforms}
                      dateRangePreset={dateRangePreset}
                      dateRangeLabel={formatRangeLabel(dateRange)}
                      loading={topStoriesInitialLoading}
                      onVisibleStoriesChange={handleVisibleStoriesChange}
                    />
                  )
                }
                return null
              })}
            </div>
          )}

          {activeTab === 'analytics' && hasAnyVisibleSection && !isComingSoonAnalyticsTab(analyticsSectionTab) && (analyticsSectionTab === 'content' || analyticsSectionTab === 'all') && showContentSection && (
            <LazyMount
              disabled={analyticsSectionTab !== 'all' || exportBusy}
              onVisible={() => markAnalyticsSectionReady('content')}
              placeholderHeight={420}
            >
            <div className="dashboardSection dashboardSection--content">
              <div className="dashboardSectionHeader">
                <div className="dashboardSectionTitleRow">
                  <div className="dashboardSectionTitle">Content Analytics</div>
                  <KpiInfoTooltip
                    content="Optimal posting times and content performance."
                    aria-label="Content Analytics info"
                    portaled
                    placement="right"
                  />
                </div>
              </div>
              <div className="dashboardPerformanceGrid">
                {isChartVisible('optimal_posting_times') && (
                  <OptimalPostingTimesUnifiedWidget
                    platformSeries={optimalPostingSeries}
                    calendarByDay={useApiPostHeatmap ? undefined : optimalPostingCalendarByDay}
                    apiPostingHeatmap={apiPostingHeatmap}
                    preferApiHeatmap={useApiPostHeatmap}
                    loading={postHeatmapLoading}
                    propertyTimezone={propertyChartTimezone}
                    dateRangeStartKey={toDateInputValue(dateRange?.start)}
                    dateRangeEndKey={toDateInputValue(dateRange?.end)}
                    dateRangeLabel={formatRangeLabel(dateRange)}
                  />
                )}
              </div>
            </div>
            </LazyMount>
          )}

          {activeTab === 'analytics' && hasAnyVisibleSection && !isComingSoonAnalyticsTab(analyticsSectionTab) && showPaidSection && (analyticsSectionTab === 'paid' || (analyticsSectionTab === 'all' && isAnalyticsSectionInAllView('paid'))) && (
            <LazyMount
              disabled={analyticsSectionTab !== 'all' || exportBusy}
              onVisible={() => markAnalyticsSectionReady('paid')}
              placeholderHeight={480}
            >
            <div className="dashboardSection dashboardSection--paid">
            <div className="dashboardSectionHeader">
              <div className="dashboardSectionTitleRow">
                <div className="dashboardSectionTitle">Paid Media</div>
                <KpiInfoTooltip
                  content="Paid media performance trends and top performing ads by platform."
                  aria-label="Paid Media info"
                  portaled
                  placement="right"
                />
              </div>
            </div>
            <div className="paidMediaSection">
              {isChartVisible('paidMediaPerformance') && (
                <ChartPanel
                  dataChartId="paidMediaPerformance"
                  title="Paid Media Performance"
                  subtitle={`Visualize revenue generated vs spend for Meta campaigns managed through SoHo · ${formatRangeLabel(dateRange)}`}
                  loading={paidMediaPerformanceLoading}
                  loadingMessage="Loading paid media performance…"
                  actions={(
                    <div className="platformFilterWrapper">
                      <div className="platformFilterTabs">
                        {['all', ...PAID_MEDIA_AD_PLATFORM_OPTIONS].map((platform) => (
                          <button
                            key={platform}
                            type="button"
                            className={`platformFilterTab ${effectivePaidMediaPlatform === platform ? 'platformFilterTab--active' : ''}`}
                            onClick={() => setPaidMediaPlatform(platform)}
                          >
                            {platform === 'all' ? 'All' : (platformLabels[platform] || platform.charAt(0).toUpperCase() + platform.slice(1))}
                          </button>
                        ))}
                      </div>
                      <div ref={paidMediaPlatformFilterRef} className="dashboardFilterSelect platformFilterDropdown">
                        <button
                          className="dashboardFilterSelectButton"
                          type="button"
                          onClick={() => setPaidMediaPlatformFilterOpen((prev) => !prev)}
                        >
                          <span className="dashboardFilterSelectLabel">Platform</span>
                          <span className="dashboardFilterSelectValue">
                            {effectivePaidMediaPlatform === 'all' ? 'All' : (platformLabels[effectivePaidMediaPlatform] || effectivePaidMediaPlatform.charAt(0).toUpperCase() + effectivePaidMediaPlatform.slice(1))}
                          </span>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </button>
                        {paidMediaPlatformFilterOpen && (
                          <div className="dashboardFilterSelectMenu">
                            {['all', ...PAID_MEDIA_AD_PLATFORM_OPTIONS].map((platform) => (
                              <button
                                key={platform}
                                type="button"
                                className={`dashboardFilterSelectOption ${effectivePaidMediaPlatform === platform ? 'dashboardFilterSelectOption--active' : ''}`}
                                onClick={() => {
                                  setPaidMediaPlatform(platform)
                                  setPaidMediaPlatformFilterOpen(false)
                                }}
                              >
                                {platform === 'all' ? 'All' : (platformLabels[platform] || platform.charAt(0).toUpperCase() + platform.slice(1))}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                >
                  <div className="paidMediaSummary">
                    <div className="paidMediaSummaryCard paidMediaSummaryCard--spend">
                      <div className="paidMediaSummaryLabelRow">
                        <span className="paidMediaSummaryLabel">Total Spend</span>
                        <KpiInfoTooltip
                          content="Total amount spent on Meta ad campaigns managed through SoHo for the selected platform and date range."
                          aria-label="Total Spend definition"
                          className="paidMediaSummaryInfo"
                          portaled
                          placement="bottom"
                        />
                      </div>
                      <div className="paidMediaSummaryValue">${formatCompactNumber(paidMediaTotals.spend)}</div>
                      <div className="paidMediaSummaryFootnote">{formatRangeLabel(dateRange)}</div>
                    </div>
                    <div className="paidMediaSummaryCard paidMediaSummaryCard--revenue">
                      <div className="paidMediaSummaryLabelRow">
                        <span className="paidMediaSummaryLabel">Revenue Generated</span>
                        <KpiInfoTooltip
                          content="Revenue attributed to your paid campaigns from reported conversions in the selected period."
                          aria-label="Revenue Generated definition"
                          className="paidMediaSummaryInfo"
                          portaled
                          placement="bottom"
                        />
                      </div>
                      <div className="paidMediaSummaryValue">${formatCompactNumber(paidMediaTotals.revenue)}</div>
                      <div className="paidMediaSummaryFootnote" title={previousPeriodTooltip || undefined}>
                        {paidMediaRevenueDelta >= 0 ? '+' : ''}
                        {paidMediaRevenueDelta}% vs previous period
                      </div>
                    </div>
                    <div className="paidMediaSummaryCard paidMediaSummaryCard--roas">
                      <div className="paidMediaSummaryLabelRow">
                        <span className="paidMediaSummaryLabel">Average ROAS</span>
                        <KpiInfoTooltip
                          content="Return on ad spend for the selected period. Calculated as revenue generated divided by total spend."
                          aria-label="Average ROAS definition"
                          className="paidMediaSummaryInfo"
                          portaled
                          placement="bottom"
                        />
                      </div>
                      <div className="paidMediaSummaryValue">{paidMediaTotals.roas.toFixed(1)}x</div>
                      <div className="paidMediaSummaryFootnote">Return on ad spend</div>
                    </div>
                  </div>

                  <div className="campaignPerformanceChart">
                    <PaidMediaPerformanceChart
                      key={effectivePaidMediaPlatform}
                      data={paidMediaSeries}
                      xKey="date"
                      series={paidMediaLineSeries}
                      height={320}
                      xTickFormatter={formatShortDate}
                      labelFormatter={formatShortDate}
                      emptyMessage="No data available"
                    />
                  </div>
                </ChartPanel>
              )}

              {paidMediaTopAdsEnabledViews.length > 0 && (
                <TopPerformingAdsWidget
                  ads={resolvedPaidTopAds}
                  campaigns={clientPaidPerformanceState.campaigns ?? []}
                  performanceCampaigns={resolvedPaidPerformanceCampaigns}
                  objectives={
                    paidPerformanceApiReady ? (clientPaidPerformanceState.objectives ?? []) : []
                  }
                  dataLevel={paidLevel}
                  loading={paidPerformanceLoading}
                  loadingMore={clientPaidPerformanceState.status === 'loadingMore'}
                  hasMore={Boolean(paidLevelPaging?.hasMore)}
                  onLoadMore={handlePaidLoadMore}
                  onViewChange={setPaidWidgetView}
                  onSortChange={setPaidWidgetSortBy}
                  onExportSortStateChange={handlePaidExportSortStateChange}
                  platforms={topAdsPlatforms}
                  onPlatformsChange={setTopAdsPlatforms}
                  platformOptions={platforms || ['instagram', 'facebook', 'linkedin', 'twitter', 'tiktok']}
                  platformLabels={platformLabels}
                  formatCompactNumber={formatCompactNumber}
                  enabledViews={
                    exportPaidMediaAllViews
                      ? ['ads', 'campaigns', 'objectives', 'performance']
                      : paidMediaTopAdsEnabledViews
                  }
                  forcedView={exportPaidMediaCaptureView}
                  defaultView={
                    paidMediaTopAdsEnabledViews.includes('ads') ? 'ads' : paidMediaTopAdsEnabledViews[0]
                  }
                />
              )}
            </div>
          </div>
            </LazyMount>
          )}

          {activeTab === 'analytics' && hasAnyVisibleSection && !isComingSoonAnalyticsTab(analyticsSectionTab) && (analyticsSectionTab === 'audience' || analyticsSectionTab === 'all') && showAudienceSection && (
            <LazyMount
              disabled={analyticsSectionTab !== 'all' || exportBusy}
              onVisible={() => markAnalyticsSectionReady('audience')}
              placeholderHeight={480}
            >
            <div className="dashboardSection dashboardSection--audience">
            <div className="dashboardSectionHeader">
              <div>
                <div className="dashboardSectionTitleRow">
                  <div className="dashboardSectionTitle">Audience Insights</div>
                  <HoverTooltip
                    content={AUDIENCE_INSIGHTS_INFO_TOOLTIP}
                    placement="below"
                    align="start"
                    maxWidth={320}
                    gap={8}
                    wrapClassName="audienceInsightsInfoTooltipWrap"
                    className="audienceInsightsInfoTooltip"
                    ariaLabel="Audience Insights info"
                  >
                    <span
                      className="liveNowCardInfoIcon dashboardSectionInfo dashboardSectionInfo--portaled"
                      role="img"
                      aria-hidden="true"
                    >
                      i
                    </span>
                  </HoverTooltip>
                </div>
              </div>
            </div>
            <div className="audienceInsightsSection">
              {isChartVisible('audienceInsights') && (
                <>
                  <div className="audienceDemographicsGrid">
                    {audienceDemographicsLoading ? (
                      <ChartPanel
                        loading
                        dataChartId="audienceInsights"
                        title="Audience Demographics"
                        className="audienceDemographicsGrid__empty"
                      />
                    ) : null}
                    {!audienceDemographicsLoading && (
                        <ChartPanel
                          dataChartId="audienceInsights"
                          title="Age & Gender Distribution"
                          subtitle={audienceAgeGenderSourceLabel}
                        >
                          <div className="audienceInsightChart">
                            <BaseBarChart
                              data={audienceDemographicData?.ageGender ?? []}
                              xKey="age"
                              series={audienceAgeGenderSeries}
                              height={220}
                              showLegend={false}
                              barSize={10}
                              showTrend={false}
                            />
                            <div className="campaignPerformanceLegend">
                              {audienceAgeGenderSeries.map((series) => (
                                <div key={series.key} className="campaignPerformanceLegendItem">
                                  <span
                                    className="campaignPerformanceLegendSwatch"
                                    style={{ backgroundColor: `var(${series.colorVar})` }}
                                  />
                                  <span>{series.label}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="audienceInsightBlock">
                            <div className="audienceInsightBlockLabel">Insight</div>
                            <p className="audienceInsightBlockText">{audienceAgeGenderSummary || 'Review age and gender breakdown to tailor content and targeting.'}</p>
                          </div>
                        </ChartPanel>
                    )}

                    {!audienceDemographicsLoading && (
                        <ChartPanel
                          dataChartId="audienceInsights"
                          title="Top Cities"
                          subtitle="Hometown city data from Facebook and Instagram"
                        >
                          <div className="audienceInsightChart">
                            <BaseBarChart
                              data={effectiveAudienceTopCities}
                              xKey="city"
                              series={audienceTopCitiesSeries}
                              height={220}
                              layout="vertical"
                              showLegend={false}
                              yAxisWidth={80}
                              barSize={10}
                            />
                            <div className="campaignPerformanceLegend">
                              {audienceTopCitiesSeries.map((series) => (
                                <div key={series.key} className="campaignPerformanceLegendItem">
                                  <span
                                    className="campaignPerformanceLegendSwatch"
                                    style={{ backgroundColor: `var(${series.colorVar})` }}
                                  />
                                  <span>{series.label}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="audienceInsightBlock">
                            <div className="audienceInsightBlockLabel">Insight</div>
                            <p className="audienceInsightBlockText">{audienceTopCitiesInsight}</p>
                          </div>
                        </ChartPanel>
                    )}

                    {!audienceDemographicsLoading && (
                        <ChartPanel
                          dataChartId="audienceInsights"
                          title="Top Countries by Platform"
                          subtitle="Hometown country data from Facebook and Instagram"
                          className="audienceDemographicsGrid__span"
                        >
                          <div className="audienceInsightChart">
                            <BaseBarChart
                              data={displayAudienceTopCountriesChart}
                              xKey="country"
                              series={effectiveAudienceTopCountriesSeries}
                              height={220}
                              showLegend={false}
                              showTrend={false}
                              barSize={10}
                              yAxisLabel="Facebook"
                              rightYAxisLabel="Instagram"
                              yTickFormatter={formatCompactNumber}
                            />
                            <div className="campaignPerformanceLegend">
                              {effectiveAudienceTopCountriesSeries.map((series) => (
                                <div key={series.key} className="campaignPerformanceLegendItem">
                                  <span
                                    className="campaignPerformanceLegendSwatch"
                                    style={{ backgroundColor: `var(${series.colorVar})` }}
                                  />
                                  <span>{series.label}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="audienceInsightBlock">
                            <div className="audienceInsightBlockLabel">Insight</div>
                            <p className="audienceInsightBlockText">{audienceTopCountriesInsight}</p>
                          </div>
                        </ChartPanel>
                    )}
                  </div>

                  {!audienceDemographicsLoading && audienceDemographicData?.geographic?.length > 0 && (
                    <ChartPanel
                      dataChartId="audienceInsights"
                      title="Geographic Distribution"
                      subtitle="Audience location breakdown with interactive map"
                      className="audienceGeographicPanel"
                    >
                      <AudienceGeographicDistribution
                        data={audienceDemographicData}
                        platform={audienceDemographicPlatformEffective}
                        platformLabel={audienceDemographicPlatformEffective === 'all' ? 'All' : platformLabels[audienceDemographicPlatformEffective]}
                      />
                      <div className="audienceInsightBlock">
                        <div className="audienceInsightBlockLabel">Insight</div>
                        <p className="audienceInsightBlockText">{audienceGeographicInsight}</p>
                      </div>
                    </ChartPanel>
                  )}
                </>
              )}
            </div>
          </div>
            </LazyMount>
          )}

          {activeTab === 'analytics' && hasAnyVisibleSection && !isComingSoonAnalyticsTab(analyticsSectionTab) && (analyticsSectionTab === 'monitoring' || analyticsSectionTab === 'all') && showMonitoringSection && (
            <LazyMount
              disabled={analyticsSectionTab !== 'all' || exportBusy}
              onVisible={() => markAnalyticsSectionReady('monitoring')}
              placeholderHeight={560}
            >
            <div className="dashboardSection dashboardSection--monitoring">
            <div className="dashboardSectionHeader">
              <div>
                <div className="dashboardSectionTitleRow">
                  <div className="dashboardSectionTitle">Monitoring & Engagement</div>
                  <KpiInfoTooltip
                    content="Message volume, sentiment, alerts, time-of-day activity, and heatmap insights."
                    aria-label="Monitoring & Engagement info"
                    portaled
                    placement="right"
                  />
                </div>
              </div>
            </div>
            <div className="monitoringSection">
              {isChartVisible('messages') && (
                <ChartPanel
                  dataChartId="messages"
                  loading={messagesReportLoading}
                  className="monitoringPanel monitoringPanel--messages"
                  title="Messages"
                  subtitle="Community activity read by the Monitoring team"
                >
                <div className="sentimentSummary" role="group" aria-label="Filter messages chart by platform">
                  <button
                    type="button"
                    className={[
                      'sentimentSummaryCard',
                      'sentimentSummaryCard--total',
                      'sentimentSummaryCard--filterable',
                    ].join(' ')}
                    onClick={() => setMessageChannelFilter('all')}
                  >
                    <div className="sentimentSummaryLabel">Total</div>
                    <div className="sentimentSummaryValue">{formatNumber(totalMonitoringShare)}</div>
                  </button>
                  {monitoringShareWithPrevious.map((item, index) => (
                    <button
                      key={item.name}
                      type="button"
                      className={[
                        'sentimentSummaryCard',
                        MESSAGES_SUMMARY_PLATFORM_TONE_CLASSES[index % MESSAGES_SUMMARY_PLATFORM_TONE_CLASSES.length],
                        'sentimentSummaryCard--filterable',
                        messageChannelFilter === item.name ? 'sentimentSummaryCard--active' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handleMessageFilterClick(item.name)}
                      aria-pressed={messageChannelFilter === item.name}
                    >
                      <div className="sentimentSummaryLabel">{item.name}</div>
                      <div className="sentimentSummaryValue">
                        {formatNumber(item.value)}{' '}
                        <span className="sentimentSummaryValueMeta">
                          ({formatMonitoringPercent(item.value)})
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="monitoringBody">
                  <div className="campaignPerformanceChart">
                    <BaseLineChart
                      data={monitoringSeriesForDisplay}
                      xKey="date"
                      series={activeMonitoringSeries}
                      height={260}
                      showLegend={false}
                    />
                  </div>

                <div className="monitoringSummaryCard">
                    <div className="monitoringDonut">
                      <BasePieChart
                        data={monitoringShareForDisplay}
                        xKey="name"
                        series={monitoringPieSeries}
                      height={260}
                        showTooltip
                    showLabels
                    sleek
                    innerRadius={72}
                    outerRadius={86}
                        onSliceClick={handleMessageFilterClick}
                        activeKey={messageChannelFilter}
                      />
                      <div className="monitoringDonutCenter">
                        <div>Total Messages</div>
                        <strong>{formatNumber(totalMonitoringShare)}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="messagesLegendGroup">
                  <div className="campaignPerformanceLegend">
                    {monitoringPieSeries.map((series) => (
                      <div key={series.key} className="campaignPerformanceLegendItem">
                        <span
                          className="campaignPerformanceLegendSwatch"
                          style={{ backgroundColor: `var(${series.colorVar})` }}
                        />
                        <span>{series.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="messagesInsightFull">
                  <div className="audienceInsightBlock">
                    <div className="audienceInsightBlockLabel">Insight</div>
                    <p className="audienceInsightBlockText">
                      {buildChartInsightText("messages", {
                        totalMonitoringShare,
                        monitoringShare: baseMonitoringShare,
                        topMessagePlatform,
                      })}
                    </p>
                  </div>
                </div>

              </ChartPanel>
              )}

              {isChartVisible('messageSentiment') && (
                <ChartPanel
                  dataChartId="messageSentiment"
                  loading={sentimentsReportLoading}
                  className="monitoringPanel monitoringPanel--sentiment"
                  title="Message Sentiment"
                  subtitle="AI-powered sentiment categorization"
                >
            <div className="sentimentSummary">
              <div className="sentimentSummaryCard sentimentSummaryCard--total">
                <div className="sentimentSummaryLabel">Total</div>
                <div className="sentimentSummaryValue">{formatNumber(sentimentTotalMessages)}</div>
              </div>
              <div className="sentimentSummaryCard sentimentSummaryCard--positive">
                <div className="sentimentSummaryLabel">Positive</div>
                <div className="sentimentSummaryValue">
                  {formatNumber(sentimentPositiveCount)}{' '}
                  <span className="sentimentSummaryValueMeta">({formatSentimentPercentFromCount(sentimentPositiveCount)})</span>
                </div>
              </div>
              <div className="sentimentSummaryCard sentimentSummaryCard--neutral">
                <div className="sentimentSummaryLabel">Neutral</div>
                <div className="sentimentSummaryValue">
                  {formatNumber(sentimentNeutralCount)}{' '}
                  <span className="sentimentSummaryValueMeta">({formatSentimentPercentFromCount(sentimentNeutralCount)})</span>
                </div>
              </div>
              <div className="sentimentSummaryCard sentimentSummaryCard--negative">
                <div className="sentimentSummaryLabel">Negative</div>
                <div className="sentimentSummaryValue">
                  {formatNumber(sentimentNegativeCount)}{' '}
                  <span className="sentimentSummaryValueMeta">({formatSentimentPercentFromCount(sentimentNegativeCount)})</span>
                </div>
              </div>
              <div className="sentimentSummaryCard sentimentSummaryCard--unknown">
                <div className="sentimentSummaryLabel">Unknown</div>
                <div className="sentimentSummaryValue">
                  {formatNumber(sentimentUnknownCount)}{' '}
                  <span className="sentimentSummaryValueMeta">({formatSentimentPercentFromCount(sentimentUnknownCount)})</span>
                </div>
              </div>
              <div className="sentimentSummaryCard sentimentSummaryCard--mixed">
                <div className="sentimentSummaryLabel">Mixed</div>
                <div className="sentimentSummaryValue">
                  {formatNumber(sentimentMixedCount)}{' '}
                  <span className="sentimentSummaryValueMeta">({formatSentimentPercentFromCount(sentimentMixedCount)})</span>
                </div>
              </div>
            </div>
                <div className="monitoringBody">
                  <div className="campaignPerformanceChart">
                    <BaseLineChart
                      data={sentimentSeriesForDisplay}
                      xKey="date"
                      series={activeSentimentSeries}
                      height={260}
                      showLegend={false}
                      tooltipClassName="rg-chart__tooltip--campaign"
                      tooltipExtraContent={({ payload }) => {
                        if (!payload || payload.length === 0) return null
                        const total = payload.reduce((sum, p) => sum + (Number(p.value) || 0), 0)
                        return (
                          <div className="rg-chart__tooltip-extra">
                            <div className="rg-chart__tooltip-row">
                              <span>Total</span>
                              <strong>{formatNumber(total)} messages</strong>
                            </div>
                          </div>
                        )
                      }}
                    />
                  </div>

                <div className="monitoringSummaryCard">
                    <div className="monitoringDonut">
                      <BasePieChart
                        data={sentimentShareForDisplay}
                        xKey="name"
                        series={sentimentPieSeries}
                        height={260}
                        showTooltip
                    showLabels
                    sleek
                        innerRadius={70}
                        outerRadius={84}
                        onSliceClick={handleSentimentFilterClick}
                        activeKey={sentimentFilter}
                      />
                      <div className="monitoringDonutCenter">
                        <div>Net Sentiment</div>
                        <strong>{netSentiment >= 0 ? '+' : ''}{netSentiment}</strong>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="sentimentInsightFull">
                  <div className="campaignPerformanceLegend sentimentLegendGroup">
                    {sentimentPieSeries.map((series) => (
                      <div key={series.key} className="campaignPerformanceLegendItem">
                        <span
                          className="campaignPerformanceLegendSwatch"
                          style={{ backgroundColor: `var(${series.colorVar})` }}
                        />
                        <span>{series.label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="audienceInsightBlock">
                    <div className="audienceInsightBlockLabel">Insight</div>
                    <p className="audienceInsightBlockText">
                      {buildChartInsightText("messageSentiment", {
                        sentimentTotalMessages,
                        sentimentPositivePercent: positivePercent,
                        sentimentNegativePercent: negativePercent,
                        sentimentSpikeDates,
                      })}
                    </p>
                  </div>
                  {SHOW_SENTIMENT_THEME_DRIVERS &&
                    (sentimentKeywordsPositive?.length > 0 || sentimentKeywordsNegative?.length > 0) && (
                    <SentimentThemeDrivers
                      positiveThemes={sentimentKeywordsPositive || []}
                      negativeThemes={sentimentKeywordsNegative || []}
                      totalPositive={sentimentPositiveCount}
                      totalNegative={sentimentNegativeCount}
                      onThemeClick={({ term, sentiment }) => {
                        setSelectedTheme(term)
                        setSelectedThemeSentiment(sentiment)
                        setSentimentDrawerOpen(true)
                      }}
                    />
                  )}
                </div>
                <SentimentThemeDrawer
                  isOpen={sentimentDrawerOpen}
                  onClose={() => {
                    setSentimentDrawerOpen(false)
                    setSelectedTheme(null)
                    setSelectedThemeSentiment(null)
                  }}
                  selectedTheme={selectedTheme}
                  selectedSentiment={selectedThemeSentiment}
                  messages={[]}
                  dateRange={dateRange}
                  platformFilter={platformPulsePlatforms.size === 0 ? 'all' : Array.from(platformPulsePlatforms)}
                  dateRangeLabel={formatRangeLabel(dateRange) || 'Last 30 days'}
                />

              </ChartPanel>
              )}
            </div>

            {isChartVisible('alerts') && (
              <ChartPanel
                dataChartId="alerts"
                loading={alertsReportLoading}
                className="alertsSection"
                title="Alerts"
                subtitle="Customer service situations emailed to on-property staff"
              >
              <div className="alertsSummaryCards">
                <div
                  className="alertsSummaryCard"
                  style={{ '--alert-accent': ALERT_SUMMARY_TOTAL_ACCENT }}
                >
                  <div className="paidMediaSummaryLabel">Total Alerts</div>
                  <div className="paidMediaSummaryValue">{formatNumber(totalAlerts)}</div>
                  <div
                    className="paidMediaSummaryFootnote"
                    title={previousPeriodTooltip || undefined}
                  >
                    {formatDeltaPercent(totalAlerts, previousTotalAlerts)}
                  </div>
                </div>
                {effectiveAlertCategories.map((category) => {
                  const pieSeries = effectiveAlertCategoryPieSeries.find(
                    (s) => s.label === category.name || s.key === category.name,
                  )
                  const accent = pieSeries ? `var(${pieSeries.colorVar})` : 'var(--chart-1)'
                  return (
                    <div
                      key={category.name}
                      className="alertsSummaryCard"
                      style={{ '--alert-accent': accent }}
                    >
                      <div className="paidMediaSummaryLabel">{category.name}</div>
                      <div className="paidMediaSummaryValue">{formatNumber(category.count)}</div>
                      <div
                        className="paidMediaSummaryFootnote"
                        title={previousPeriodTooltip || undefined}
                      >
                        {formatDeltaPercent(
                          category.count,
                          useApiAlerts
                            ? category.previous ?? 0
                            : category.previous ?? previousAlertCategoryCount(category.name),
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div
                className={`monitoringBody alertsChartsBody${alertsChartsEmpty ? ' alertsChartsBody--empty' : ''}${
                  !alertsChartsEmpty && effectiveTotalAlertReportingCategories <= 0
                    ? ' alertsChartsBody--noCategories'
                    : ''
                }`}
              >
                {alertsChartsEmpty ? (
                  <div className="alertsChartsEmptyState" role="status">
                    No data available
                  </div>
                ) : (
                  <>
                <div className="campaignPerformanceChart alertsBarChart">
                  <ScrollableStackedBarChart
                    data={effectiveAlertsChartData}
                    xKey="date"
                    series={effectiveAlertCategorySeries}
                    height={260}
                    xTickFormatter={formatShortDate}
                    labelFormatter={formatShortDate}
                    barSize={10}
                    barCategoryGap="30%"
                    barGap={3}
                    barSlotWidth={44}
                  />
                </div>

                <div className="alertsDonutChart alertsDonutChart--alerts">
                  <div className="monitoringDonut">
                    <BasePieChart
                      data={effectiveAlertCategories}
                      xKey="name"
                      series={effectiveAlertCategoryPieSeries}
                      valueKey="count"
                      height={260}
                      showTooltip
                      valueFormatter={formatNumber}
                      sleek
                      innerRadius={58}
                      outerRadius={72}
                      showLabels
                      labelOffset={12}
                      chartMargin={{ top: 6, right: 28, bottom: 6, left: 28 }}
                      onSliceClick={() => {}}
                    />
                    <div className="monitoringDonutCenter">
                      <div>Total Alerts</div>
                      <strong>{totalAlerts}</strong>
                    </div>
                  </div>
                </div>

                {effectiveTotalAlertReportingCategories > 0 ? (
                <div className="alertsDonutChart alertsDonutChart--categories">
                  <div className="monitoringDonut">
                    <BasePieChart
                      data={effectiveAlertReportingCategories}
                      xKey="name"
                      series={effectiveAlertReportingCategoryPieSeries}
                      valueKey="count"
                      height={260}
                      showTooltip
                      valueFormatter={formatNumber}
                      sleek
                      innerRadius={58}
                      outerRadius={72}
                      showLabels
                      labelOffset={12}
                      chartMargin={{ top: 6, right: 28, bottom: 6, left: 28 }}
                      onSliceClick={() => {}}
                    />
                    <div className="monitoringDonutCenter">
                      <div>Categories</div>
                      <strong>{effectiveTotalAlertReportingCategories}</strong>
                    </div>
                  </div>
                </div>
                ) : null}

                <div className="alertsLegendGroup">
                  <div className="alertsLegendSection alertsLegendSection--types">
                    <div className="alertsLegendTitle">Alert Types</div>
                    <div className="campaignPerformanceLegend alertsLegendItems">
                      {effectiveAlertCategoryPieSeries.map((series) => (
                        <div key={series.key} className="campaignPerformanceLegendItem">
                          <span
                            className="campaignPerformanceLegendSwatch"
                            style={{ backgroundColor: `var(${series.colorVar})` }}
                          />
                          <span>{series.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {effectiveAlertReportingCategoryPieSeries.length > 0 && (
                  <div className="alertsLegendSection alertsLegendSection--categories">
                    <div className="alertsLegendTitle">Categories</div>
                    <div className="campaignPerformanceLegend alertsLegendItems">
                      {effectiveAlertReportingCategoryPieSeries.map((series) => (
                        <div key={series.key} className="campaignPerformanceLegendItem">
                          <span
                            className="campaignPerformanceLegendSwatch"
                            style={{ backgroundColor: `var(${series.colorVar})` }}
                          />
                          <span>{series.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
                  </>
                )}
                <div className="alertsInsightFull">
                  <div className="audienceInsightBlock">
                    <div className="audienceInsightBlockLabel">Insight</div>
                    <p className="audienceInsightBlockText">
                      {buildChartInsightText("alerts", {
                        totalAlerts,
                        previousTotalAlerts,
                        alertSpikeDates,
                      })}
                    </p>
                  </div>
                </div>
              </div>

            </ChartPanel>
            )}

            {isChartVisible('timeOfDay') && (
              <div className="monitoringGridCards">
                <ChartPanel dataChartId="timeOfDay" title="Time of Day" size="sm" className="timeOfDayCard" loading={monitoringTimeOfDayLoading}>
                  <div className="timeOfDayGrid">
                    <div className="timeOfDayChartsSection">
                      <div className="timeOfDayPanels">
                        <div className="timeOfDayPanel">
                          <div className="timeOfDayPanelTitle">Messages</div>
                          <div className="timeOfDayBody">
                            {messagesTimeOfDayEmpty ? (
                              <div className="timeOfDayChartsEmptyState" role="status">
                                No data available
                              </div>
                            ) : (
                            <div className="monitoringDonut">
                            <BasePieChart
                              data={timeOfDayMessageData}
                              xKey="name"
                              series={timeOfDaySeriesMessages}
                              height={280}
                              showTooltip={false}
                              showLabels
                              sleek
                              innerRadius={72}
                              outerRadius={86}
                              labelOffset={18}
                              chartMargin={{ top: 36, right: 84, bottom: 52, left: 84 }}
                              valueFormatter={(value) => formatPercent(value, 0)}
                            />
                            {topMessagesTimeOfDay && (
                              <div className="monitoringDonutCenter monitoringDonutCenter--small">
                                <div>Most Active</div>
                                <strong>{topMessagesTimeOfDay.name}</strong>
                                <div>{formatPercent(topMessagesTimeOfDay.value, 0)}</div>
                              </div>
                            )}
                            </div>
                            )}
                          </div>
                        </div>
                        <div className="timeOfDayPanel">
                          <div className="timeOfDayPanelTitle">Alerts</div>
                          <div className="timeOfDayBody">
                            {alertsTimeOfDayEmpty ? (
                              <div className="timeOfDayChartsEmptyState" role="status">
                                No data available
                              </div>
                            ) : (
                            <div className="monitoringDonut">
                            <BasePieChart
                              data={timeOfDayAlertData}
                              xKey="name"
                              series={timeOfDaySeriesAlerts}
                              height={280}
                              showTooltip={false}
                              showLabels
                              sleek
                              innerRadius={72}
                              outerRadius={86}
                              labelOffset={18}
                              chartMargin={{ top: 36, right: 84, bottom: 52, left: 84 }}
                              valueFormatter={(value) => formatPercent(value, 0)}
                            />
                            {topAlertsTimeOfDay && (
                              <div className="monitoringDonutCenter monitoringDonutCenter--small">
                                <div>Most Active</div>
                                <strong>{topAlertsTimeOfDay.name}</strong>
                                <div>{formatPercent(topAlertsTimeOfDay.value, 0)}</div>
                              </div>
                            )}
                            </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="timeOfDayLegend timeOfDayLegend--single">
                        {timeOfDaySeriesMessages.map((entry) => {
                          const config = timeOfDaySeriesMessages.find((item) => item.key === entry.key)
                          return (
                            <div key={entry.key} className="monitoringLegendItem">
                              <span className="monitoringLegendDot" style={{ backgroundColor: `var(${config?.colorVar || '--chart-1'})` }} />
                              <span>{entry.label}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                    <div className="timeOfDaySummary">
                      <div className="timeOfDaySummaryHeader">
                        <span className="timeOfDaySummaryIcon">AI</span>
                        <div className="timeOfDaySummaryHeading">Time of Day Insights</div>
                      </div>
                      <div className="timeOfDaySummarySection">
                        <div className="timeOfDaySummaryTitle">Key takeaways</div>
                        <ul className="timeOfDaySummaryList">
                          {topMessagesTimeOfDay && topMessagesTimeOfDay.value > 0 ? (
                            <li>
                              Messages are most active during {topMessagesTimeOfDay.name} (
                              {formatPercent(topMessagesTimeOfDay.value, 0)} of volume).
                            </li>
                          ) : (
                            <li>No message activity recorded for this date range.</li>
                          )}
                          {topAlertsTimeOfDay && topAlertsTimeOfDay.value > 0 ? (
                            <li>
                              Alerts peak during {topAlertsTimeOfDay.name} (
                              {formatPercent(topAlertsTimeOfDay.value, 0)} of volume).
                            </li>
                          ) : (
                            <li>No alerts recorded for this date range.</li>
                          )}
                        </ul>
                      </div>
                      <div className="timeOfDaySummarySection">
                        <div className="timeOfDaySummaryTitle">Recommended actions</div>
                        <div className="timeOfDaySummaryText">
                          {topMessagesTimeOfDay?.value > 0
                            ? `Staff coverage for ${topMessagesTimeOfDay.name} handles most inbound messages.`
                            : 'Adjust the date range or property to review time-of-day patterns.'}
                          {topAlertsTimeOfDay?.value > 0
                            ? ` Plan extra capacity during ${topAlertsTimeOfDay.name} when alerts spike.`
                            : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                </ChartPanel>
              </div>
            )}

            {isChartVisible('messageHeatmap') && (
              <MessageHeatmapUnifiedWidget
                weeklyHours={effectiveHeatmapWeeklyHours}
                weeklyDays={effectiveHeatmapWeeklyDays}
                weeklyValues={effectiveHeatmapWeeklyValues}
                calendarDates={effectiveHeatmapCalendarDates}
                hourlyDailyMatrix={effectiveHeatmapDailyHourly}
                monthlyHourlyBundle={effectiveHeatmapMonthlyHourly}
                dateRangeLabel={formatRangeLabel(dateRange)}
                propertyTimezone={propertyChartTimezone}
                loading={messagesHeatmapLoading}
              />
            )}
          </div>
            </LazyMount>
          )}

        {activeTab === 'goals' && showGoalsSection && (
          <div className="dashboardSection dashboardSection--goals">
            {(goalsPersistPending || hasActiveGoals) && (
              <div className="goalsProgressSection goalsProgressSection--outsidePanel">
                {goalsPersistPending || goalsYtdPerformancePending ? (
                  <div className="goalsTabLoadingState" role="status" aria-live="polite">
                    <SohoLoader message="Loading goal progress…" />
                  </div>
                ) : (
                  <div className="goalsProgressGrid">
                    {committedGoalsList.map((goal) => (
                      <GoalCard
                        key={goal.id}
                        goal={goal}
                        formatGoalValue={formatGoalValue}
                        isPeriodExpired={goal.isPeriodExpired}
                        periodEndedAcknowledged={goal.periodEndedAcknowledged}
                        periodEndLabel={goal.periodEndLabel}
                        onExtend={
                          goal.isPeriodExpired && goal.periodEndedAcknowledged
                            ? () => requestExtendGoal(goal.id)
                            : undefined
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            <GoalsSummaryHeader
              showSummaryPills={hasActiveGoals && !goalsPersistPending}
              activeGoalsCount={committedGoalsList.length}
              status={goalsStrategyStatus ?? 'no-active'}
              timeRemainingText={goalsTimeRemainingText ?? ''}
              onOpenGoalsHelp={() => {
                dispatch(fetchMetricGoalTypes())
                setGoalsPreActivationDrawerOpen(true)
              }}
            />
            <ChartPanel
              dataChartId="socialGoalsOverview"
              className="socialGoalsSection"
              loading={goalsTabDataLoading}
              loadingMessage="Loading goals…"
            >
              <div className="goalsOverview goalsContentWrapper goalsOverview--progressFirst">
                {!goalsActivated && (
                  <GoalsZeroState
                    onPresetSelect={applyGoalsPreset}
                    selectedGoalsPreset={selectedGoalsPreset}
                    presets={resolvedGoalPresets.map((p) => ({ id: p.id, name: p.name }))}
                    typesLoading={metricGoalTypesState.status === 'loading'}
                    hidePlaceholder={Boolean(selectedGoalsPreset || goalsShowSetup)}
                  />
                )}

                {(goalsActivated || selectedGoalsPreset || goalsShowSetup) && (
                  <GoalsConfigPanel expanded={goalsActivated || goalsConfigExpandedResolved}>
                {!goalsActivated && (goalsShowSetup || selectedGoalsPreset) && (
                  <div ref={goalsBuilderRef} className="goalsZeroStateSetup">
                    <div className="goalsSetupHeader">
                      <div className="goalsSetupHeaderRow">
                        <div className="goalsToolbarGroup goalsToolbarGroup--goalTypeAndHelp">
                          <div ref={goalsTypeRef} className="goalsSelect">
                            <button
                              type="button"
                              id="goalsTypeSelect"
                              className="goalsSelectButton"
                              aria-haspopup="listbox"
                              aria-expanded={goalsTypeOpen}
                              onClick={() => setGoalsTypeOpen((prev) => !prev)}
                            >
                              <span className="goalsSelectLabel">Goal type</span>
                              <span className={`goalsSelectValue ${!selectedGoalsPreset ? 'goalsSelectValue--placeholder' : ''}`}>
                                {selectedGoalsPresetLabel}
                              </span>
                            </button>
                            {goalsTypeOpen && (
                              <div className="goalsSelectMenu" role="listbox" aria-label="Goal type">
                                {resolvedGoalPresets.map((preset) => (
                                  <button
                                    key={preset.id}
                                    type="button"
                                    className={`goalsSelectOption ${selectedGoalsPreset === preset.id ? 'goalsSelectOption--active' : ''}`}
                                    onClick={() => {
                                      applyGoalsPreset(preset.id)
                                      setGoalsTypeOpen(false)
                                    }}
                                  >
                                    {preset.name}
                                  </button>
                                ))}
                                {customGoals.length > 0 && (
                                  <div className="goalsSelectDivider" aria-hidden="true" />
                                )}
                                {customGoals.map((goal) => (
                                  <button
                                    key={goal.id}
                                    type="button"
                                    className={`goalsSelectOption ${selectedGoalsPreset === goal.id ? 'goalsSelectOption--active' : ''}`}
                                    onClick={() => {
                                      applyGoalsPreset(goal.id)
                                      setGoalsTypeOpen(false)
                                    }}
                                  >
                                    {`Custom: ${goal.name}`}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="goalsToolbarActions">
                          <button
                            type="button"
                            className="goalsCancelButton"
                            onClick={handleGoalsSetupCancel}
                            aria-label="Cancel setup"
                          >
                            Cancel
                          </button>
                          <button
                            className="goalsApplyButton"
                            type="button"
                            disabled={goalsActivateDisabled || goalsPersistBusy}
                            onClick={() => handleGoalsSave({ fromActivation: true })}
                          >
                            {goalsPersistBusy ? 'Saving…' : 'Save & Activate'}
                          </button>
                        </div>
                      </div>
                      {selectedGoalsPreset && (
                        <div className="goalsSetupSummary">
                          <p className="goalsSetupSummaryLine">
                            {selectedGoalMetrics.size} active · {goalsTimeRangeLabel} · {goalsPlatformFilterLabel}
                          </p>
                          {goalsCategoryCoverage.length > 0 && (
                            <div className="goalsSetupSummaryChips">
                              {goalsCategoryCoverage.map((label) => (
                                <span key={label} className="goalsSetupSummaryChip">
                                  {label}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="goalsBuilder goalsBuilder--inGoalTypeSetup">
                      <div className="goalsConfigurator goalsConfigurator--inGoalTypeSetup">
                        <GoalsMetricsConfigurator
                          isGoalsReadOnly={isGoalsReadOnly}
                          goalsMetricListRef={goalsMetricListRef}
                          filteredGoalMetricGroups={goalsConfiguratorMetricGroups}
                          selectedGoalMetrics={selectedGoalMetrics}
                          expandedGoalGroups={expandedGoalGroups}
                          toggleGoalMetricGroup={toggleGoalMetricGroup}
                          toggleGoalMetricGroupExpanded={toggleGoalMetricGroupExpanded}
                          toggleGoalMetric={toggleGoalMetric}
                          goalMetricTargets={goalMetricTargets}
                          setGoalMetricTargets={setGoalMetricTargets}
                          goalMetricPeriods={goalMetricPeriods}
                          setGoalMetricPeriods={setGoalMetricPeriods}
                          goalMetricReach={goalMetricReach}
                          setGoalMetricReach={setGoalMetricReach}
                          goalMetricPeriodStarts={goalMetricPeriodStarts}
                          setGoalMetricPeriodStarts={setGoalMetricPeriodStarts}
                          goalMetricPeriodValues={goalMetricPeriodValues}
                          setGoalMetricPeriodValues={setGoalMetricPeriodValues}
                          editingMetricIds={editingMetricIds}
                          setEditingMetricIds={setEditingMetricIds}
                          onMetricGoalDone={handleMetricGoalDone}
                          onMetricGoalEditStart={handleMetricGoalEditStart}
                          onMetricGoalCancel={handleMetricGoalCancel}
                          formatGoalValue={formatGoalValue}
                          metricsSectionMode="setup"
                          setupPreviewMode={isGoalsSetupPreview}
                          showMetricFilters
                          goalsTimeRef={goalsTimeRef}
                          goalsTimeOpen={goalsTimeOpen}
                          setGoalsTimeOpen={setGoalsTimeOpen}
                          goalsTimeRange={goalsTimeRange}
                          setGoalsTimeRange={setGoalsTimeRange}
                          goalsTimeRangeLabel={goalsTimeRangeLabel}
                          goalsPlatformFilterRef={goalsPlatformFilterRef}
                          goalsPlatformFilterOpen={goalsPlatformFilterOpen}
                          setGoalsPlatformFilterOpen={setGoalsPlatformFilterOpen}
                          goalsPlatformFilter={goalsPlatformFilter}
                          setGoalsPlatformFilter={setGoalsPlatformFilter}
                          goalsPlatformFilterLabel={goalsPlatformFilterLabel}
                          goalsPlatformOptions={goalsPlatformOptions}
                        />
                      </div>
                    </div>
                  </div>
                )}

              {showGoalsConfigurator && (
                <div className="goalsBuilder" ref={goalsBuilderRef}>
                  <div className="goalsConfigurator">
                    <GoalsMetricsConfigurator
                      isGoalsReadOnly={isGoalsReadOnly}
                      goalsMetricListRef={goalsMetricListRef}
                      filteredGoalMetricGroups={filteredGoalMetricGroups}
                      selectedGoalMetrics={selectedGoalMetrics}
                      expandedGoalGroups={expandedGoalGroups}
                      toggleGoalMetricGroup={toggleGoalMetricGroup}
                      toggleGoalMetricGroupExpanded={toggleGoalMetricGroupExpanded}
                      toggleGoalMetric={toggleGoalMetric}
                      goalMetricTargets={goalMetricTargets}
                      setGoalMetricTargets={setGoalMetricTargets}
                      goalMetricPeriods={goalMetricPeriods}
                      setGoalMetricPeriods={setGoalMetricPeriods}
                      goalMetricReach={goalMetricReach}
                      setGoalMetricReach={setGoalMetricReach}
                      goalMetricPeriodStarts={goalMetricPeriodStarts}
                      setGoalMetricPeriodStarts={setGoalMetricPeriodStarts}
                      goalMetricPeriodValues={goalMetricPeriodValues}
                      setGoalMetricPeriodValues={setGoalMetricPeriodValues}
                      goalPeriodEndedAcknowledged={goalPeriodEndedAcknowledged}
                      onRequestExtendGoal={requestExtendGoal}
                      editingMetricIds={editingMetricIds}
                      setEditingMetricIds={setEditingMetricIds}
                      onMetricGoalDone={handleMetricGoalDone}
                      onMetricGoalEditStart={handleMetricGoalEditStart}
                      onMetricGoalCancel={handleMetricGoalCancel}
                      formatGoalValue={formatGoalValue}
                      metricsSectionMode="edit"
                      activeStrategyTitle={selectedGoalsPresetLabel}
                      onModifyGoals={openGoalsConfigForEdit}
                      onCancelEdit={handleGoalsCancel}
                      onSaveEdit={() => handleGoalsSave()}
                      saveEditDisabled={goalsPersistBusy}
                      onDeleteGoal={handleDeleteGoal}
                      targetsHelpTooltip={
                        isGoalsReadOnly
                          ? 'Metrics and targets for this goal type. Use Modify goals to change selection or targets.'
                          : 'Add or remove metrics and edit targets, then Save changes.'
                      }
                      showMetricFilters
                      goalsTimeRef={goalsTimeRef}
                      goalsTimeOpen={goalsTimeOpen}
                      setGoalsTimeOpen={setGoalsTimeOpen}
                      goalsTimeRange={goalsTimeRange}
                      setGoalsTimeRange={setGoalsTimeRange}
                      goalsTimeRangeLabel={goalsTimeRangeLabel}
                      goalsPlatformFilterRef={goalsPlatformFilterRef}
                      goalsPlatformFilterOpen={goalsPlatformFilterOpen}
                      setGoalsPlatformFilterOpen={setGoalsPlatformFilterOpen}
                      goalsPlatformFilter={goalsPlatformFilter}
                      setGoalsPlatformFilter={setGoalsPlatformFilter}
                      goalsPlatformFilterLabel={goalsPlatformFilterLabel}
                      goalsPlatformOptions={goalsPlatformOptions}
                    />
                  </div>
                </div>
              )}
                </GoalsConfigPanel>
                )}
              </div>
            </ChartPanel>
          </div>
        )}
        </div>
      </div>
      <GoalsPreActivationDrawer
        isOpen={goalsPreActivationDrawerOpen}
        onClose={() => setGoalsPreActivationDrawerOpen(false)}
        referenceProps={{
          goalPresets: resolvedGoalPresets,
          customGoals,
          goalMetricGroups,
          goalMetricLookup,
          formatGoalValue,
        }}
      />
      {goalPeriodEndedNoticeGoal && (
        <div
          className="dashboardGoalsDeleteConfirmOverlay"
          role="presentation"
          onClick={() => acknowledgeGoalPeriodEnded(goalPeriodEndedNoticeGoal.id)}
        >
          <div
            className="dashboardGoalsDeleteConfirmDialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dashboardGoalsPeriodEndedTitle"
            aria-describedby="dashboardGoalsPeriodEndedDesc"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="dashboardGoalsPeriodEndedTitle" className="dashboardGoalsDeleteConfirmTitle">
              Time period reached
            </p>
            <p id="dashboardGoalsPeriodEndedDesc" className="dashboardGoalsDeleteConfirmText">
              The tracking period for <strong>{goalPeriodEndedNoticeGoal.name}</strong> ended on{' '}
              {goalPeriodEndedNoticeGoal.periodEndLabel}. You can extend this goal to start a new period.
            </p>
            <div className="dashboardGoalsDeleteConfirmActions">
              <button
                type="button"
                className="dashboardGoalsDeleteConfirmPrimary dashboardGoalsExtendConfirmPrimary"
                onClick={() => acknowledgeGoalPeriodEnded(goalPeriodEndedNoticeGoal.id)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {goalExtendConfirmGoal && (
        <div
          className="dashboardGoalsDeleteConfirmOverlay"
          role="presentation"
          onClick={() => setGoalExtendConfirmMetricId(null)}
        >
          <div
            className="dashboardGoalsDeleteConfirmDialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dashboardGoalsExtendConfirmTitle"
            aria-describedby="dashboardGoalsExtendConfirmDesc"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="dashboardGoalsExtendConfirmTitle" className="dashboardGoalsDeleteConfirmTitle">
              Extend this goal?
            </p>
            <p id="dashboardGoalsExtendConfirmDesc" className="dashboardGoalsDeleteConfirmText">
              Start a new {getGoalMetricPeriodExtendLabel(goalExtendConfirmGoal.period)} for{' '}
              <strong>{goalExtendConfirmGoal.name}</strong>? Progress will carry over and the date range will
              reset from today.
            </p>
            <div className="dashboardGoalsDeleteConfirmActions">
              <button
                type="button"
                className="dashboardGoalsDeleteConfirmCancel"
                onClick={() => setGoalExtendConfirmMetricId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dashboardGoalsDeleteConfirmPrimary dashboardGoalsExtendConfirmPrimary"
                onClick={confirmExtendGoal}
              >
                Extend goal
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteGoalConfirm && (
        <div
          className="dashboardGoalsDeleteConfirmOverlay"
          role="presentation"
          onClick={() => setShowDeleteGoalConfirm(false)}
        >
          <div
            className="dashboardGoalsDeleteConfirmDialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dashboardGoalsDeleteConfirmTitle"
            aria-describedby="dashboardGoalsDeleteConfirmDesc"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="dashboardGoalsDeleteConfirmTitle" className="dashboardGoalsDeleteConfirmTitle">
              Delete this goal?
            </p>
            <p id="dashboardGoalsDeleteConfirmDesc" className="dashboardGoalsDeleteConfirmText">
              This removes your active goal and all selected metrics. You can choose a new goal type and metrics
              afterward.
            </p>
            <div className="dashboardGoalsDeleteConfirmActions">
              <button
                type="button"
                className="dashboardGoalsDeleteConfirmCancel"
                onClick={() => setShowDeleteGoalConfirm(false)}
              >
                Cancel
              </button>
              <button type="button" className="dashboardGoalsDeleteConfirmPrimary" onClick={confirmDeleteGoal}>
                Delete goal
              </button>
            </div>
          </div>
        </div>
      )}
      {customGoalModalOpen && (
        <div
          className="goalsCustomModalOverlay"
          onClick={() => setCustomGoalModalOpen(false)}
        >
          <div
            className="goalsCustomModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="customGoalTitle"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="goalsCustomModalHeader">
              <div>
                <div id="customGoalTitle" className="goalsCustomModalTitle">
                  Create custom goal
                </div>
                <div className="goalsCustomModalHelper">
                  Define what you want to track. Pick metrics and set targets.
                </div>
              </div>
              <button
                className="goalsCustomModalClose"
                type="button"
                onClick={() => setCustomGoalModalOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="goalsCustomModalBody">
              <div className="goalsCustomField">
                <label className="goalsCustomLabel" htmlFor="customGoalName">
                  Goal name
                </label>
                <input
                  id="customGoalName"
                  className="goalsCustomInput"
                  type="text"
                  placeholder="e.g., Grow weekend engagement"
                  value={customGoalNameDraft}
                  onChange={(e) => setCustomGoalNameDraft(e.target.value)}
                />
              </div>

              <div className="goalsCustomField">
                <label className="goalsCustomLabel" htmlFor="customGoalIntent">
                  What are you trying to achieve?
                </label>
                <textarea
                  id="customGoalIntent"
                  className="goalsCustomTextarea"
                  placeholder="e.g., Increase Instagram engagement by improving post consistency"
                  value={customGoalIntentDraft}
                  onChange={(e) => setCustomGoalIntentDraft(e.target.value)}
                />
                <span className="goalsCustomHelper">
                  Describe your objective in plain language. We’ll suggest goals and metrics.
                </span>
              </div>

              {customGoalProposal && (
                <div className="goalsCustomField">
                  <div className="goalsCustomProposalCard">
                    <div className="goalsCustomProposalTitle">AI proposal</div>
                    <div className="goalsCustomProposalRow">
                      <span className="goalsCustomProposalLabel">Goal type</span>
                      <span className="goalsCustomProposalValue">{customGoalProposal.presetName}</span>
                    </div>
                    <div className="goalsCustomProposalRow">
                      <span className="goalsCustomProposalLabel">Suggested metrics</span>
                      <div className="goalsCustomProposalMetrics">
                        {customGoalProposal.metricIds.map((metricId) => (
                          <span key={metricId} className="goalsCustomProposalPill">
                            {goalMetricLookup[metricId]?.name ?? metricId}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="goalsCustomProposalRow">
                      <span className="goalsCustomProposalLabel">Suggested period</span>
                      <span className="goalsCustomProposalValue">
                        {customGoalProposal.timeRange === 'month'
                          ? 'Monthly'
                          : customGoalProposal.timeRange === 'quarter'
                          ? 'Quarterly'
                          : 'Annual'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {customGoalManualMode && (
                <>
                  <div className="goalsCustomField">
                    <div className="goalsCustomFieldHeader">
                      <label className="goalsCustomLabel">Pick metrics</label>
                      <span className="goalsCustomCounter">
                        {customGoalMetricsDraft.size} / {maxCustomGoalMetrics} selected
                      </span>
                    </div>
                    <div className="goalsCustomMetricList">
                      {filteredGoalMetricGroups.map((group) => {
                        const selectedCount = group.metrics.filter((metric) =>
                          customGoalMetricsDraft.has(metric.id)
                        ).length
                        const isGroupChecked = selectedCount === group.metrics.length
                        const isGroupIndeterminate = selectedCount > 0 && selectedCount < group.metrics.length
                        const isGroupExpanded = customGoalExpandedGroups.has(group.id)
                        const isGroupDisabled =
                          selectedCount === 0 && customGoalMetricsDraft.size >= maxCustomGoalMetrics
                        return (
                          <div key={group.id} className="goalsMetricGroup goalsMetricGroup--compact">
                            <div className="goalsMetricGroupHeader">
                              <div className="goalsMetricGroupHeaderLeft">
                                <label className="goalsMetricGroupToggle">
                                  <IndeterminateCheckbox
                                    className="dashboardConfigureCheckbox goalsMetricCheckbox"
                                    checked={isGroupChecked}
                                    indeterminate={isGroupIndeterminate}
                                    onChange={() => toggleCustomGoalMetricGroup(group.metrics.map((metric) => metric.id))}
                                    disabled={isGroupDisabled}
                                  />
                                  <span className="goalsMetricGroupTitle">{group.label}</span>
                                </label>
                                <div className="goalsMetricGroupCount">
                                  {selectedCount} of {group.metrics.length} selected
                                </div>
                              </div>
                              <button
                                className="goalsMetricGroupToggleButton"
                                type="button"
                                aria-expanded={isGroupExpanded}
                                onClick={() => toggleCustomGoalGroupExpanded(group.id)}
                              >
                                <span className="goalsMetricGroupToggleIcon" aria-hidden="true">
                                  {isGroupExpanded ? '−' : '+'}
                                </span>
                              </button>
                            </div>
                            {isGroupExpanded && (
                              <div className="goalsCustomMetricRows">
                                {group.metrics.map((metric) => {
                                  const isSelected = customGoalMetricsDraft.has(metric.id)
                                  const isDisabled =
                                    !isSelected && customGoalMetricsDraft.size >= maxCustomGoalMetrics
                                  return (
                                    <div
                                      key={metric.id}
                                      className={`goalsCustomMetricRow ${isSelected ? 'is-selected' : ''}`}
                                    >
                                      <label className="goalsCustomMetricCheck">
                                        <input
                                          type="checkbox"
                                          className="dashboardConfigureCheckbox goalsMetricCheckbox"
                                          checked={isSelected}
                                          onChange={() => toggleCustomGoalMetric(metric.id)}
                                          disabled={isDisabled}
                                        />
                                        <span className="goalsCustomMetricName">{metric.name}</span>
                                      </label>
                                      <div className="goalsMetricMeta">
                                        <span>{metric.platform}</span>
                                        <span>{metric.source}</span>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    {customGoalMetricsDraft.size >= maxCustomGoalMetrics && (
                      <span className="goalsCustomHelper">
                        Max {maxCustomGoalMetrics} metrics selected. Deselect one to add another.
                      </span>
                    )}
                  </div>

                  <div className="goalsCustomField">
                    <label className="goalsCustomLabel">Time period</label>
                    <div className="goalsMetricPeriod goalsMetricPeriod--compact" role="group" aria-label="Time period">
                      {['month', 'quarter', 'year'].map((range) => (
                        <button
                          key={range}
                          type="button"
                          className={`goalsMetricPill ${customGoalTimeRangeDraft === range ? 'is-active' : ''}`}
                          onClick={() => setCustomGoalTimeRangeDraft(range)}
                        >
                          {range === 'month' ? 'Monthly' : range === 'quarter' ? 'Quarterly' : 'Annual'}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="goalsCustomModalActions">
              {customGoalProposal ? (
                <>
                  <button
                    className="goalsApplyButton goalsApplyButton--ghost"
                    type="button"
                    onClick={handleEditCustomGoalManual}
                  >
                    Edit manually
                  </button>
                  <button
                    className="goalsApplyButton"
                    type="button"
                    onClick={handleUseCustomGoalProposal}
                  >
                    Use this goal
                  </button>
                </>
              ) : customGoalManualMode ? (
                <>
                  <button
                    className="goalsApplyButton goalsApplyButton--tertiary"
                    type="button"
                    onClick={() => setCustomGoalModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="goalsApplyButton"
                    type="button"
                    disabled={!customGoalNameDraft.trim() || customGoalMetricsDraft.size === 0}
                    onClick={() => handleCreateCustomGoal()}
                  >
                    Create goal
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="goalsApplyButton goalsApplyButton--tertiary"
                    type="button"
                    onClick={() => setCustomGoalModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="goalsApplyButton"
                    type="button"
                    disabled={!customGoalNameDraft.trim()}
                    onClick={handleGenerateCustomGoal}
                  >
                    Generate goals
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {hoveredKpiId && (
        <KpiHoverLayer
          hoveredCard={liveNowCardsWithIcons.find(card => card.id === hoveredKpiId)}
          cardRect={hoveredKpiRect}
          renderPlatformIcons={renderPlatformIcons}
          onMouseEnter={handleOverlayEnter}
          onMouseLeave={handleOverlayLeave}
        />
      )}
      {openKpiCardId && openKpiCardState.rect && (
        <KpiHoverLayer
          id={`kpi-popover-${openKpiCardId}`}
          hoveredCard={liveNowCardsWithIcons.find(card => card.id === openKpiCardId)}
          cardRect={openKpiCardState.rect}
          overlayRef={kpiPopoverRef}
          renderPlatformIcons={renderPlatformIcons}
        />
      )}
        </div>
      {isConfigureOpen && (
        <div className="dashboardConfigureOverlay" onClick={closeConfigure}>
          <div className="dashboardConfigureDrawer" onClick={(event) => event.stopPropagation()}>
            <div className="dashboardConfigureHeader">
              <div>
                <div className="dashboardConfigureTitle">Configure Dashboard</div>
                <div className="dashboardConfigureSubtitle">Customize charts</div>
              </div>
              <button className="dashboardConfigureClose" type="button" onClick={closeConfigure} aria-label="Close">
                ×
              </button>
            </div>
            <div className="dashboardConfigureTopRow">
              <div className="dashboardConfigurePlatformSection">
                <div className="dashboardConfigureSectionLabel">Platform filter</div>
                <div ref={configurePlatformFilterRef} className="dashboardFilterSelect">
                  <button
                    className="dashboardFilterSelectButton"
                    type="button"
                    onClick={() => setConfigurePlatformFilterOpen((prev) => !prev)}
                  >
                    <span className="dashboardFilterSelectLabel">Platform</span>
                    <span className="dashboardFilterSelectValue">
                      {draftCampaignChannel === 'all'
                        ? 'All platforms'
                        : platformLabels[draftCampaignChannel] || draftCampaignChannel.charAt(0).toUpperCase() + draftCampaignChannel.slice(1)}
                    </span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  {configurePlatformFilterOpen && (
                    <div className="dashboardFilterSelectMenu" style={{ minWidth: '100%' }}>
                      <button
                        type="button"
                        className={`dashboardFilterSelectOption ${draftCampaignChannel === 'all' ? 'dashboardFilterSelectOption--active' : ''}`}
                        onClick={() => {
                          setDraftCampaignChannel('all')
                          setConfigurePlatformFilterOpen(false)
                        }}
                      >
                        All platforms
                      </button>
                      {(platforms || ['instagram', 'facebook', 'linkedin', 'twitter', 'tiktok']).map((platform) => (
                        <button
                          key={platform}
                          type="button"
                          className={`dashboardFilterSelectOption ${draftCampaignChannel === platform ? 'dashboardFilterSelectOption--active' : ''}`}
                          onClick={() => {
                            setDraftCampaignChannel(platform)
                            setConfigurePlatformFilterOpen(false)
                          }}
                        >
                          {platformLabels[platform] || platform.charAt(0).toUpperCase() + platform.slice(1)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="dashboardConfigureWidgetsSection">
              <div className="dashboardConfigureSectionLabel">Dashboard widgets</div>
              <label className="dashboardConfigureWidgetToggle">
                <input
                  type="checkbox"
                  checked={draftShowSohoPulse}
                  onChange={(e) => setDraftShowSohoPulse(e.target.checked)}
                />
                <span>Soho Pulse</span>
              </label>
            </div>
            <div className="dashboardConfigureList">
              <div className="dashboardConfigureListHeader">
                <label className="dashboardConfigureSelectAll">
                  <IndeterminateCheckbox
                    className="dashboardConfigureCheckbox"
                    checked={draftSelectedCharts.size === allChartIds.length}
                    indeterminate={draftSelectedCharts.size > 0 && draftSelectedCharts.size < allChartIds.length}
                    onChange={toggleAllCharts}
                  />
                  <span>Select All</span>
                </label>
                <span className="dashboardConfigureBadge">
                  {draftSelectedCharts.size} of {allChartIds.length} charts visible
                </span>
              </div>
              <DndContext
                sensors={configureDndSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleConfigureDragEnd}
              >
                <SortableContext
                  items={(() => {
                    const order = []
                    const seen = new Set()
                    for (const chartId of draftWidgetOrder) {
                      const chart = CHART_LOOKUP[chartId]
                      if (!chart) continue
                      if (!seen.has(chart.sectionId)) {
                        seen.add(chart.sectionId)
                        const section = chartVisibilitySections.find((s) => s.id === chart.sectionId)
                        const sectionChartIds = section?.charts.map((c) => c.id) ?? []
                        const orderedCharts = draftWidgetOrder.filter((id) => sectionChartIds.includes(id))
                        if (!configureSectionExpanded[chart.sectionId]) {
                          order.push(chart.sectionId)
                        } else {
                          order.push(...orderedCharts)
                        }
                      }
                    }
                    return order
                  })()}
                  strategy={verticalListSortingStrategy}
                >
                  {(() => {
                    const sectionOrder = []
                    const seen = new Set()
                    for (const chartId of draftWidgetOrder) {
                      const chart = CHART_LOOKUP[chartId]
                      if (!chart || seen.has(chart.sectionId)) continue
                      seen.add(chart.sectionId)
                      sectionOrder.push(chartVisibilitySections.find((s) => s.id === chart.sectionId))
                    }
                    return sectionOrder.filter(Boolean).map((section) => {
                      const isExpanded = configureSectionExpanded[section.id] !== false
                      const sectionChartIds = section.charts.map((c) => c.id)
                      const orderedCharts = draftWidgetOrder.filter((id) => sectionChartIds.includes(id))
                      const selectedCount = sectionChartIds.filter((id) => draftSelectedCharts.has(id)).length
                      return (
                        <div key={section.id} className="dashboardConfigureSection">
                          <SortableSectionHeader
                            section={section}
                            isExpanded={isExpanded}
                            onToggleExpand={toggleConfigureSectionExpanded}
                            selectedCount={selectedCount}
                            totalCount={sectionChartIds.length}
                            onToggleSection={toggleSectionSelection}
                            isDraggable={!isExpanded}
                          />
                          {isExpanded && (
                            <div className="dashboardConfigureSectionItems">
                              {orderedCharts.map((chartId) => {
                                const chart = CHART_LOOKUP[chartId]
                                if (!chart) return null
                                return (
                                  <SortableChartItem
                                    key={chartId}
                                    chart={chart}
                                    isVisible={draftSelectedCharts.has(chartId)}
                                    onToggle={toggleChartSelection}
                                  />
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })
                  })()}
                </SortableContext>
              </DndContext>
            </div>
            <div className="dashboardConfigureFooter">
              <button
                className="dashboardConfigureReset"
                type="button"
                onClick={() => setShowResetConfirm(true)}
              >
                Reset to Default
              </button>
              <button
                className="dashboardConfigureApply"
                type="button"
                onClick={applyConfigure}
                disabled={draftSelectedCharts.size === 0 || !hasDraftChanges}
              >
                Save & Apply
              </button>
            </div>
            {showResetConfirm && (
              <div className="dashboardConfigureResetOverlay" onClick={() => setShowResetConfirm(false)}>
                <div className="dashboardConfigureResetDialog" onClick={(e) => e.stopPropagation()}>
                  <p className="dashboardConfigureResetTitle">Reset layout to default?</p>
                  <p className="dashboardConfigureResetText">This will restore default widgets and order.</p>
                  <div className="dashboardConfigureResetActions">
                    <button type="button" className="dashboardConfigureResetCancel" onClick={() => setShowResetConfirm(false)}>
                      Cancel
                    </button>
                    <button type="button" className="dashboardConfigureResetConfirm" onClick={resetConfigureToDefault}>
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {exportBusy && exportProgress && (
        <DashboardExportProgressBanner progress={exportProgress} />
      )}

      {isExportOpen && (
        <div className="dashboardExportOverlay" onClick={closeExport}>
          <div className="dashboardExportDrawer" onClick={(event) => event.stopPropagation()}>
            <div className="dashboardExportHeader">
              <div className="dashboardExportHeaderMain">
                {exportCustomizeMode && (
                  <button
                    type="button"
                    className="dashboardExportHeaderBack"
                    onClick={() => setExportCustomizeMode(false)}
                    aria-label="Back to report settings"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                )}
                <div>
                  <div className="dashboardExportTitle">
                    {exportCustomizeMode ? 'Customize report' : 'Export report'}
                  </div>
                  <div className="dashboardExportSubtitle">
                    {exportCustomizeMode
                      ? 'Choose charts and data scope for your report.'
                      : 'Download a report using your current dashboard view and settings.'}
                  </div>
                </div>
              </div>
              <button className="dashboardExportClose" type="button" onClick={closeExport} aria-label="Close">
                ×
              </button>
            </div>

            {exportCustomizeMode ? (
              <>
                <div className="dashboardExportControlsSection">
                  <div
                    className={`dashboardExportCollapsible${
                      exportSectionCollapsed ? ' dashboardExportCollapsible--collapsed' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className={`dashboardExportCollapsibleHeader${
                        exportSectionCollapsed ? ' dashboardExportCollapsibleHeader--collapsed' : ''
                      }`}
                      onClick={() => setExportSectionCollapsed((collapsed) => !collapsed)}
                      aria-expanded={!exportSectionCollapsed}
                    >
                      <div className="dashboardExportCollapsibleHeaderRow">
                        <span className="dashboardExportCollapsibleTitle">Report settings</span>
                        <svg
                          className={`dashboardExportCollapsibleChevron ${
                            exportSectionCollapsed ? '' : 'dashboardExportCollapsibleChevron--open'
                          }`}
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </div>
                      {exportSectionCollapsed ? (
                        <div className="dashboardExportCollapsibleSummary">
                          <p
                            className="dashboardExportCollapsibleSummaryName"
                            title={exportSettingsSummary.reportName}
                          >
                            {exportSettingsSummary.reportName}
                          </p>
                          <p className="dashboardExportCollapsibleSummaryMeta">
                            {exportSettingsSummary.details}
                          </p>
                        </div>
                      ) : null}
                    </button>
                    <div className="dashboardExportCollapsibleBodyWrap">
                      <div className="dashboardExportCollapsibleBody">
                        <ExportReportSettingsForm {...getExportReportSettingsFormProps(true)} />
                      </div>
                    </div>
                  </div>
                  <div className="dashboardExportListHeader">
                    <label className="dashboardExportSelectAll">
                      <IndeterminateCheckbox
                        checked={draftExportCharts.size === exportableChartIds.length}
                        indeterminate={
                          draftExportCharts.size > 0 && draftExportCharts.size < exportableChartIds.length
                        }
                        className="dashboardExportCheckbox"
                        onChange={toggleExportAllCharts}
                      />
                      <span>Select All</span>
                    </label>
                    <span className="dashboardExportBadge">{draftExportCharts.size} charts selected</span>
                  </div>
                </div>
                <div className="dashboardExportList">
                  {exportChartVisibilitySections.map((section) => {
                    const sectionChartIds = section.charts.map((chart) => chart.id)
                    const selectedCount = sectionChartIds.filter((chartId) => draftExportCharts.has(chartId)).length
                    const indeterminate = selectedCount > 0 && selectedCount < sectionChartIds.length
                    const checked = selectedCount === sectionChartIds.length

                    return (
                      <div key={section.id} className="dashboardExportSection">
                        <label className="dashboardExportSectionHeader">
                          <IndeterminateCheckbox
                            checked={checked}
                            indeterminate={indeterminate}
                            className="dashboardExportCheckbox"
                            onChange={() => toggleExportSectionSelection(section)}
                          />
                          <span>{section.label}</span>
                        </label>
                        <div className="dashboardExportItems">
                          {section.charts.map((chart) => {
                            const chartScope = widgetDataScopes[chart.id] || reportDataScope
                            const isIncluded = draftExportCharts.has(chart.id)

                            return (
                              <div
                                key={chart.id}
                                className={`dashboardExportItem${isIncluded ? ' dashboardExportItem--withScope' : ''}`}
                              >
                                <label className="dashboardExportItemPrimary">
                                  <input
                                    type="checkbox"
                                    className="dashboardExportCheckbox"
                                    checked={isIncluded}
                                    onChange={() => toggleExportChartSelection(chart.id)}
                                  />
                                  <span className="dashboardExportItemLabel">{chart.label}</span>
                                </label>
                                {isIncluded && (
                                  <ExportWidgetDataScopeSwitch
                                    chartLabel={chart.label}
                                    value={chartScope}
                                    reportDataScope={reportDataScope}
                                    onChange={(scope) => setWidgetDataScope(chart.id, scope)}
                                  />
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                  <div className="dashboardExportSection">
                    <div className="dashboardExportSectionHeader dashboardExportSectionHeader--static">
                      <span>Insights & Recommendations</span>
                    </div>
                    <div className="dashboardExportItems">
                      {insightsSections.map((section) => (
                        <label key={section.id} className="dashboardExportItem">
                          <input
                            type="checkbox"
                            className="dashboardExportCheckbox"
                            checked={draftExportInsights.has(section.id)}
                            onChange={() => toggleExportInsightSelection(section.id)}
                          />
                          {section.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="dashboardExportFooter dashboardExportFooter--single">
                  <button
                    className="dashboardExportApply"
                    type="button"
                    onClick={handleExport}
                    disabled={draftExportCharts.size === 0 || exportBusy}
                  >
                    {exportBusy ? 'Generating report…' : 'Download report'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="dashboardExportDefaultBody">
                  <div className="dashboardExportDefaultCard">
                    <ExportReportSettingsForm {...getExportReportSettingsFormProps(false)} />
                  </div>
                  <div className="dashboardExportDefaultMeta">
                    <p className="dashboardExportDefaultMetaText">
                      {draftExportCharts.size} chart{draftExportCharts.size !== 1 ? 's' : ''} from your current dashboard view
                      {' · '}
                      {formatDataScopeLabel(reportDataScope)}
                    </p>
                  </div>
                </div>
                <div className="dashboardExportFooter dashboardExportFooter--default">
                  <ExportHoverTooltip
                    as="button"
                    type="button"
                    className="dashboardExportCustomizeButton"
                    content={EXPORT_REPORT_SETTINGS_TOOLTIPS.customize}
                    onClick={openCustomizeExport}
                    aria-label="Customize report"
                  >
                    Customize
                  </ExportHoverTooltip>
                  <button
                    className="dashboardExportApply"
                    type="button"
                    onClick={handleExport}
                    disabled={draftExportCharts.size === 0 || exportBusy}
                  >
                    {exportBusy ? 'Generating report…' : 'Download report'}
                  </button>
                </div>
              </>
            )}
            {isScheduleOpen && (
              <div className="dashboardExportScheduleOverlay" onClick={() => setIsScheduleOpen(false)}>
                <div className="dashboardExportScheduleModal" onClick={(e) => e.stopPropagation()}>
                  <div className="dashboardExportScheduleHeader">
                    <h3 className="dashboardExportScheduleTitle">Schedule Report Delivery</h3>
                    <button type="button" className="dashboardExportScheduleClose" onClick={() => setIsScheduleOpen(false)} aria-label="Close">
                      ×
                    </button>
                  </div>
                  <div className="dashboardExportScheduleBody">
                    <label className="dashboardExportScheduleRow dashboardExportScheduleRow--checkbox">
                      <input
                        type="checkbox"
                        checked={scheduleDraft.enabled}
                        onChange={(e) => setScheduleDraft((p) => ({ ...p, enabled: e.target.checked }))}
                      />
                      <span>Enable scheduled delivery</span>
                    </label>
                    {scheduleDraft.enabled && (
                      <>
                        <div className="dashboardExportScheduleRow">
                          <label className="dashboardExportScheduleLabel">Frequency</label>
                          <select
                            value={scheduleDraft.frequency}
                            onChange={(e) => setScheduleDraft((p) => ({ ...p, frequency: e.target.value }))}
                          >
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                        {scheduleDraft.frequency === 'weekly' && (
                          <div className="dashboardExportScheduleRow">
                            <label className="dashboardExportScheduleLabel">Day</label>
                            <select
                              value={scheduleDraft.dayOfWeek}
                              onChange={(e) => setScheduleDraft((p) => ({ ...p, dayOfWeek: Number(e.target.value) }))}
                            >
                              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, i) => (
                                <option key={d} value={i}>{d}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {scheduleDraft.frequency === 'monthly' && (
                          <div className="dashboardExportScheduleRow">
                            <label className="dashboardExportScheduleLabel">Day of month</label>
                            <select
                              value={scheduleDraft.dayOfMonth}
                              onChange={(e) => setScheduleDraft((p) => ({ ...p, dayOfMonth: Number(e.target.value) }))}
                            >
                              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                                <option key={d} value={d}>{d}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="dashboardExportScheduleRow">
                          <label className="dashboardExportScheduleLabel">Time</label>
                          <input
                            type="time"
                            value={scheduleDraft.time}
                            onChange={(e) => setScheduleDraft((p) => ({ ...p, time: e.target.value }))}
                          />
                        </div>
                        <div className="dashboardExportScheduleRow">
                          <label className="dashboardExportScheduleLabel">Recipients</label>
                          <div className="dashboardExportScheduleRecipients">
                            <input
                              type="email"
                              placeholder="email@example.com"
                              value={scheduleDraft.recipientInput || ''}
                              onChange={(e) => setScheduleDraft((p) => ({ ...p, recipientInput: e.target.value }))}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addScheduleRecipient())}
                            />
                            <button type="button" onClick={addScheduleRecipient}>Add</button>
                            {(scheduleDraft.recipients || []).map((email) => (
                              <span key={email} className="dashboardExportScheduleTag">
                                {email}
                                <button type="button" onClick={() => removeScheduleRecipient(email)} aria-label="Remove">×</button>
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="dashboardExportScheduleRow">
                          <label className="dashboardExportScheduleLabel">Format</label>
                          <select
                            value={scheduleDraft.format}
                            onChange={(e) => setScheduleDraft((p) => ({ ...p, format: e.target.value }))}
                          >
                            <option value="pdf">PDF</option>
                            <option value="csv">CSV</option>
                          </select>
                        </div>
                        {loadReportSchedule() && (
                          <div className="dashboardExportScheduleBadge">Saved locally</div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="dashboardExportScheduleFooter">
                    <button type="button" onClick={() => setIsScheduleOpen(false)}>Cancel</button>
                    <button
                      type="button"
                      onClick={saveSchedule}
                      disabled={scheduleDraft.enabled && (!scheduleDraft.recipients || scheduleDraft.recipients.length === 0)}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isInsightsOpen && (
        <div className="dashboardInsightsOverlay" onClick={closeInsights}>
          <div className="dashboardInsightsDrawer" onClick={(event) => event.stopPropagation()}>
            <div className="dashboardInsightsHeader">
              <div>
                <div className="dashboardInsightsTitle">Insights & Recommendations</div>
                <div className="dashboardInsightsSubtitle">Last 30 days performance summary</div>
              </div>
              <div className="dashboardInsightsHeaderActions">
                <button className="dashboardInsightsClose" type="button" onClick={closeInsights} aria-label="Close">
                  ×
                </button>
              </div>
            </div>
            <div className="dashboardInsightsBody">
              {insightsTab === 'insights' && (
                <div className="dashboardInsightsTabContent dashboardInsightsTabContent--insights">
                  <div className="dashboardInsightsHeaderRow">
                    <div className="dashboardInsightsSeverityChips">
                      {['all', 'high', 'medium', 'low'].map((sev) => (
                        <button
                          key={sev}
                          type="button"
                          className={`dashboardInsightsSeverityChip ${insightsSeverityFilter === sev ? `dashboardInsightsSeverityChip--active dashboardInsightsSeverityChip--active-${sev}` : ''}`}
                          onClick={() => setInsightsSeverityFilter(sev)}
                        >
                          {sev === 'all' ? 'All' : sev.charAt(0).toUpperCase() + sev.slice(1)}
                        </button>
                      ))}
                    </div>
                    <div className="dashboardInsightsCount">
                      {derivedInsightCards.filter((c) => insightsSeverityFilter === 'all' || c.severity === insightsSeverityFilter).length} insights
                      {' · '}
                      {derivedInsightCards.filter((c) => (insightsSeverityFilter === 'all' || c.severity === insightsSeverityFilter) && insightReviewedIds.has(c.id)).length} reviewed
                    </div>
                  </div>
                  <div className="dashboardInsightsCardList">
                    {derivedInsightCards
                      .filter((c) => insightsSeverityFilter === 'all' || c.severity === insightsSeverityFilter)
                      .map((card) => (
                        <div
                          key={card.id}
                          className={`dashboardInsightsIntelligenceCard ${insightReviewedIds.has(card.id) ? 'dashboardInsightsIntelligenceCard--reviewed' : ''}`}
                        >
                          <div className="dashboardInsightsIntelligenceCardMeta">
                            <span className="dashboardInsightsIntelligenceCardSection">{card.section === 'audience' ? 'Audience' : card.section === 'campaign' ? 'Campaign' : card.section === 'inbox' ? 'Inbox' : card.section === 'paid' ? 'Paid' : 'Growth'}</span>
                            <span className={`dashboardInsightsSeverityBadge dashboardInsightsSeverityBadge--${card.severity}`}>
                              {card.severity.charAt(0).toUpperCase() + card.severity.slice(1)}
                            </span>
                          </div>
                          <div className="dashboardInsightsIntelligenceCardRow">
                            <span className="dashboardInsightsIntelligenceCardIcon">
                              {card.iconType === 'audience' && (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                  <circle cx="9" cy="7" r="4" />
                                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                              )}
                              {card.iconType === 'campaign' && (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M3 11h4l10-6v14l-10-6H3z" />
                                  <path d="M21 10v4" />
                                  <path d="M7 14v4" />
                                </svg>
                              )}
                              {card.iconType === 'inbox' && (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
                                  <path d="M7 12h10" />
                                  <path d="M9 8h6" />
                                </svg>
                              )}
                              {card.iconType === 'paid' && (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="9" />
                                  <path d="M12 7v10" />
                                  <path d="M9.5 9.5c0-1.38 1.34-2.5 3-2.5s3 1.12 3 2.5" />
                                </svg>
                              )}
                              {card.iconType === 'growth' && (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M3 17l6-6 4 4 7-7" />
                                  <path d="M14 8h6v6" />
                                </svg>
                              )}
                            </span>
                            <div className="dashboardInsightsIntelligenceCardContent">
                              <div className="dashboardInsightsIntelligenceCardTitle">{card.title}</div>
                              <div className="dashboardInsightsIntelligenceCardContext">{card.context}</div>
                            </div>
                          </div>
                          <div className="dashboardInsightsIntelligenceCardActions">
                            <button
                              type="button"
                              className="dashboardInsightsIntelligenceCardLink"
                              onClick={() => handleInsightCta(card.actionCta, card.id)}
                            >
                              {card.actionCta}
                            </button>
                            <button
                              type="button"
                              className="dashboardInsightsIntelligenceCardLink"
                              onClick={() => handleInsightCta(card.viewCta, card.id)}
                            >
                              {card.viewCta}
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
      {toastMessage && <div className="dashboardToast">{toastMessage}</div>}
      {activeTab === 'goals' && showGoalsSection && hasActiveGoals && selectedGoalMetrics.size > 0 && (
        <Tooltip
          id="goal-card-tooltip"
          className="goalCardTooltipContent"
          place="bottom"
          positionStrategy="fixed"
          variant="light"
          noArrow
          offset={8}
          style={{ zIndex: 10000 }}
          render={({ activeAnchor }) => {
            if (!activeAnchor) return null
            const name = activeAnchor.getAttribute('data-goal-name') ?? 'Goal'
            const current = activeAnchor.getAttribute('data-goal-current') ?? '—'
            const target = activeAnchor.getAttribute('data-goal-target') ?? '—'
            const progress = activeAnchor.getAttribute('data-goal-progress') ?? '—'
            const status = activeAnchor.getAttribute('data-goal-status') ?? '—'
            const statusType = activeAnchor.getAttribute('data-goal-status-type') ?? ''
            const statusClass = statusType ? `goals-active-badge goals-active-badge--${statusType}` : ''
            return (
              <div className="rg-chart__tooltip rg-chart__tooltip--campaign goalCardTooltip">
                <div className="rg-chart__tooltip-title">{name}</div>
                <div className="rg-chart__tooltip-row">
                  <span>Current</span>
                  <strong>{current}</strong>
                </div>
                <div className="rg-chart__tooltip-row">
                  <span>Target</span>
                  <strong>{target}</strong>
                </div>
                <div className="rg-chart__tooltip-row">
                  <span>Progress</span>
                  <strong>{progress}</strong>
                </div>
                <div className="rg-chart__tooltip-row goalCardTooltip-statusRow">
                  <span>Status</span>
                  <span className={statusClass || undefined}>{status}</span>
                </div>
              </div>
            )
          }}
        />
      )}
    </div>
  )
})

export default Dashboard
